/**
 * Booking detail — "the bar for every other screen" (R15). The full record:
 * journey, requirement chips (only the SET ones — R17), quote + note,
 * payment state with a manual refresh, the latest send, the customer-send
 * affordance (same SEC-6 component as Done), deposit link, and the
 * net-locked 'Re-open in calculator' (board 2814 M6, DD-8: only where the server
 * says `calculatorAllowed`; an account run gets "Change the price" instead).
 */
import { useEffect, useRef, useState } from 'preact/hooks';
import {
  cancelBookingRun,
  // ⭐ board 2814 M4 (DD-5, D7): "Change who pays" on a cash run, through 2764's route.
  setBookingPayerKind,
  // ⭐ board 2814 M6 (DD-8): "Change the price" on an account run.
  changeBookingPriceRequest,
  copyPaymentLink,
  copyQuoteMessage,
  correctCustomerEmail,
  getBookingDetailFull,
  getBookingTimeline,
  markRunTravelled,
  // ⭐ board 2814 M7 (DD-7): "Pass to a taxi" and its undo.
  passToTaxiRequest,
  undoTaxiPassRequest,
  TAXI_NOTE_MAX_CHARS,
  TAXI_PASS_REFUSAL_COPY,
  sendQuoteRequest,
  // board 2106 M5 — the balance ask's one network call and its outcome type. The
  // COPY it may return lives entirely in actions.ts (SEC-5), so this screen
  // renders `outcome.message` and never composes a refusal sentence of its own.
  askForBalanceRequest,
  type BalanceAskOutcome,
  // board 2249 M5 — "Invoice this job": its two calls, the body builder, the PDF
  // path, and the outcome types. Every sentence a refusal can produce is composed
  // in actions.ts (`JOB_INVOICE_COPY`), never on this screen.
  issueJobInvoiceRequest,
  jobInvoiceBody,
  jobInvoiceHistoryWords,
  balanceAskHistoryWords,
  jobInvoicePdfHref,
  jobInvoiceStandingCopy,
  previewJobInvoiceRequest,
  type ApiIssuedJobInvoice,
  type ApiJobInvoicePreview,
  type JobInvoiceBody,
  type JobInvoiceFormField,
  type JobInvoiceRefusal,
  CANCEL_ALREADY_COPY,
  CANCEL_PORTAL_NOT_TOLD_COPY,
  CANCEL_PORTAL_RETRY_COPY,
  CANCEL_FAILED_COPY,
  NOT_GOING_AHEAD_REFUSED_COPY,
  NOT_GOING_AHEAD_DONE_COPY,
  CANCEL_REFUND_REFUSED_COPY,
  CANCEL_REFUSED_COPY,
  CANCEL_RETAINED_COPY,
  MARK_TRAVELLED_NOT_CONFIRMED_COPY,
  cancelInvoicedCopy,
  // ⛔ RE-EXPORTED FROM shared/send-schema.ts through the actions module, never
  // restated here. One sentence, one home - see its docblock.
  REFUND_OUTCOME_UNKNOWN_COPY,
  type ApiBookingDetail,
  type ApiBookingTimelineEntry,
  type ApiLifecycleStage,
  type CancelBookingOutcome,
  type CorrectEmailOutcome,
  type MarkTravelledOutcome,
  // board 2788 M1 - "Change date and time". Its copy is inline on this screen.
  rescheduleBooking,
  type RescheduleOutcome,
  // ⭐ board 2788 M6 (step 3) - the linked variant reads the home's request itself,
  // in its own effect (never `load()`), and words DD-17 with the Requests row's rule.
  listPortalRequests,
  bookingRequestMismatch,
  mismatchSteer,
  dublinWallClockOf,
  type PortalCrossing,
  type PortalRequest,
  // ⭐ Board 2814 M2 (DD-11 item 4) - the home's ask answered ON the booking: the
  // same routes, the same words and the same history lines as the Requests row.
  acceptPortalAsk,
  declinePortalAsk,
  askDeclineQuestion,
  askLineOf,
  askNotice,
  runTravelledText,
  TRAVELLED_REQUEST_COPY,
  historyLinesOf,
  DECLINE_REASON_WORDS,
} from '../lib/actions.js';
import {
  KIOSK_RESCHEDULE_REASONS,
  RESCHEDULE_REASON_LABELS,
  RESCHEDULE_TIME_RE,
  isRescheduleReason,
  type LinkedRescheduleRefusal,
  type LinkedPortalOutcome,
  type RescheduleRefusal,
} from '../../shared/reschedule-reasons.js';
// ⭐ board 2871 - the server's own return rule, run before posting for the year bound.
import { isReturnDay, returnLegRefusal } from '../../shared/return-leg.js';
// ⛔ THE SHARED COMPONENT, never a kiosk copy. `ADR4-D11`'s claim is that the
// nurse and the operator see ONE object on ONE lifecycle; two components
// rendering "the same" six stages is how that claim quietly stops being true.
import type { ComponentChildren, Ref } from 'preact';
// ⭐ board 2106 M5 — `Button` JOINS THE IMPORTS, and it is the first on this
// screen. Every other control here is a hand-written `<button class="btn …">`,
// which is `CLASS_LEVEL_DEBT`'s `btn: 5` for this screen; the library component
// carries the stamp the adoption census keys on, so the new control is ADOPTED
// and that figure does not move (measured: still 5). Converting the existing
// five is T1749's sweep, not this milestone's.
// ⛔ THE ATTRIBUTE'S NAME IS DELIBERATELY NOT SPELLED ANYWHERE IN THIS FILE.
// `adoption-census.spec.tsx`'s arm (iii) reds on the literal appearing in an app
// root AND DOES NOT STRIP COMMENTS, which is the fail-closed direction: a screen
// that mentions it could uncomment it later with nothing noticing.
import { ActionRow, Button, Chip, Detail, EmptyState, FactsTable, FormField, Lifecycle, formatWhenParts, toneFor, type Tone } from '@ecmb/ui';
import { bookingTypeLabel, bookingStatusLabel, channelLabel, isChannelConfigured, paymentStatusLabel, sendStatusLabel, addOnLabelPairs, type Registry } from '../store/registry.js';
import { civilDateLabel, dublinDayAtTime, euro, shortDateTime, whenText } from '../lib/format.js';
import { Banner } from '../components/Banner.js';
import { SendQuote } from '../components/SendQuote.js';
import { PAYER_WORDS } from '../lib/payer-words.js';
import { parseEuroToCents } from '../lib/price-actions.js';
// ⭐ board 2814 M6 fix round 1 (ux F6): the one price ceiling the server enforces.
import { MAX_PRICE_CENTS } from '../../shared/money-bounds.js';
import { getOr } from '../lib/api.js';
import { balanceTextLedgerOutcome, contactPreferenceLabel, isSmsNumberRejected, phoneRefusalCopy, resolveRecipientEmail } from '../../shared/send-schema.js';
// board 2106 M5 — TYPE ONLY. The two rail names this screen offers are the
// schema's, so a third spelling cannot reach the route from here; the values
// themselves are written once, in `BALANCE_CHANNEL_OPTIONS` below.
import type { BalanceAskChannel } from '../../shared/balance-ask-schema.js';
// Board 2089 M3 (R-7): the ONE read-back rendering of a stored E.164.
import { formatPhoneWithCountry } from '../../shared/phone.js';
import { BOOKING_STATUSES, bookingTimelineKindLabel, isBookingCancellable, timelineSendOutcomeLabel, travelledRunKeepsRequest, type BookingStatus } from '../../shared/status-schema.js';
// ⭐ T1717 M6 - the ONE Dublin instant rendering, shared with PortalRequests,
// CustomerInfoBlock and the driver/team message.
import { formatBackWhen, formatDublinInstant, RETURN_TIME_NOT_SET } from '../../shared/dublin-date.js';
import { terminalDesignation } from '../../shared/service-schema.js';
// ⛔ THE ONE RENDER OF A CAPTURED HEADCOUNT (T1871 M6). Never a local
// truthiness test: `Number(null) === 0`, so "not captured" would print as a
// party of nobody. This function is where that rule lives.
import { formatPassengerCount } from '../../shared/passengers.js';
import { DISPLAY_SEPARATOR, displayRouteLabel, formatRouteLabel, splitRouteLabel } from '../../shared/route-label.js';
// The key PREDICATE only (never a key writer) - see the per-stop gate below.
import { slugify } from '../../shared/pricing-flatten.js';
/* ⛔ NO `fetchBookingSeries` HERE ANY MORE (board 2764 M6, R-12). This screen read
   the series only to gate "Generate invoice", which opened the kiosk's own
   statement of runs; both are gone, so the detail asks for no nursing schedule at
   all. The client read itself went at board 2814 M6 (DD-8). */

/**
 * ⭐ T1723 M4 - THE STATUS CHIP'S TONE COMES FROM `toneFor`, NOT FROM THIS FILE
 * (DESIGN.md §7.4, `packages/ui/src/status-presentation.ts`).
 *
 * ⛔ THE WORDS DO NOT MOVE. `bookingStatusLabel(registry, ...)` still supplies
 * the text and the chip's position is unchanged: the tone is PAINT ON TOP OF A
 * WORD a reader can read out loud (SC 1.4.1), which is why the tone rules set
 * `background` and `color` and nothing else.
 *
 * ⛔ AN OUT-OF-VOCABULARY STATUS GETS NO TONE AT ALL, and the guard is the point
 * rather than defensive habit. `b.status` is a plain `string` off the wire and
 * `bookingStatusLabel` falls back to the RAW VALUE for anything it cannot label,
 * so a legacy or future status would render an unregistered word. Toning it
 * would be worse than untoned twice over: `toneFor` would hand back `undefined`
 * and paint `tone--undefined` (a class the sheet does not define, so the chip
 * silently keeps the neutral look while claiming a tone), and
 * `conformance.spec.tsx`'s arm (iii) grades every toned element's text against
 * the label registry. Untoned is the honest rendering of "we do not know what
 * this word means".
 *
 * ⚠️ `Bookings.tsx` holds the same five lines for the same chip on the list row.
 * Duplicated for one milestone: the shared home is `client/store/registry.ts`,
 * outside this pass's file set, and T1723 M6 folds both onto one shared card.
 * ⛔ NOT REUSED FOR THE PAYMENT CHIP below. Payment is a fourth vocabulary that
 * `status-presentation.ts` deliberately refuses to carry (its header states why:
 * the module is bundled into the nurse's browser too).
 */
const isBookingStatus = (v: string): v is BookingStatus =>
  (BOOKING_STATUSES as readonly string[]).includes(v);

/**
 * ⭐ T1749 M10 (Step 2c) — THE CHIP IS THE LIBRARY'S, AND THE CLASS STRING DID
 * NOT MOVE. This was `statusChipClass()`, composing `chip chip--status` (plus the
 * tone) by hand; `Chip.tsx:118` pushes `chip--status` for `neutral`, so
 * `<Chip neutral tone={statusTone(...)}>` emits the byte-identical string and the
 * conversion is zero-pixel (`components-chip.spec.tsx` pins the two equal). What
 * is left here is the one decision this screen owns: an out-of-vocabulary status
 * gets NO tone, for the reason the header gives at length.
 */
function statusTone(status: string): Tone | undefined {
  return isBookingStatus(status) ? toneFor('booking', status) : undefined;
}

export type BookingDetailProps = {
  /* ⛔ NO `onGenerateInvoice` (board 2764 M6, R-12): see the import note above. */
  registry: Registry;
  bookingId: number;
  onReopen: (detail: ApiBookingDetail) => void;
  /**
   * ⭐ Board 2814 M2 fix round (ux) - THE RAIL'S NURSING-HOME COUNT, re-read after
   * Helen answers the home's ask here, exactly as the Requests screen's `onChanged`
   * does: answering an ask takes the request out of Needs you now, and a badge that
   * waited for the 30s poll would contradict the screen she just acted on. App
   * passes the same `refreshPortalCount` path. Optional, and a no-op when absent,
   * so every fixed-prop render keeps today's screen.
   */
  onRequestsChanged?: () => void;
  /** ⭐ board 2812 M3 (§7.5, Q246): the Web badge, re-read after a send, a copy of the
   *  quote message, a cancel or a not-going-ahead here, since each moves the Unsent
   *  pile it counts. Optional, a no-op when absent (every fixed-prop render). */
  onInboxChanged?: () => void;
};

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready'; detail: ApiBookingDetail };

/**
 * The History panel's OWN three states, shaped like the sibling AuditHistory's
 * LoadState (same panel, same box, same question) — loaded-with-entries,
 * loaded-but-empty and failed have to be told apart, because a panel whose whole
 * purpose is to be the record of what happened must never render a blank that
 * could mean either.
 */
type TimelineState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | {
      kind: 'ready';
      entries: ApiBookingTimelineEntry[];
      /** The server-decided spine, or null when there is none to show — which is
       *  what a CANCELLED run returns. ⛔ Never derived on the client: the whole
       *  point of `ADR4-D11` is that the server decides which stages, in which
       *  states, per role. */
      lifecycle: ApiLifecycleStage[] | null;
    };

/**
 * The outcome chip tone for a timeline send row. Reads the OUTCOME the server
 * already computed with the shared classifier (shared/send-schema.ts
 * sendOutcome) — never (status, channel) again, which is the whole point of
 * lifting that rule out of SendQuote.tsx: this panel and the send surface sit on
 * the same screen, reading the same ledger rows, and must not describe them
 * differently. Same three app chip variants SendQuote uses, so an in-flight or
 * unclassifiable send is never coloured as good news.
 */
function outcomeChipClass(outcome: string | null): string {
  if (outcome === 'failed') return 'chip chip--edited';
  if (outcome === 'delivered') return 'chip chip--paid';
  // 'sent' -> the neutral tone, same as SendQuote's mapper. Spelled out for the
  // same reason it is there: neither mapper is exhaustiveness-checked, so the
  // two drift silently, and the docblock above forbids exactly that drift.
  if (outcome === 'sent') return 'chip chip--estimate';
  return 'chip chip--estimate';
}

/**
 * ⭐ board 2811 rework part 2c (Captain item 15): a send row's provider code in
 * words. "Not delivered · 51" told Helen nothing.
 * ⭐ board 2811 R1 review (ux-review): any other numeric code is WORDS too, never
 * the code ("provider code 12" told her no more); the code stays in the ledger.
 */
function sendErrorWords(channel: string, code: string): string {
  if (isSmsNumberRejected(channel, code)) return 'the number may be wrong';
  return /^\d+$/.test(code.trim()) ? 'the provider refused it' : code;
}

/**
 * ⭐ board 2811 R1 review (ux-review): a balance text the provider refused or
 * failed reads "Balance text failed", never "Balance text sent · Not delivered",
 * which contradicts itself. The word is the list chip's (Bookings.tsx).
 * ⭐ board 2811 R3 (item 2): and a row the provider has NOT accepted (composed,
 * handed to nobody, or a status this build does not know) is never "sent": it
 * reads "Balance text: not known if it went". The three words come from the ask
 * ladder's own map (`balanceTextLedgerOutcome`, shared/send-schema.ts), so
 * History and the ladder cannot disagree about one ledger row. Every other send
 * keeps its kind's label.
 */
function timelineKindWords(entry: ApiBookingTimelineEntry): string | null {
  if (entry.kind === 'balance_text_sent') {
    const ledger = balanceTextLedgerOutcome(entry.delivery_status ?? '');
    if (ledger === 'failed') return 'Balance text failed';
    if (ledger === 'pending') return 'Balance text: not known if it went';
  }
  return bookingTimelineKindLabel(entry.kind);
}

/**
 * One history line. The kind's operator wording comes from the shared registry
 * map, EXCEPT for a payments row: that fact is a payment status, so it reads
 * through the same registry label as the payment chip above it and cannot drift
 * from it.
 */
function TimelineRow({ entry, registry }: { entry: ApiBookingTimelineEntry; registry: Registry }) {
  const label = entry.source === 'payments'
    ? paymentStatusLabel(registry, entry.kind)
    : isReturnOnlyDateChange(entry.kind, entry.detail)
      ? RETURN_CHANGED_LABEL
      : timelineKindWords(entry);
  return (
    <li>
      <span class="audit-who">{label}</span>
      {`, ${shortDateTime(entry.at)}`}
      {entry.amount_cents != null && ` · ${euro(entry.amount_cents)}`}
      {entry.channel != null && ` · ${channelLabel(registry, entry.channel)}`}
      {/* The WORD comes from the timeline's own outcome map, not from
          sendStatusLabel — the plan requires `accepted` to read differently per
          channel (terminal success on email, Twilio's earliest state on SMS) and
          SEND_STATUS_LABELS is keyed on the status alone, so both rows read
          'Accepted' and differed only by chip TONE. A colour-only distinction is a
          WCAG 1.4.1 failure. Tone and word now come from the SAME served
          `outcome`, so they cannot disagree either. */}
      {entry.delivery_status != null && (
        <>
          {' · '}
          <span class={outcomeChipClass(entry.outcome)}>
            {timelineSendOutcomeLabel(entry.outcome)}
          </span>
        </>
      )}
      {/* A short provider diagnostic, bounded at the ledger sink
          (MAX_SEND_ERROR_CODE). Usually a scalar (a Twilio code, an HTTP status)
          but NOT guaranteed to be one: notify/notify.ts flattens the transport
          error to its message and deposit-paid-notify.ts ledgers that. The comment
          that used to sit here claimed "only scalars (SC-13)" and was false. */}
      {/* ⭐ board 2811 rework part 2c (Captain item 15): WORDS, not a bare code.
          A code that says the number itself was refused (`isSmsNumberRejected`)
          reads as that; any other numeric code reads "the provider refused it"
          (board 2811 R1 review, no code at all); a non-numeric diagnostic stays
          verbatim. */}
      {entry.error_code != null && ` · ${sendErrorWords(entry.channel ?? '', entry.error_code)}`}
      {/* MARKED, not plain (T608 M4 review, finding 1). `actor` USED TO BE
          SELF-ASSERTED — it came from a hand-craftable
          `qh_session=<passcode>::<name>` cookie — and this line's field separator
          is U+00B7, so an actor of "Owen · Quote sent · Delivered" rendered as
          three more plain fields and fabricated a quote-sent-and-delivered fact
          on the screen Helen decides from. T642 M4 closed the SOURCE (the cookie
          is now an opaque token and the name is read from the operators row it
          joins to), but this markup stays and is still load-bearing for two
          reasons: every row written BEFORE that cut-over is still in this
          append-only table with no erasure path, and a provisioned display_name
          is simply a narrower forgery path, not a closed one. The 'by' names the
          field lexically and .audit-who (the same class the sibling AuditHistory
          panel puts on ITS actor — no new CSS) marks exactly which bytes are the
          NAME, so a separator inside them cannot masquerade as one of the real
          fields, which are all plain.

          The straight ASCII QUOTES are the second half of that (the live
          ux-review's finding 1): .audit-who is bold, and it is also the class on
          every row's genuine kind label, so a forged actor still read as further
          bold field content and the only remaining tell was the ABSENCE of a
          colour chip — asking Helen to notice something missing. The quotes give
          the run a visible START and END, so the whole span reads as one quoted
          claim. RENDERED TEXT, not a CSS ::before/::after (some screen readers
          skip generated content), and OUTSIDE the span so the marked run stays
          exactly the stored actor's bytes. */}
      {/* ⭐ board 2811 rework part 2c (Captain item 15): a SYSTEM actor (the row's
          schema-closed `actor_kind`, set by the writer's code and never by a name)
          reads "Automatic", a fixed word from this file, never the stored id. An
          operator, even one named like a system actor, keeps the quoted run. */}
      {entry.actor != null && entry.actor_kind === 'system' && ' · Automatic'}
      {entry.actor != null && entry.actor_kind !== 'system' && (
        <>
          {' · by "'}
          <span class="audit-who">{entry.actor}</span>
          {'"'}
        </>
      )}
      {/* TEXT NODE only, like AuditHistory's note (sec #23): `detail` is
          operator-supplied at one remove, so a detail of "<img onerror=...>"
          must render literally. Rendered verbatim rather than parsed: it is
          audit provenance, and the only alternative is splitting a format three
          emitters own. simplified: no per-kind detail formatting, upgrade when
          Helen needs the status pair as registry labels rather than raw values.
          ⭐ Rework 2 (delta 8): the ONE exception, the three whole-job kinds, whose
          single emitter writes flags; `jobInvoiceHistoryWords` reads that exact
          grammar into words and anything else still renders verbatim.
          ⭐ board 2811 M7 fix pass (ux Low): the second, `balance_ask_withdrawn`,
          whose cause token reads as words (`balanceAskHistoryWords`, a closed
          table); an unknown token still renders verbatim. ⭐ board 2811 M8: the
          same helper words `balance_ask_moved`'s two date-times as `date_changed`
          does. ⭐ board 2811 rework part 2c: the wire now carries the actor KIND,
          so a system actor reads "Automatic" (above) by the kind, never by the
          name, which would reopen the forgery the markup closes. */}
      {/* ⭐ board 2814 M8R1 (R12): a detail that only repeats the row's own label
          (the taxi pass files "run passed to a taxi" under "Run passed to a taxi")
          is not said twice. */}
      {entry.detail != null && entry.detail.trim().toLowerCase() !== (label ?? '').toLowerCase()
        && ` · ${jobInvoiceHistoryWords(entry.kind, entry.detail) ?? balanceAskHistoryWords(entry.kind, entry.detail) ?? dateChangedHistoryWords(entry.kind, entry.detail) ?? priceChangedHistoryWords(entry.kind, entry.detail) ?? entry.detail}`}
    </li>
  );
}

/**
 * THE SECOND DOOR, on screen (T660 M5 / P0-11 + R7), under Alex's 2026-07-31
 * ruling. Correct the stored `customers.email` for this booking's customer.
 *
 * WHY IT LIVES HERE AND NOT ON DONE. Done is the SEND surface, and SEC-6 says no
 * editable recipient may appear on it — `send-quote-ui.spec.ts` pins
 * `getByLabel('Customer phone')` at zero there, and an editable address beside
 * the send button is the same defect in the other channel. This screen is a
 * deliberate lookup of one booking's record, which is exactly the "different
 * door" the ruling admits: nothing gets here as a side effect of quoting.
 *
 * BEHIND A DISCLOSURE, closed by default, native <details> — the same pattern
 * and the same reasoning as the capture control (no new CSS, no JS state for the
 * open/closed half, keyboard and AT behaviour from the platform), and it keeps a
 * rarely-used correction from adding a permanent field to a dense record screen.
 *
 * ⛔ EVERY OUTCOME SENTENCE HERE NAMES NO THIRD PARTY (SEC-5). The 409 case
 * especially: saying whose record already holds the address would make this box
 * an enumeration oracle for an operator session that authorises as nothing more
 * than "is an operator".
 */
function CustomerEmailCorrection({
  bookingId,
  onCorrected,
  onConflict,
  conflictPending = false,
}: {
  bookingId: number;
  /** Re-read the booking so the record on screen shows the corrected address —
   *  never patched in locally, which would claim a write the server may not have
   *  made. */
  onCorrected: () => void;
  /**
   * ⭐⭐ T1749 M8 (C15, the CLIENT half) — A 409 HAPPENED, and only the HOST can
   * tell which 409 it was.
   *
   * `correctCustomerEmail` keys 409 to `'conflict'` from the STATUS ALONE, and
   * that is deliberate: SEC-5 forbids rendering the server's prose, and the
   * server's account refusal carries no machine code, so the two 409s — "that
   * address belongs to another customer" and "this booking has no customer
   * record at all" — are indistinguishable on the wire.
   *
   * ⛔ SO THE CLIENT ASKS ITS OWN QUESTION INSTEAD OF READING THE BODY. The host
   * re-reads the booking and branches on what it observes: a booking whose
   * `customer` is now null is the account case, and it says so in the client's
   * own words. Nothing here parses a sentence, and no third party is ever named.
   */
  onConflict: () => void;
  /** T1749 M8 round 2 (security L3): the host has seen a 409 and is re-reading.
   *  Until that lands, the control cannot know WHICH 409 it was, so it states
   *  neither — see the call site. */
  conflictPending?: boolean;
}) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<CorrectEmailOutcome | null>(null);

  const save = async () => {
    if (busy || typed.trim() === '') return;
    setBusy(true);
    setOutcome(null);
    const res = await correctCustomerEmail(bookingId, typed.trim());
    setBusy(false);
    setOutcome(res);
    if (res.kind === 'corrected' || res.kind === 'unchanged') {
      setTyped('');
      onCorrected();
    }
    // T1749 M8 (C15): a 409 is the one outcome whose MEANING depends on state
    // this component cannot see. It does not clear the typed value — Helen may
    // want to correct a typo and retry — it only asks the host to look again.
    if (res.kind === 'conflict') onConflict();
  };

  return (
    <details>
      <summary class="capture-hint">Correct the email address on this customer</summary>
      {/* ⭐ T1749 M8 (the adoption criterion) — `FormField`, on the one field this
          milestone opens on this screen. The pair it replaces was this file's own:
          the same `.field-label` with the same `for`, the same `.login-input`, the
          same id — so the markup is unchanged and every pin on it still resolves.
          What the screen stops owning is the WIRING (label ↔ control ↔ hint ↔
          error), which is the half that drifts. */}
      <FormField
        id="correct-customer-email"
        label="New email address"
        type="email"
        autoComplete="off"
        value={typed}
        onInput={setTyped}
      />
      <button
        type="button"
        class="btn btn--quiet btn--inline"
        disabled={busy || typed.trim() === ''}
        onClick={() => void save()}
      >
        Save email
      </button>
      {outcome?.kind === 'corrected' && !outcome.resendPrompt && (
        <Banner kind="ok">The email address is updated. Future quotes go to the new address.</Banner>
      )}
      {/* R7 — the quote for this booking had ALREADY been emailed to the address
          just replaced, so a live payable deposit link is sitting in somebody
          else's inbox and this customer has never seen theirs. Correcting the
          record is not the whole remedy, so the prompt is part of the outcome
          rather than something Helen has to infer. It points at the send block
          already on this screen instead of adding a second send button.

          THE STAKES ARE IN THE COPY, not only in this comment (live ux-review).
          The banner used to give an instruction with no stated reason, on a
          screen an operator reads between phone calls, which is exactly the
          banner a busy operator defers. What is actually at stake is named
          plainly: the wrong inbox holds the customer's name, journey and total
          and a payment link that is live and re-payable (core/stripe.ts: links
          never expire, which is why a superseded one is switched off
          explicitly). No alarm language - the fact is alarming enough. */}
      {outcome?.kind === 'corrected' && outcome.resendPrompt && (
        <Banner kind="warn">
          The email address is updated. The quote had already been emailed to the old address, so that
          inbox still holds this customer's name, journey and total, and a payment link that is live
          and can be paid more than once. Send it again to the new one using the send section below.
        </Banner>
      )}
      {outcome?.kind === 'unchanged' && (
        <Banner kind="info">That is already the address on file. Nothing changed.</Banner>
      )}
      {outcome?.kind === 'conflict' && !conflictPending && (
        <Banner kind="error">
          That email address is already on file for a different customer, so nothing was changed.
          Check the address with the customer and try again.
        </Banner>
      )}
      {/* A PERMANENT refusal, so no "try again in a moment" (live ux-review):
          re-pressing Save on the same text 400s every time. The honest half of
          the transient copy below survives verbatim - nothing WAS changed - and
          the ask is the one that can actually succeed: change the address. */}
      {outcome?.kind === 'invalid' && (
        <Banner kind="error">
          That is not a valid email address. Nothing was changed. Check it with the customer and type
          it again.
        </Banner>
      )}
      {outcome?.kind === 'error' && (
        <Banner kind="error">
          Saving the email address did not work. Nothing was changed. Try again in a moment.
        </Banner>
      )}
    </details>
  );
}

/**
 * board 2788 M1 - a `date_changed` history row in words. Its one emitter
 * (`dateChangedDetail`, core/reschedule-booking.ts) writes
 * `<old day>[ <old time>] -> <new day> <new time> reason=<token>` on a customer
 * booking and `reason=<token>` alone on an account booking. Anything else, or a
 * token this build does not know, renders verbatim (null).
 * ⭐ board 2871 (Captain review, security Low 3): a return-only change writes
 * `leg=return reason=<token>`, which reads as the reason under the label
 * RETURN_CHANGED_LABEL rather than "Date and time changed".
 */
function dateChangedHistoryWords(kind: string, detail: string): string | null {
  if (kind !== 'date_changed') return null;
  const m = /^(?:leg=return |(\d{4}-\d{2}-\d{2})(?: (.+?))? -> (\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}) )?reason=([a-z_]+)$/.exec(detail);
  const token = m?.[5];
  if (m === null || !isRescheduleReason(token)) return null;
  const why = RESCHEDULE_REASON_LABELS[token];
  if (m[1] === undefined || m[3] === undefined) return why;
  const from = civilDateLabel(m[1], m[2] ?? null) || m[1];
  const to = civilDateLabel(m[3], m[4] ?? null) || m[3];
  return `${from} to ${to}. ${why}`;
}

/** ⭐ board 2871 (Captain review, security Low 3) - the history label of a return-only
 *  change. ⚠️ Contains no other timeline label and is contained by none ('Return
 *  answer changed by ECMB' does not contain it), the case-insensitive `getByText` rule. */
const RETURN_CHANGED_LABEL = 'Return changed';

/** A `date_changed` row whose detail carries the closed `leg=return` marker. */
function isReturnOnlyDateChange(kind: string, detail: string | null | undefined): boolean {
  return kind === 'date_changed' && typeof detail === 'string' && /^leg=return reason=[a-z_]+$/.test(detail);
}

/**
 * ⭐ board 2814 M8R1 (R5) - a `price_changed` row's detail (`from=<cents|none>
 * to=<cents>`, `core/change-price.ts`'s `priceChangedDetail`) in words: "€60.00 to
 * €75.00", or "set to €75.00" on a run that had no price. Anything else is null, and
 * the row renders the detail verbatim as every other kind does.
 */
function priceChangedHistoryWords(kind: string, detail: string): string | null {
  if (kind !== 'price_changed') return null;
  const m = /^from=(none|\d+) to=(\d+)$/.exec(detail);
  if (m === null) return null;
  const to = euro(Number(m[2]));
  return m[1] === 'none' ? `set to ${to}` : `${euro(Number(m[1]))} to ${to}`;
}

/** board 2788 M1 - the words for each refusal code (SEC-5: never the server's prose). */
const RESCHEDULE_REFUSAL_COPY: Readonly<Record<RescheduleRefusal, string>> = {
  cancelled: 'This booking is cancelled, so its date cannot be changed.',
  completed: 'This booking is completed, so its date cannot be changed.',
  series_run: 'This run is part of a repeating schedule, so it cannot be moved on its own.',
  undated: 'This booking has no date to change.',
  portal_linked: "This trip came from the home's portal, so its date cannot be changed here yet.",
  date_passed: "This trip's date has passed, so it cannot be moved.",
  travelled: 'This run is marked as travelled, so it cannot be moved.',
  job_invoice_paid: 'A paid invoice or receipt for this job shows its date, so it cannot be moved.',
  account_invoiced:
    'This run is on an account invoice that has already been issued. Withdraw that invoice on the Invoices tab first.',
  new_date_past: 'Pick a date and time later than now. Nothing was changed.',
  too_far_ahead: 'That date is more than two years away. Check the year. Nothing was changed.',
  // ⭐ board 2871: the refusal names the fields that resolve it (the dialog shows them,
  // ABOVE Save; this alert renders below it, so the copy says no direction).
  after_return: "The new time would be after this trip's return. Nothing was changed. Change the return date and time, then save again.",
  clash: 'Another run in this schedule is already at that date and time. Nothing was changed.',
};

/** The sentence for one refusal, naming the invoice or the return where there is one. */
function rescheduleRefusalWords(
  code: RescheduleRefusal,
  invoiceNumber?: string,
  returnDay?: string | null,
  returnTime?: string | null,
): string {
  if (code === 'account_invoiced' && invoiceNumber !== undefined) {
    return `This run is on invoice ${invoiceNumber}, which has already been issued. Withdraw that invoice on the Invoices tab first.`;
  }
  if (code === 'after_return' && (returnDay != null || returnTime != null)) {
    // ⭐ board 2871 (Captain re-test, item 3): the row shape, "Fri 23 Oct, 18:00".
    const when = returnDay == null ? ` at ${returnTime ?? ''}` : ` on ${whenText(returnDay, returnTime ?? null)}`;
    return `This trip's return${when} would be before the new time. Nothing was changed. Change the return date and time, then save again.`;
  }
  return RESCHEDULE_REFUSAL_COPY[code];
}

/**
 * ⭐ board 2788 M1 (R-1) - "Change date and time" on one kiosk booking.
 *
 * BEHIND A DISCLOSURE, closed by default, native <details>, on
 * `CustomerEmailCorrection`'s pattern and for its reasons (no new CSS, no JS
 * state for open/closed, keyboard and AT from the platform). Catalog controls
 * only: three `FormField`s and the screen's quiet inline button.
 *
 * ⛔ THE SERVER DECIDES. `advisory` is the detail read's answer; when it says
 * no, this shows the reason instead of the form, and the route re-checks every
 * rule under the booking lock, so a stale screen can only meet a refusal.
 *
 * ⛔ THE REASON IS A CLOSED LIST WITH NO DEFAULT: an unpicked select keeps Save
 * off, so a reason nobody chose is never recorded.
 */
/**
 * ⭐ board 2788 M6 (step 0) - a LINKED change's refusal, which came back from the
 * home's request BEFORE anything was written, in the screen's own words (SEC-5).
 * DD-12: a refusal on the request's return names the return's time.
 */
const LINKED_REFUSAL_COPY: Readonly<Record<LinkedRescheduleRefusal, string>> = {
  request_changed: 'The home changed this request since this screen was opened. Nothing was changed. Look at it again.',
  // ⭐ Board 2814 M2 (DD-11 item 4): the ask is answered on this booking now, above.
  cancel_asked: 'The home has asked to cancel this trip. Answer the home\u2019s ask above first. Nothing was changed.',
  portal_refused:
    "The home's request cannot take that date and time: it must be ahead, within 28 days, and before any return. Nothing was changed.",
  portal_missing: "That request is not on the home's portal, or the portal cannot take a change yet. Nothing was changed.",
  no_collection: "The home's portal gave no collection time for that change, so nothing was changed. Ring the home.",
};

function linkedRefusalWords(code: LinkedRescheduleRefusal, returnTime?: string | null): string {
  if (code === 'portal_refused' && returnTime != null) {
    return `This trip's return at ${returnTime} would be before the new time: ring the home. Nothing was changed.`;
  }
  return LINKED_REFUSAL_COPY[code];
}

/**
 * ⭐ board 2788 M6 (step 0, DD-18) - WHAT BECAME OF THE HOME'S REQUEST after the
 * booking side was decided. `changed` and `already` are success; every other arm
 * is an error that tells Helen to press again (pressing again is safe: the booking
 * side answers `unchanged` and only the portal write is tried again).
 */
function linkedPortalWords(portal: LinkedPortalOutcome, bookingMoved: boolean): string | null {
  const head = bookingMoved ? 'The booking is changed, but ' : '';
  switch (portal) {
    case 'changed':
    case 'already':
      return null;
    case 'not_reached':
      return `${head}the home's request was not reached, so it still shows the old time. Press Save again: the booking will not move twice.`;
    case 'unknown':
      return `${head}the home's portal did not answer, so its request may or may not show the new time. Press Save again.`;
    case 'request_changed':
      return `${head}the home changed its request in between, so the request was not updated. Look at it in Needs you now.`;
  }
}

/** What the linked variant knows about the home's request (step 3). */
type LinkedRequestState =
  | { kind: 'loading' }
  | { kind: 'failed' }
  | { kind: 'ready'; request: PortalRequest; crossing: PortalCrossing };

/**
 * ⭐⭐ board 2788 M6 (step 3) - THE HOME'S REQUEST THIS BOOKING CAME FROM, read
 * outside the screen's `load()` (the plan keeps that effect out of this change: a
 * sibling branch edits it). The request is found through the
 * crossings map the Requests list already serves (the entry whose booking is this
 * one), so no new route and no new key on the detail read. `reloadKey` re-reads it
 * after a save. ⛔ A FAILED READ IS `failed`, never "not linked": the screen then
 * offers no form, because it cannot say whether the time is an appointment or a
 * collection, and guessing would move a resident's pickup.
 * ⭐⭐ Board 2814 M2 (driver decision on DD-11 item 4) - LIFTED TO THE SCREEN, CALLED
 * ONCE PER BOOKING: the ask block and the disclosure share this one read, instead
 * of the disclosure fetching on mount. `GET /api/portal/requests` is served from
 * the same cached, kept-keys-projected rows the Requests screen reads, so no
 * server read was added for the ask (plan-review Low 8 superseded).
 * ⛔ A DIFFERENT BOOKING STARTS AT `loading`, never at the last booking's answer.
 */
function useLinkedRequest(bookingId: number, linked: boolean, reloadKey: number): LinkedRequestState | null {
  const [state, setState] = useState<LinkedRequestState | null>(linked ? { kind: 'loading' } : null);
  const readFor = useRef<number | null>(null);
  useEffect(() => {
    if (!linked) {
      readFor.current = null;
      setState(null);
      return undefined;
    }
    if (readFor.current !== bookingId) {
      readFor.current = bookingId;
      setState({ kind: 'loading' });
    }
    let live = true;
    void listPortalRequests()
      .then(({ requests, crossings }) => {
        if (!live) return;
        const entry = crossings === null
          ? undefined
          : Object.entries(crossings).find(([, c]) => c.bookingId === bookingId);
        const request = entry === undefined ? undefined : requests.find((r) => String(r.requestId) === entry[0]);
        setState(entry === undefined || request === undefined
          ? { kind: 'failed' }
          : { kind: 'ready', request, crossing: entry[1] });
      })
      .catch(() => {
        if (live) setState({ kind: 'failed' });
      });
    return () => {
      live = false;
    };
  }, [bookingId, linked, reloadKey]);
  return state;
}

/** The request's own kind of time, for the linked form's label (DD-11). */
function linkedTimeLabel(meaning: string | null | undefined): string {
  if (meaning === 'appointment') return 'Appointment time';
  if (meaning === 'collection') return 'Collection time';
  return 'New time';
}

function ChangeDateAndTime({
  bookingId,
  advisory,
  currentDay,
  currentTime,
  returnTrip,
  returnAt,
  statedReturnDate,
  appointmentAt,
  portal,
  onReloadRequest,
  onChanged,
  taxiPassed = false,
}: {
  bookingId: number;
  advisory: NonNullable<ApiBookingDetail['reschedule']>;
  /** ⭐ board 2814 M7 (DD-7): the run was passed to a taxi, so a change says to tell the firm. */
  taxiPassed?: boolean;
  currentDay: string | null;
  currentTime: string | null;
  /** ⭐ board 2871: the booking's own `return_trip`, its stored return instant, and
   *  the return day the customer typed on the site form (free text, or null). */
  returnTrip: boolean;
  returnAt: string | null;
  statedReturnDate: string | null;
  /** ⭐ board 2788 M6: the appointment this booking carries (`extra.appointmentAt`),
   *  which the linked form starts on for an appointment request. */
  appointmentAt: string | null;
  /** ⭐ Board 2814 M2: the screen's ONE read of the home's request (`useLinkedRequest`,
   *  lifted to `BookingDetail`), and the way to ask for it again. */
  portal: LinkedRequestState | null;
  onReloadRequest: () => void;
  /** Re-read the booking so the facts show the new date (never patched locally). */
  onChanged: () => void;
}) {
  const linked = advisory.linked === true;
  const ready = portal?.kind === 'ready' ? portal : null;
  const [day, setDay] = useState(currentDay ?? '');
  const [time, setTime] = useState(currentTime !== null && RESCHEDULE_TIME_RE.test(currentTime) ? currentTime : '');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<RescheduleOutcome | null>(null);
  const details = useRef<HTMLDetailsElement>(null);
  /** Set once Helen types in either field, so a late request read never overwrites her. */
  const touched = useRef(false);
  /**
   * ⭐ board 2871 - RETURN DATE AND RETURN TIME, on a kiosk-only booking with its
   * return on (a linked booking's return is the home's, DD-12). They start on the
   * stored return; else on the day the customer stated on the site form, when it
   * is a real calendar day (`isReturnDay`), or the outbound's day; the time starts
   * blank unless stored (the site's text is a default to confirm, never sent
   * untyped). While Return date is untouched it follows New date, but only when it
   * started on the outbound's day: a return on another day stays put.
   * ⛔ fix round 1 (security L1): the keys are SENT ONLY WHEN THE RETURN DIFFERS
   * from where the dialog started (typed, or moved by the follow); an unchanged
   * return sends nothing and the server keeps and checks the stored one.
   * ⛔ fix round 2 (gate BLOCK, security R2-1): THE START IS THE RETURN THE SCREEN
   * LAST LOADED, re-taken on every reload that changes it (the effect below). The
   * dialog stays mounted across `load()`, so a start frozen at mount would drop a
   * return typed back to its first value and re-post a saved one on every save.
   */
  const showReturn = !linked && returnTrip;
  const returnStartOf = () => {
    const stored = dublinWallClockOf(returnAt);
    const stated = isReturnDay(statedReturnDate) ? statedReturnDate : null;
    return { day: stored?.day ?? stated ?? currentDay ?? '', time: stored?.time ?? '' };
  };
  const [returnStart, setReturnStart] = useState(returnStartOf);
  const returnFollows = returnStart.day === currentDay;
  const [returnDay, setReturnDay] = useState(returnStart.day);
  const [returnTime, setReturnTime] = useState(returnStart.time);
  const returnDayTouched = useRef(false);
  useEffect(() => {
    const start = returnStartOf();
    setReturnStart(start);
    setReturnDay(start.day);
    setReturnTime(start.time);
    returnDayTouched.current = false;
  }, [returnAt, currentDay, statedReturnDate]);
  /** ⭐ fix round 2 (G3): the last save kept the outbound, so a `new_date_past` is the return's. */
  const [postedReturnOnly, setPostedReturnOnly] = useState(false);
  const [returnTooFar, setReturnTooFar] = useState(false);
  /** ⭐ board 2871 (Captain re-test, item 2): an edit or a reopen retires the last outcome. */
  const clearOutcome = () => {
    setOutcome(null);
    setReturnTooFar(false);
  };
  /**
   * ⭐ board 2871 (Captain's ruling, C2c) - AN EMPTY RETURN HALF IS NEVER SILENTLY
   * DROPPED. Incomplete when Helen emptied a time that was there, emptied the time
   * after typing a date, or has a time with no date. The follow rule's own date
   * move never counts (`returnDayTouched` stays false). While incomplete the empty
   * field says so and Save is off, so nothing posts and no old return is judged.
   */
  const returnIncomplete = showReturn && (
    (returnTime === '' && (returnStart.time !== '' || returnDayTouched.current))
    || (returnTime !== '' && returnDay === ''));
  // ⭐ Captain re-test (item 3): the dialog has no Return trip switch, so it names none.
  const RETURN_INCOMPLETE = 'Enter the return date and time.';
  // The return travels only with both halves and only when it moved.
  const sendReturn = showReturn && returnTime !== '' && returnDay !== ''
    && (returnDay !== returnStart.day || returnTime !== returnStart.time);
  const canSave = day !== '' && time !== '' && isRescheduleReason(reason) && !returnIncomplete;
  /** ⭐ board 2811 M8: the post-save status area (below). */
  const statusAreaRef = useRef<HTMLDivElement | null>(null);

  /**
   * ⭐ board 2811 M8 (ux Low to Medium): AFTER A SAVE, THE STATUS IS BROUGHT INTO
   * VIEW. The M8 ux drive found the notices starting near the fold, so a warn and
   * the success line fell below it. `nearest` scrolls only as far as the area
   * needs, as the job-invoice preview does in this file; FEATURE-DETECTED, because
   * jsdom has no `scrollIntoView` and the screen must not depend on a scroll.
   * Keyed on `outcome` alone, with no one-shot ref to consume, so Preact running
   * this render's pending effect at the start of the next render (the re-read the
   * save triggers) still finds the area this outcome rendered.
   */
  useEffect(() => {
    if (outcome === null) return;
    const area = statusAreaRef.current;
    if (area !== null && typeof area.scrollIntoView === 'function') area.scrollIntoView({ block: 'nearest' });
  }, [outcome]);

  /**
   * ⭐ board 2788 M6 (step 3) - THE LINKED FORM STARTS ON THE REQUEST'S OWN TIME, as
   * the booking holds it: for an appointment request that is the appointment
   * (`extra.appointmentAt`, else the request's own instant), because that is the
   * time Helen types and the portal derives the collection from it (DD-3, DD-11);
   * for a collection request it is the booking's own day and time.
   */
  useEffect(() => {
    if (ready === null || touched.current) return;
    if (ready.request.timeMeaning !== 'appointment') return;
    const wall = dublinWallClockOf(appointmentAt) ?? dublinWallClockOf(ready.request.journeyDate);
    if (wall === null) return;
    setDay(wall.day);
    setTime(wall.time);
  }, [ready?.request.requestId, ready?.request.timeMeaning, appointmentAt]);

  const save = async () => {
    if (busy || !canSave || !isRescheduleReason(reason)) return;
    // ⭐ board 2871 - the mistyped year is said here and nothing is posted. A return
    // before the trip is left to the server, whose refusal names the stored return.
    setReturnTooFar(false);
    if (sendReturn && returnLegRefusal({ day: returnDay, time: returnTime }, { day, time }) === 'too_far') {
      setOutcome(null);
      setReturnTooFar(true);
      return;
    }
    setBusy(true);
    setOutcome(null);
    setPostedReturnOnly(day === currentDay && time === (currentTime ?? '').trim());
    // ⭐ board 2788 M6 (step 0): the request revision this screen rendered, so a
    // home change made since it was read is refused before anything is written.
    const res = await rescheduleBooking(
      bookingId,
      { day, time, reason, ...(sendReturn ? { returnDay, returnTime } : {}) },
      ready?.request.revision ?? null,
    );
    setBusy(false);
    setOutcome(res);
    if (res.kind === 'changed' || res.kind === 'error') setReason('');
    if (res.kind === 'changed' || res.kind === 'unchanged' || res.kind === 'error'
      || (res.kind === 'portal_refused' && res.code === 'request_changed')) {
      onChanged();
      if (linked) onReloadRequest();
    }
  };

  const mismatch = ready === null ? null : bookingRequestMismatch(ready.request, ready.crossing);
  // ⭐⭐ board 2788 M7 (step 0, the M6 wave's security Medium): the steer follows the
  // side that moved last (`mismatchSteer`), never `homeChanged`, which Helen's DD-2
  // re-price clears.
  const steer = ready === null ? null : mismatchSteer(ready.request, ready.crossing);
  const ask = ready !== null && ready.request.askState === 'open' ? ready.request : null;
  const askWhen = ask !== null && ask.askKind === 'change' ? formatDublinInstant(ask.askJourneyDate) : null;
  const askCollection = ask !== null && ask.askKind === 'change' && ask.timeMeaning === 'appointment'
    ? formatDublinInstant(ask.askPickupAt)
    : null;
  const cancelAsked = ask !== null && ask.askKind === 'cancel';
  // The form waits for the request on a linked booking, and is never offered while
  // the home's cancel ask is open (the route refuses `cancel_asked`).
  const formOffered = advisory.allowed && (!linked || (ready !== null && !cancelAsked));

  return (
    <>
      {/* ⭐⭐ board 2788 M6 (DD-17) - THE MISMATCH MARK, at rest and outside the
          disclosure, so it cannot be missed; only under DD-17's condition. When the
          request moved last the booking is the one to move (the form below);
          otherwise the booking is right and the home's request is behind, and
          "Send to the home again" opens the form on the booking's own time, so one
          Save resends it (the booking side answers unchanged).
          ⭐ board 2788 M7 (step 0, the M6 ux review): the control sits BELOW the
          sentence and OUTSIDE the banner, whose `role="status"` region would
          otherwise announce a button as part of the message. */}
      {mismatch !== null && (
        <Banner kind="warn">
          {`The booking says ${mismatch.booking} but the home's request says ${mismatch.request}.`}
          {steer === 'change' ? ' The home changed its request: change the booking to match below.' : null}
        </Banner>
      )}
      {mismatch !== null && steer === 'send' && (
        <Button
          variant="quiet"
          size="inline"
          onClick={() => {
            if (details.current !== null) details.current.open = true;
            queueMicrotask(() => document.getElementById('reschedule-reason')?.focus());
          }}
        >
          Send to the home again
        </Button>
      )}
      {/* ⭐ board 2871 (Captain re-test, item 2): reopening the form clears the last
          outcome, so "The date and time are changed." never sits beside a fresh edit. */}
      <details
        ref={details}
        onToggle={(e) => {
          if ((e.currentTarget as HTMLDetailsElement).open) clearOutcome();
        }}
      >
        <summary class="capture-hint">Change date and time</summary>
        {/* ⭐ board 2788 M6 (step 3, DD-8) - AN OPEN ASK IS SAID FIRST, because saving
            here closes a change ask and is refused while a cancel ask is open.
            ⭐ Board 2814 M2 (DD-11 item 4): it is answered in the ask block ABOVE
            this disclosure now, so both sentences point there. */}
        {ask !== null && ask.askKind === 'change' && (
          <Banner kind="info">
            {`The home has asked to move this to ${askWhen ?? 'a new time'}${askCollection === null ? '' : ` (collection ${askCollection})`}. Answer the home's ask above first: saving a change here closes it.`}
          </Banner>
        )}
        {cancelAsked && (
          <Banner kind="info">The home has asked to cancel this trip. Answer the home&apos;s ask above first.</Banner>
        )}
        {!advisory.allowed ? (
          <Banner kind="info">
            {advisory.why === undefined
              ? 'The date and time of this booking cannot be changed.'
              : rescheduleRefusalWords(advisory.why, advisory.invoiceNumber)}
          </Banner>
        ) : linked && portal?.kind === 'loading' ? (
          <p class="field-hint" role="status">Reading the home&apos;s request…</p>
        ) : linked && portal?.kind === 'failed' ? (
          <Banner kind="error">
            The home&apos;s request could not be read, so this trip&apos;s date cannot be changed here just now.{' '}
            <Button variant="quiet" size="inline" onClick={onReloadRequest}>
              Try again
            </Button>
          </Banner>
        ) : formOffered && (
          <>
            <FormField
              id="reschedule-day"
              label="New date"
              type="date"
              value={day}
              onInput={(v: string) => {
                clearOutcome();
                touched.current = true;
                setDay(v);
                if (showReturn && returnFollows && !returnDayTouched.current) setReturnDay(v);
              }}
            />
            <FormField
              id="reschedule-time"
              label={linked ? linkedTimeLabel(ready?.request.timeMeaning) : 'New time'}
              type="time"
              value={time}
              onInput={(v: string) => {
                clearOutcome();
                touched.current = true;
                setTime(v);
              }}
            />
            {showReturn && (
              <>
                <FormField
                  id="reschedule-return-day"
                  label="Return date"
                  type="date"
                  value={returnDay}
                  {...(returnIncomplete && returnTime !== '' ? { error: RETURN_INCOMPLETE } : {})}
                  onInput={(v: string) => {
                    clearOutcome();
                    returnDayTouched.current = true;
                    setReturnDay(v);
                  }}
                />
                <FormField
                  id="reschedule-return-time"
                  label="Return time"
                  type="time"
                  value={returnTime}
                  {...(returnIncomplete && returnTime === '' ? { error: RETURN_INCOMPLETE } : {})}
                  onInput={(v: string) => {
                    clearOutcome();
                    setReturnTime(v);
                  }}
                />
              </>
            )}
            <FormField
              id="reschedule-reason"
              kind="select"
              label="Why is it changing?"
              value={reason}
              onInput={(v: string) => {
                clearOutcome();
                setReason(v);
              }}
              options={[
                { value: '', label: 'Pick a reason' },
                ...KIOSK_RESCHEDULE_REASONS.map((r) => ({ value: r, label: RESCHEDULE_REASON_LABELS[r] })),
              ]}
            />
            <button
              type="button"
              class="btn btn--quiet btn--inline"
              disabled={busy || !canSave}
              onClick={() => void save()}
            >
              Save new date and time
            </button>
          </>
        )}
        {/* ⭐ board 2811 M8 (ux Low to Medium): the post-save status, as ONE area the
            effect above brings into view after a save (the job-invoice preview's
            `previewAreaRef` shape in this file). */}
        <div ref={statusAreaRef}>
        {(outcome?.kind === 'changed' || outcome?.kind === 'unchanged') && (
          <>
            {/* ⭐ board 2811 M8 (ux Med): the balance facts come FIRST, above the
                success line, so they are in view without scrolling past the form.
                At most one is set (the route's one notice). */}
            {outcome.notices?.balanceAskMoveFailed === true && (
              <Banner kind="warn">The balance ask could not be moved to the new date. Tell Alex.</Banner>
            )}
            {outcome.notices?.balanceAskNotMoved === true && (
              <Banner kind="warn">The balance link has gone out, or goes out within the hour, so it was not moved.</Banner>
            )}
            {outcome.kind === 'changed'
              ? <Banner kind="ok">{outcome.returnOnly === true ? 'The return is changed.' : 'The date and time are changed.'}</Banner>
              : outcome.portal === undefined || outcome.portal === 'changed' || outcome.portal === 'already'
                ? (
                  <Banner kind="info">
                    {outcome.portal === undefined
                      ? "That is already this booking's date and time. Nothing changed."
                      : "The booking already had that time. The home's request now matches it."}
                  </Banner>
                )
                : null}
            {/* ⭐ board 2788 M6 (step 3) - the collection the portal derived (DD-3). */}
            {outcome.pickup !== undefined && (outcome.portal === 'changed' || outcome.portal === 'already') && (
              <Banner kind="ok">
                {`The bus collects at ${outcome.pickup.time} on ${civilDateLabel(outcome.pickup.day, null) || outcome.pickup.day}.`}
              </Banner>
            )}
            {outcome.askClosed === true && (
              <Banner kind="info">The home&apos;s ask to move this trip is closed by this change.</Banner>
            )}
            {outcome.portal !== undefined && linkedPortalWords(outcome.portal, outcome.kind === 'changed') !== null && (
              <Banner kind="error">{linkedPortalWords(outcome.portal, outcome.kind === 'changed')}</Banner>
            )}
            {outcome.kind === 'changed' && outcome.notices.tellCustomer && <Banner kind="warn">Tell the customer the new date.</Banner>}
            {outcome.kind === 'changed' && outcome.notices.replaceInvoice && (
              <Banner kind="warn">The invoice still shows the old date: replace it.</Banner>
            )}
            {outcome.kind === 'changed' && outcome.notices.tellTeam && (
              <Banner kind="warn">The team was told the old time: let them know.</Banner>
            )}
            {outcome.kind === 'changed' && taxiPassed && <Banner kind="warn">{TAXI_TELL_FIRM_COPY}</Banner>}
          </>
        )}
        {outcome?.kind === 'portal_refused' && (
          <Banner kind="error">{linkedRefusalWords(outcome.code, outcome.returnTime)}</Banner>
        )}
        {outcome?.kind === 'refused' && (
          <Banner kind="error">
            {outcome.code === 'new_date_past' && postedReturnOnly
              ? 'Pick a return later than now. Nothing was changed.'
              : rescheduleRefusalWords(outcome.code, outcome.invoiceNumber, outcome.returnDay, outcome.returnTime)}
          </Banner>
        )}
        {returnTooFar && (
          <Banner kind="error">The return is a year or more after the trip. Check the year. Nothing was changed.</Banner>
        )}
        {outcome?.kind === 'invalid' && (
          <Banner kind="error">That date, time or reason was not accepted. Nothing was changed. Check them and save again.</Banner>
        )}
        {outcome?.kind === 'error' && (
          <Banner kind="error">Changing the date and time did not work. Check the date above, then try again.</Banner>
        )}
        </div>
      </details>
    </>
  );
}

/**
 * ⭐⭐ Board 2814 M2 (plan DD-11 item 4) - THE HOME'S ASK, ANSWERED ON THE BOOKING
 * ITSELF, and the request's history beside it. Once a request is confirmed it is a
 * booking (Alex's ruling), so a home's change or cancel ask on it is answered here,
 * and is ALSO listed in Needs you now (DD-11 row 9).
 *   · the ask in words (`askLineOf`, the Requests row's own sentence);
 *   · a CHANGE ask's Accept is this screen's one gold while the ask is open;
 *   · a CANCEL ask's Accept ends the trip and this run, so it is destructive: it
 *     confirms in place and is never gold (DESIGN.md §5.1, as on the row);
 *   · Decline takes one of DD-5's reasons and stays off until one is picked.
 * Accept and Decline post 2788 M4's routes with the `askSeq` and `revision` THIS
 * SCREEN RENDERED, every outcome is said in `askNotice`'s words, and after any
 * outcome both the booking and the request are read again.
 * ⛔ A FAILED READ IS SAID, never shown as "no ask": the screen cannot tell an
 * open ask from none. While the read is in flight it says nothing at all.
 */
function LinkedAsk({
  portal,
  bookingStatus,
  travelled,
  onAnswered,
  taxiPassed = false,
}: {
  portal: LinkedRequestState | null;
  /** ⭐ board 2814 M7 (DD-7): the run was passed to a taxi, so an accept says to tell the firm. */
  taxiPassed?: boolean;
  /** The booking's own status: a cancel ask's accept ends the run while it is live. */
  bookingStatus: string;
  /** ⭐ Board 2814 M4 (ruling (c)): the run travelled, so accepting the home's cancel
   *  ask closes the request and the run stays on the month's bill. */
  travelled: boolean;
  /** Re-read the booking, the home's request and the rail's count (never patched locally). */
  onAnswered: () => void;
}) {
  const [reason, setReason] = useState('');
  const [cancelArmed, setCancelArmed] = useState(false);
  /** ⭐ board 2812 M4 (step 0 (a)): the ask's Decline confirms in place, with its question
   *  ("Decline the change? The trip stays at 08:15 ..."), like a To do card's; it holds
   *  the `askSeq` it was armed on, so a replaced ask is never declined unseen (step 0 (b)). */
  const [declineArmedSeq, setDeclineArmedSeq] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  /** ⭐ board 2814 M7: the last answer was an accept, so a passed run's notice shows. */
  const [accepted, setAccepted] = useState(false);
  // The same latch as the Requests row's: `busy` is fixed at render time, so a
  // second tap in the same task would post twice without a ref.
  const inFlight = useRef(false);
  /**
   * ⭐ Board 2814 M2 fix round (ux) - THE KEYBOARD FOLLOWS THE ANSWER. Accept and
   * Decline unmount once the ask is answered, and a removed `activeElement` falls
   * to `<body>`, so the outcome is focused instead (the Requests screen's notice
   * idiom: a `tabIndex={-1}` status). The cancel ask's confirm in place moves it
   * too: arming lands on "Keep it", the safe first control, and "Keep it" hands it
   * back to the control that armed it.
   */
  const noticeRef = useRef<HTMLDivElement>(null);
  const keepRef = useRef<HTMLButtonElement>(null);
  const armRef = useRef<HTMLButtonElement>(null);
  const wasArmed = useRef(false);
  const declineKeepRef = useRef<HTMLButtonElement>(null);
  const declineRef = useRef<HTMLButtonElement>(null);
  const wasDeclineArmed = useRef(false);
  useEffect(() => {
    if (notice !== null) noticeRef.current?.focus();
  }, [notice]);
  useEffect(() => {
    if (cancelArmed) keepRef.current?.focus();
    else if (wasArmed.current) armRef.current?.focus();
    wasArmed.current = cancelArmed;
  }, [cancelArmed]);
  // The decline's confirm moves the keyboard the same way: arming lands on "Keep it",
  // and "Keep it" hands it back to Decline.
  useEffect(() => {
    const armed = declineArmedSeq !== null;
    if (armed) declineKeepRef.current?.focus();
    else if (wasDeclineArmed.current) declineRef.current?.focus();
    wasDeclineArmed.current = armed;
  }, [declineArmedSeq]);

  if (portal === null || portal.kind === 'loading') return null;
  if (portal.kind === 'failed') {
    return <Banner kind="error">Could not read the home&apos;s request. Open Needs you now to check.</Banner>;
  }
  const r = portal.request;
  const askLine = askLineOf(r);
  const history = historyLinesOf(r);
  const picked = DECLINE_REASON_WORDS.find((d) => d.value === reason)?.value;
  // ⛔ ARMED ONLY FOR THE ASK IT WAS ARMED ON: a re-read that brings a replaced ask (a new
  // `askSeq`) shows Decline again, so the reason is never sent against an ask Helen has
  // not seen.
  const declineArmed = declineArmedSeq !== null && declineArmedSeq === r.askSeq;

  const answer = async (decision: 'accept' | 'decline'): Promise<void> => {
    if (inFlight.current) return;
    if (r.askState !== 'open' || typeof r.askSeq !== 'number' || typeof r.revision !== 'number') return;
    // ⛔ In the handler too, not only by `disabled`: a dispatched click walks past
    // a disabled control (measured in this repo).
    if (decision === 'decline' && picked === undefined) {
      setNotice({ kind: 'error', text: 'Pick why you are saying no, then press Decline again.' });
      return;
    }
    inFlight.current = true;
    setBusy(true);
    setNotice(null);
    try {
      const outcome = decision === 'accept'
        ? await acceptPortalAsk(r.requestId, { askSeq: r.askSeq, revision: r.revision })
        : await declinePortalAsk(r.requestId, { askSeq: r.askSeq, revision: r.revision, reason: picked! });
      setNotice(askNotice(r, decision, outcome, 'booking'));
      setAccepted(decision === 'accept');
      setReason('');
      onAnswered();
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const repeating = r.journeyDate === null;
  return (
    <>
      {notice !== null && (
        <Banner kind={notice.kind === 'ok' ? 'ok' : 'error'} elementRef={noticeRef} tabIndex={-1}>
          {notice.text}
        </Banner>
      )}
      {notice?.kind === 'ok' && accepted && taxiPassed && <Banner kind="warn">{TAXI_TELL_FIRM_COPY}</Banner>}
      {askLine !== null && (
        <>
          <Banner kind="warn">{askLine}</Banner>
          {declineArmed ? (
            /* ⭐ board 2812 M4 (step 0 (a)): the decline's confirm takes the controls' place,
               so no gold stays up beside a destructive question (the To do card's rule). */
            <>
              <p class="field-hint">{askDeclineQuestion(r)}</p>
              <Button
                variant="destructive-quiet"
                disabled={busy}
                onClick={() => {
                  setDeclineArmedSeq(null);
                  void answer('decline');
                }}
              >
                Yes, decline it
              </Button>
              <Button variant="secondary" elementRef={declineKeepRef} onClick={() => setDeclineArmedSeq(null)}>
                Keep it
              </Button>
            </>
          ) : r.askKind === 'change' ? (
            <Button variant="primary" size="inline" disabled={busy} onClick={() => void answer('accept')}>
              Accept the change
            </Button>
          ) : cancelArmed ? (
            /* The safe choice first, as on the Requests row: Enter keeps the trip. */
            <>
              {/* ⭐⭐ Board 2814 M8R1 (Captain's ruling on Q287): a live run that has
                  travelled keeps the home's request open, so the confirm says why and
                  offers no "Yes"; the server refuses the accept (`request_run_travelled`).
                  This screen's own cancel below is the one correction path. */}
              <p class="field-hint">
                {travelledRunKeepsRequest(bookingStatus, travelled)
                  ? TRAVELLED_REQUEST_COPY
                  : !isBookingCancellable(bookingStatus)
                    ? 'Accepting cancels the trip.'
                    : 'Accepting cancels the trip and this run, so it comes off the month’s bill.'}
              </p>
              <Button variant="secondary" elementRef={keepRef} onClick={() => setCancelArmed(false)}>
                Keep it
              </Button>
              {!travelledRunKeepsRequest(bookingStatus, travelled) && (
                <Button
                  variant="destructive-quiet"
                  disabled={busy}
                  onClick={() => {
                    setCancelArmed(false);
                    void answer('accept');
                  }}
                >
                  {repeating ? 'Yes, stop the trip' : 'Yes, cancel the trip'}
                </Button>
              )}
            </>
          ) : (
            <Button variant="destructive-quiet" elementRef={armRef} disabled={busy} onClick={() => setCancelArmed(true)}>
              {`${repeating ? 'Stop' : 'Cancel'} the trip${isBookingCancellable(bookingStatus) && !travelled ? ' and end the run' : ''}`}
            </Button>
          )}
          {!cancelArmed && !declineArmed && (
            <>
              <FormField
                id="linked-ask-reason"
                label="Why not?"
                kind="select"
                value={reason}
                options={[
                  { value: '', label: 'Pick a reason' },
                  ...DECLINE_REASON_WORDS.map((d) => ({ value: d.value, label: d.label })),
                ]}
                onInput={setReason}
              />
              <Button
                variant="quiet"
                size="inline"
                elementRef={declineRef}
                disabled={busy || picked === undefined}
                ariaDescribedBy={picked === undefined ? 'linked-ask-reason-hint' : undefined}
                onClick={() => {
                  // ⛔ In the handler too, not only by `disabled`: no reason, no question;
                  // `answer` says why. Else it ARMS (board 2812 M4, step 0 (a)): nothing
                  // is posted until "Yes, decline it".
                  if (picked === undefined || typeof r.askSeq !== 'number') { void answer('decline'); return; }
                  setDeclineArmedSeq(r.askSeq);
                }}
              >
                {/* ⭐ board 2812 fix round (Captain's item 6, the request card's words): on a
                    CHANGE ask it says what it refuses, "Decline the change", so it never
                    reads as ending the trip; a CANCEL ask (the only other kind that
                    reaches here: `askLineOf` is null for any other) keeps "Decline". */}
                {r.askKind === 'change' ? 'Decline the change' : 'Decline'}
              </Button>
              {picked === undefined && (
                <p class="field-hint" id="linked-ask-reason-hint">Pick a reason to decline</p>
              )}
            </>
          )}
        </>
      )}
      {/* ⭐ DD-11: the request's history lives on its booking now, because a
          confirmed request with a booking is no longer listed on Requests.
          ⭐ Board 2814 M2 fix round (ux Low): a caption ABOVE the list, not a
          facts-table term beside it. As the only fact in a gapless facts grid the
          term sat flush against the first bullet. Existing classes only. */}
      {history.length > 0 && (
        <>
          <p class="field-hint">Request history</p>
          <ul class="portal-request-history">
            {history.map((t, i) => <li key={i}>{t}</li>)}
          </ul>
        </>
      )}
    </>
  );
}

/**
 * ⭐⭐ T1002 M7 — "Cancel this run", with the refund that makes it possible on a
 * PAID booking.
 *
 * ⛔ TERMINAL, AND THERE IS NO UNDO HALF. `setBookingStatus`'s UPDATE ends
 * `AND status NOT IN ('cancelled','completed')`, so a cancel cannot be reversed
 * through application code, and a refund that reached the provider cannot be
 * recalled. Inbox's dismiss offers an Undo; that does NOT carry over here, and
 * offering one would be a lie about what this button does.
 *
 * BEHIND A DISCLOSURE, closed by default, native <details> — the same pattern
 * and the same reasoning as CustomerEmailCorrection directly above (no new CSS,
 * no JS state for the open/closed half, keyboard and AT behaviour from the
 * platform). A rarely-used, irreversible action must not be a permanent control
 * on a dense record screen.
 *
 * ⭐ THE CONFIRM IS THE HOUSE PATTERN (DESIGN.md §5.1, as Inbox.tsx implements
 * it): tapping the destructive control SWAPS it for a Yes/Keep pair, and the
 * screen's primary hides while the confirm is up. Deliberately NOT a modal and
 * NOT window.confirm — there are none in this codebase, and two in-source
 * rulings refuse a confirm step outright citing "too many buttons". This costs
 * ZERO steady-state controls, which is what reconciles it with those rulings:
 * the pair exists only mid-confirm, and the budget census measures the rest
 * state.
 *
 * ⛔ THE LABELS STATE THE CONSEQUENCE, not a bare verb, and the Yes label is
 * DERIVED from the rest label so the two can never drift into promising
 * different things. "Cancel and keep the full deposit" rather than "refund
 * nothing": "nothing" would be doing double duty as an amount and as a
 * negation, which reads badly aloud and is exactly the sentence an operator
 * mishears on a phone call.
 */
/** ⭐ board 2814 M4 (DD-5) - the cash fact, the same words the Requests step and
 *  the lists use. */
const CASH_TO_DRIVER_WORDS = 'Cash, paid to the driver on the day';

/** ⭐ board 2812 M3 fix round 2 (UX-4): how a home's run is billed, in the Money column
 *  (R-11: listed for Vicky). No figure: the run's price is the "Price" fact above it
 *  (board 2812 fix round, SC-7: it moved from The trip's "Quoted" row).
 *  ⛔ fix round 3 (F3-1): a cancelled run and a taxi-passed run are NOT billed (the
 *  arrears rule bills non-cancelled runs only, and `accountBillableRunSql` excludes
 *  `taxi_passed_at IS NOT NULL`), so they never read "Billed to". */
const RUN_MONEY_WORDS = {
  account: (home: string | null) =>
    `Billed to ${home ?? 'the home'} on its monthly invoice. There is no deposit.`,
  cash: 'Paid in cash to the driver on the day. The home is not billed for it.',
  taxi: 'Nothing is billed for this run while it is with the taxi.',
  cancelled: 'Cancelled. Nothing is billed for it.',
} as const;

/** ⭐ board 2812 M5 (fix round 1, U2): the closed send block on a confirmed job (R-11:
 *  listed for Vicky). */
export const SEND_AGAIN_WORDS = 'Send the quote again';

/**
 * ⭐⭐ board 2814 M4 (plan DD-5, D7) - "CHANGE WHO PAYS" ON A CASH RUN. A mis-tapped
 * cash payer would never be billed, and the account billing fragment hides the
 * run from the Invoices tab too, so this is where it is put right: 2764's existing
 * route (`POST /api/bookings/:id/payer-kind`) with `hse` or `house`. The route
 * refuses an invoiced run and never writes `self`. A quiet disclosure: one press
 * shows the two answers and "Keep it as cash"; nothing is written until an answer.
 */
function ChangeWhoPays({ bookingId, onChanged }: { bookingId: number; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);
  const pick = async (payerKind: 'hse' | 'house'): Promise<void> => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      const res = await setBookingPayerKind(bookingId, payerKind);
      if (!res.ok) {
        setError(res.message);
        return;
      }
      setOpen(false);
      onChanged();
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  return (
    <>
      {error !== null && <Banner kind="error">{error}</Banner>}
      {open ? (
        <div class="action-row">
          <p class="field-hint">Who pays for this run? The home is billed for it at the end of the month.</p>
          <Button variant="secondary" size="inline" disabled={busy} onClick={() => void pick('hse')}>
            {PAYER_WORDS['hse']}
          </Button>
          <Button variant="secondary" size="inline" disabled={busy} onClick={() => void pick('house')}>
            {PAYER_WORDS['house']}
          </Button>
          <Button variant="quiet" size="inline" disabled={busy} onClick={() => setOpen(false)}>
            Keep it as cash
          </Button>
        </div>
      ) : (
        <Button variant="quiet" size="inline" onClick={() => setOpen(true)}>
          Change who pays
        </Button>
      )}
    </>
  );
}

/**
 * ⭐ board 2814 M6 fix round 1 (ux F6) - WHAT IS WRONG WITH A TYPED PRICE, in Helen's
 * words, BEFORE the confirm step: the server's 400 stays the control. Three causes,
 * three sentences: not a number, not above zero, past the one ceiling the server
 * enforces (`MAX_PRICE_CENTS`, shared with `parseMoneyCents`'s bound; never a
 * second hand-typed figure here).
 */
function priceEntryProblem(raw: string): { cents: number } | { problem: string } {
  const text = raw.trim().replace(/^\+/, '');
  const asNumber = text === '' ? Number.NaN : Number(text);
  if (Number.isFinite(asNumber) && asNumber <= 0) return { problem: 'The price must be more than 0.' };
  const cents = parseEuroToCents(raw);
  if (cents === null) return { problem: 'Type the price as a number, like 120 or 120.50.' };
  if (cents > MAX_PRICE_CENTS) {
    return { problem: 'That figure is too big to be the price of a run. Check it and type it again.' };
  }
  return { cents };
}

/**
 * ⭐⭐ board 2814 M6 (DD-8) - "CHANGE THE PRICE" ON AN ACCOUNT RUN, the one thing
 * "Re-open in calculator" still did for one (no nursing booking opens the
 * calculator now). `POST /api/bookings/:id/price` writes a new quote version with
 * Helen as its author; it refuses a cancelled run, a run passed to a taxi and a run
 * on a live account invoice, in words. It also SETS a first price on a run nobody
 * has priced (the generator writes none when the price book has no figure).
 * ⛔ CONFIRM IN PLACE (DESIGN.md §5.1): a quiet press opens the amount field;
 * "Continue" swaps it for one step that states the new price; "Yes, change the
 * price" writes it; the outcome is said in words and the screen re-reads
 * (`onChanged`, never patched in locally).
 * ⛔ NEVER GOLD (DESIGN.md §5.2): every control here is secondary or quiet.
 * The caret follows the step: the field when it opens or is refused (marked and
 * described), the step on Continue, the outcome when it lands.
 */
function ChangeThePrice({
  bookingId,
  currentCents,
  onChanged,
}: {
  bookingId: number;
  /** ⭐ board 2814 M8R1 (R5): the run's price now (`latest_quote_cents`), so the
   *  confirm says the old figure beside the new one; null on a run with no price. */
  currentCents: number | null;
  onChanged: () => void;
}) {
  const [stage, setStage] = useState<'closed' | 'field' | 'confirm'>('closed');
  const [amount, setAmount] = useState('');
  /** The field's message; `seq` re-takes the caret on a second refusal with the same words. */
  const [fieldError, setFieldError] = useState<{ text: string; seq: number } | null>(null);
  const [outcome, setOutcome] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  /** A REF, for `MarkTravelled`'s reason: a second tap in the same task reads it. */
  const inFlight = useRef(false);
  const fieldRef = useRef<HTMLInputElement | null>(null);
  const stepRef = useRef<HTMLDivElement | null>(null);
  const outcomeRef = useRef<HTMLDivElement | null>(null);
  const changeRef = useRef<HTMLButtonElement | null>(null);
  /** ⭐ fix round 1 (ux F4): "Keep the price" unmounts the button that had the caret,
   *  so the caret goes back to "Change the price" rather than to the page. */
  const backToChange = useRef(false);
  const entry = amount.trim() === '' ? null : priceEntryProblem(amount);
  const cents = entry !== null && 'cents' in entry ? entry.cents : null;

  useEffect(() => {
    // "Not now" returns here too: the field it lands on takes the caret.
    if (stage === 'field') fieldRef.current?.focus();
    if (stage === 'confirm') stepRef.current?.focus();
    if (stage === 'closed' && backToChange.current) {
      backToChange.current = false;
      changeRef.current?.focus();
    }
  }, [stage, fieldError?.seq]);
  useEffect(() => {
    if (outcome !== null) outcomeRef.current?.focus();
  }, [outcome]);

  const refuse = (text: string): void => {
    setStage('field');
    setFieldError((prev) => ({ text, seq: (prev?.seq ?? 0) + 1 }));
  };
  const open = (): void => {
    setOutcome(null);
    setFieldError(null);
    setAmount('');
    setStage('field');
  };
  const keep = (): void => {
    backToChange.current = true;
    setStage('closed');
  };
  const proceed = (): void => {
    if (entry === null) {
      refuse('Type the price as a number, like 120 or 120.50.');
      return;
    }
    if ('problem' in entry) {
      refuse(entry.problem);
      return;
    }
    setFieldError(null);
    setStage('confirm');
  };
  const save = async (): Promise<void> => {
    if (inFlight.current || cents === null) return;
    inFlight.current = true;
    setBusy(true);
    try {
      const res = await changeBookingPriceRequest(bookingId, cents);
      if (res.ok) {
        setStage('closed');
        setOutcome({
          kind: 'ok',
          text: res.value.changed
            ? `The price of this run is now ${euro(cents)}.`
            : `The price of this run was already ${euro(cents)}. Nothing was changed.`,
        });
        onChanged();
        return;
      }
      if (res.code === 'price_change_amount_invalid') {
        refuse(res.message);
        return;
      }
      // A rule refusal (cancelled, passed to a taxi, invoiced) is about the run, not
      // the figure, so it is said once, focused, and the field closes.
      setStage('closed');
      setOutcome({ kind: 'error', text: res.message });
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  // ⭐ fix round 1 (ux F7): the control sits in an action row (the house spacing off
  // the facts table above), and the outcome renders straight under it, where the
  // press was, not further down the screen.
  return (
    <>
      {stage === 'closed' && (
        <div class="action-row">
          <Button variant="quiet" size="inline" elementRef={changeRef} onClick={open}>Change the price</Button>
        </div>
      )}
      {outcome !== null && (
        <Banner kind={outcome.kind} elementRef={outcomeRef} tabIndex={-1}>{outcome.text}</Banner>
      )}
      {stage === 'field' && (
        <>
          <FormField
            id="change-price-amount"
            label="New price in euro"
            inputMode="decimal"
            autoComplete="off"
            value={amount}
            elementRef={fieldRef}
            onInput={(v: string) => {
              setAmount(v);
              setFieldError(null);
            }}
            error={fieldError?.text}
          />
          <div class="action-row">
            <Button variant="secondary" size="inline" onClick={proceed}>Continue</Button>
            <Button variant="quiet" size="inline" onClick={keep}>Keep the price</Button>
          </div>
        </>
      )}
      {stage === 'confirm' && cents !== null && (
        <div role="group" aria-label="Check the new price" tabIndex={-1} ref={stepRef}>
          {/* ⭐ board 2814 M8R1 (R5): the old and the new price, and who is not told. */}
          <p>
            {currentCents === null
              ? `Change the price of this run to ${euro(cents)}? The home is not told.`
              : `Change the price of this run from ${euro(currentCents)} to ${euro(cents)}? The home is not told.`}
          </p>
          <div class="action-row">
            <Button variant="secondary" size="inline" disabled={busy} onClick={() => void save()}>
              Yes, change the price
            </Button>
            <Button variant="quiet" size="inline" disabled={busy} onClick={() => setStage('field')}>
              Not now
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

/** ⭐ board 2814 M7 (DD-7) - what a reschedule, an accepted ask or a cancel on a
 *  run passed to a taxi adds to its outcome: the taxi firm does not see this screen. */
const TAXI_TELL_FIRM_COPY = 'This run was passed to a taxi: tell the taxi firm.';
/** ⭐ board 2814 M7 - the pass outcome when a team alert already went out (2788
 *  DD-4's "already told" record: a `team_alert_sent` row on the timeline). */
const TAXI_TELL_TEAM_COPY = 'The team was told about this run: let them know.';
/** The note field's hint (S15): the field takes free text, so it says what not to type. */
const TAXI_NOTE_HINT = 'The taxi firm or its booking number. No resident details.';

/**
 * ⭐⭐ board 2814 M7 (plan DD-7) - "PASS TO A TAXI", AND ITS UNDO. A control block
 * like `MarkTravelled`: outside the action row and the load effect, never gold
 * (a pass is not this screen's primary). The host shows it on a confirmed run that
 * is not cancelled, and hides it while the cancel confirm is up.
 * ⛔ CONFIRM IN PLACE (DESIGN.md §5.1): a quiet press opens the optional note
 * field; "Continue" swaps it for one step that says what the pass does; "Yes, pass
 * it to a taxi" writes it; the outcome is said in words and the screen re-reads
 * (`onChanged`, never patched locally). On a passed run the same block offers
 * "Undo", with its own confirm.
 * The caret follows the step: the field when it opens or is refused, the step on
 * Continue, the outcome when it lands, the control on a back-out.
 */
function PassToTaxi({
  bookingId,
  passed,
  onChanged,
}: {
  bookingId: number;
  /** The server's `detail.taxi !== null`. */
  passed: boolean;
  onChanged: () => void;
}) {
  const [stage, setStage] = useState<'closed' | 'field' | 'confirm' | 'undo'>('closed');
  const [note, setNote] = useState('');
  const [fieldError, setFieldError] = useState<{ text: string; seq: number } | null>(null);
  const [outcome, setOutcome] = useState<{ kind: 'ok' | 'error'; text: string; tellTeam?: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  /** A REF, for `MarkTravelled`'s reason: a second tap in the same task reads it. */
  const inFlight = useRef(false);
  const fieldRef = useRef<HTMLInputElement | null>(null);
  const stepRef = useRef<HTMLDivElement | null>(null);
  const outcomeRef = useRef<HTMLDivElement | null>(null);
  const controlRef = useRef<HTMLButtonElement | null>(null);
  const backToControl = useRef(false);

  useEffect(() => {
    if (stage === 'field') fieldRef.current?.focus();
    if (stage === 'confirm' || stage === 'undo') stepRef.current?.focus();
    if (stage === 'closed' && backToControl.current) {
      backToControl.current = false;
      controlRef.current?.focus();
    }
  }, [stage, fieldError?.seq]);
  useEffect(() => {
    if (outcome !== null) outcomeRef.current?.focus();
  }, [outcome]);

  const refuseField = (text: string): void => {
    setStage('field');
    setFieldError((prev) => ({ text, seq: (prev?.seq ?? 0) + 1 }));
  };
  const open = (): void => {
    setOutcome(null);
    setFieldError(null);
    setNote('');
    setStage(passed ? 'undo' : 'field');
  };
  const backOut = (): void => {
    backToControl.current = true;
    setStage('closed');
  };
  const proceed = (): void => {
    // The server's bound, counted as it counts (code points), so the field says
    // so before the confirm step rather than after it.
    if ([...note.trim()].length > TAXI_NOTE_MAX_CHARS) {
      refuseField(TAXI_PASS_REFUSAL_COPY.taxi_note_too_long);
      return;
    }
    setFieldError(null);
    setStage('confirm');
  };
  const pass = async (): Promise<void> => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    try {
      const res = await passToTaxiRequest(bookingId, note);
      if (res.ok) {
        setStage('closed');
        setOutcome(res.outcome === 'passed'
          ? { kind: 'ok', text: 'This run is passed to a taxi.', tellTeam: res.tellTeam }
          : { kind: 'ok', text: 'This run was already passed to a taxi. Nothing was changed.' });
        onChanged();
        return;
      }
      if (res.code === 'taxi_note_too_long' || res.code === 'taxi_note_not_text') {
        refuseField(res.message);
        return;
      }
      setStage('closed');
      setOutcome({ kind: 'error', text: res.message });
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  const undo = async (): Promise<void> => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    try {
      const res = await undoTaxiPassRequest(bookingId);
      setStage('closed');
      if (res.ok) {
        setOutcome(res.outcome === 'undone'
          ? { kind: 'ok', text: 'This run is back with us. Tell the taxi firm.' }
          : { kind: 'ok', text: 'This run was not passed to a taxi. Nothing was changed.' });
        onChanged();
        return;
      }
      setOutcome({ kind: 'error', text: res.message });
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <>
      {stage === 'closed' && (
        <div class="action-row">
          {/* ⭐ board 2812 M3 fix round 3 (F3-13): the default size, not `inline`, so it
              stands as tall as "Mark this run as travelled" above it in the next slot. */}
          <Button variant="quiet" elementRef={controlRef} onClick={open}>
            {passed ? 'Undo the taxi pass' : 'Pass to a taxi'}
          </Button>
        </div>
      )}
      {outcome !== null && (
        <Banner kind={outcome.kind} elementRef={outcomeRef} tabIndex={-1}>{outcome.text}</Banner>
      )}
      {outcome?.tellTeam === true && <Banner kind="warn">{TAXI_TELL_TEAM_COPY}</Banner>}
      {stage === 'field' && (
        <>
          <FormField
            id="taxi-note"
            label="Note for the taxi (optional)"
            autoComplete="off"
            value={note}
            hint={TAXI_NOTE_HINT}
            elementRef={fieldRef}
            onInput={(v: string) => {
              setNote(v);
              setFieldError(null);
            }}
            error={fieldError?.text}
          />
          <div class="action-row">
            <Button variant="secondary" size="inline" onClick={proceed}>Continue</Button>
            <Button variant="quiet" size="inline" onClick={backOut}>Keep it with us</Button>
          </div>
        </>
      )}
      {stage === 'confirm' && (
        <div role="group" aria-label="Check the taxi pass" tabIndex={-1} ref={stepRef}>
          <p>Pass this run to a taxi? Nothing is charged or billed for it while it is with the taxi. The home is not told.</p>
          <div class="action-row">
            <Button variant="secondary" size="inline" disabled={busy} onClick={() => void pass()}>
              Yes, pass it to a taxi
            </Button>
            <Button variant="quiet" size="inline" disabled={busy} onClick={() => setStage('field')}>
              Not now
            </Button>
          </div>
        </div>
      )}
      {stage === 'undo' && (
        <div role="group" aria-label="Check the undo" tabIndex={-1} ref={stepRef}>
          {/* ⭐ board 2814 M8R1 (R12): plain words for "the money paths". */}
          <p>Take this run back from the taxi? It can be billed and paid for again.</p>
          <div class="action-row">
            <Button variant="secondary" size="inline" disabled={busy} onClick={() => void undo()}>
              Yes, take it back
            </Button>
            <Button variant="quiet" size="inline" disabled={busy} onClick={backOut}>
              Keep it with the taxi
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

/** ⭐ Board 2814 M4 (Captain ruling (c)) - the travelled run's cancel confirm. */
const TRAVELLED_CANCEL_HINT =
  'This trip ran, so its run is on the month\u2019s bill. Cancelling it here takes the run off the bill.';

function CancelRun({
  bookingId,
  depositPaidCents,
  depositRefundedCents,
  cancellable,
  notGoingAhead,
  travelled,
  taxiPassed = false,
  confirming,
  setConfirming,
  onCancelled,
}: {
  bookingId: number;
  /** The PAID deposit in cents, or null when nothing has been paid on this
   *  booking. Null is what makes this the pre-M7 shape: no amount goes on the
   *  wire, so the server's deposit-paid guards do not relax. */
  depositPaidCents: number | null;
  /**
   * ⛔ board 2106 M6 delta 3 — what has ALREADY been refunded against that same
   * deposit, so this control's ceiling is the server's ceiling. Null when there is
   * no single paid deposit (the shape the refund path refuses rather than guesses
   * at) and, on any fixture predating this delta, absent — which `?? 0` reads as
   * "nothing refunded". See `overDeposit` for why that is the fail-safe direction.
   */
  depositRefundedCents: number | null;
  /** False once the run is cancelled or completed. The controls go, the outcome
   *  banner stays — see the render. */
  cancellable: boolean;
  /** board 2343 — the server's advisory "may be ended as not going ahead"
   *  (`detail.notGoingAhead`). True shows the plain path instead of the
   *  disclosure; see the render. */
  notGoingAhead: boolean;
  /**
   * ⭐ Board 2814 M4 (Captain ruling (c), 2026-10-06) - the run has TRAVELLED. The
   * confirm then says the trip ran and that cancelling takes the run off the
   * month's bill, and ONLY that confirm sends `travelledAcknowledged`: booking
   * detail's acknowledged cancel is the one correction path for a travelled run.
   */
  travelled: boolean;
  /** ⭐ board 2814 M7 (DD-7): the run was passed to a taxi, so a cancel says to tell the firm. */
  taxiPassed?: boolean;
  /** Lifted to the screen, because §5.1's rule is about the SCREEN's primary,
   *  which is not inside this component. */
  confirming: boolean;
  setConfirming: (v: boolean) => void;
  /** Re-read the booking so the status chip says 'Cancelled' (and, after a
   *  refused "Not going ahead", so the flag the server re-derives brings the
   *  disclosure back). Never patched in locally, which would claim a write the
   *  server may not have made. */
  onCancelled: () => void;
}) {
  // Prefilled with the WHOLE deposit: the commonest cancellation refunds all of
  // it, and a blank box on a money field invites a typo where the default is
  // already the right answer.
  const [refundEuros, setRefundEuros] = useState(
    depositPaidCents === null ? '' : (depositPaidCents / 100).toFixed(2),
  );
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<CancelBookingOutcome | null>(null);
  /** board 2343 — the last press was the plain "Not going ahead" path, so its
   *  banner renders outside the disclosure (see the render). */
  const [plainPress, setPlainPress] = useState(false);
  /**
   * ⛔ A REF, NOT STATE. `setState` does not re-render before a second tap in
   * the same task, so a `busy` boolean in state cannot stop a double submit —
   * only a ref is read back synchronously. The SERVER latch (`result.changed`)
   * is the real guarantee that one tap means one refund; this one only saves the
   * wasted round trip, and it is reset in `finally` so a transient failure stays
   * retryable.
   */
  const inFlight = useRef(false);
  const bannerRef = useRef<HTMLDivElement | null>(null);

  // Banner.tsx's own docblock: role="status" tells a screen-reader user what
  // happened and does nothing for a keyboard user's caret, and the control that
  // caused this is gone (or disabled) by now. So the caret is moved onto the
  // announcement rather than dropped to <body>.
  useEffect(() => {
    if (outcome !== null) bannerRef.current?.focus();
  }, [outcome]);

  /**
   * ⭐ board 2343 rework 1 (UX review) — THE PLAIN PATH NEVER DROPS FOCUS TO
   * <body>. Opening its confirm unmounts "Not going ahead" (which had focus), and
   * "Keep the job" unmounts the pair. So the opener parks where focus goes next —
   * the consequence sentence, which is also the Yes button's description — and
   * Keep parks the way back; the effect below moves it once the render lands.
   */
  const plainFocus = useRef<'hint' | 'rest' | null>(null);
  const plainHintRef = useRef<HTMLParagraphElement | null>(null);
  const plainRestRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    const want = plainFocus.current;
    plainFocus.current = null;
    if (want === 'hint') plainHintRef.current?.focus();
    if (want === 'rest') plainRestRef.current?.focus();
  }, [confirming]);

  /**
   * ⛔ AN EMPTY BOX IS NOT ZERO, and reading it as zero would be the worst
   * default available here: `Number('')` is 0, so a cleared field would have
   * read as "cancel and keep the WHOLE deposit" on a screen where that is a real
   * and irreversible choice. Zero has to be typed.
   *
   * Otherwise the same expression Quote.tsx uses for its one money input,
   * widened from `> 0` to `>= 0` because zero is the top tier of the
   * cancellation scale rather than a rejected amount.
   */
  const refundCents = ((): number | null => {
    if (depositPaidCents === null || refundEuros.trim() === '') return null;
    const euros = Number(refundEuros);
    return Number.isFinite(euros) && euros >= 0 ? Math.round(euros * 100) : null;
  })();
  /**
   * ⛔ CHECKED HERE AS WELL AS ON THE SERVER, because the server's refusal
   * arrives too late to help: it answers `exceeds_remaining` only AFTER the
   * CANCEL HAS COMMITTED, so a typo costs a run that is gone for good and a
   * refund that never went out. Catching the common typo before the request is
   * made is the point of this line.
   *
   * ⭐⭐ IT IS NOW THE SAME BOUND AS THE SERVER'S — board 2106 M6 delta 3, and
   * this paragraph is the record of the change because the paragraph it replaces
   * spent itself explaining why the two DISAGREED.
   *
   * What it used to say, and what was true until this delta: HERE the typed amount
   * was checked against `detail.payment.amount_cents`, the GROSS deposit; on the
   * SERVER against `paid - consumedRefundCents(existing)`, i.e. what is LEFT after
   * every refund already on that payment. "The two agree only while nothing has
   * been refunded yet" — and a Stripe DASHBOARD refund lands in `payment_refunds`
   * without anyone here asking, so the disagreement arrived unannounced.
   *
   * ⛔ WHY THAT MATTERED MORE THAN ITS "SAFE DIRECTION" ARGUMENT SUGGESTED. The
   * old note reasoned that the gap only ever FAILED TO CATCH something early,
   * never admitted something the server would allow. True — but the server's
   * refusal arrives AFTER THE CANCEL HAS COMMITTED (`app.ts:602-620`), so
   * "failed to catch it early" is the whole of the harm: the run is gone for good
   * and the refund never went out. A pre-commit ceiling that is knowably too high
   * is the only save there is, set wrong.
   *
   * ⛔ THE SUBTRACTION IS THE SERVER'S OWN, NOT A SECOND ONE. `depositRefundedCents`
   * is `consumedRefundCents` — the same function, over the same single
   * `deposit_paid` row `lockRefundablePayment` locks — computed in the route and
   * passed in. Nothing is re-summed on this screen.
   *
   * ⚠️ `?? 0` IS THE FAIL-SAFE DIRECTION, and it is a real branch rather than
   * defensive noise: every fixture predating this delta sends no key at all. An
   * absent figure must read as "nothing refunded", which leaves the ceiling where
   * it was — too high, caught late — rather than as "everything refunded", which
   * would block a legitimate refund outright.
   */
  const alreadyRefundedCents = depositRefundedCents ?? 0;
  // A plain number, not `number | null`: it is only ever read inside the
  // `depositPaidCents !== null` branch below, and `overDeposit` keeps that null
  // check of its own — so the nullability lives on the ONE test that needs it
  // rather than being threaded through every sentence that renders the figure.
  // ⛔ FLOORED AT ZERO (M6 security gate, LOW-5). A refund taken at the Stripe
  // Dashboard that is LARGER than the payment had left is RECORDED rather than
  // rejected (core/refund-webhook.ts says so in the guard voice), so this
  // subtraction can go negative and the hint rendered "Up to -€5.00". The
  // control was never unsafe - `overDeposit` blocks every positive amount once
  // the remainder is 0 - but a negative ceiling on a money screen is a figure
  // Helen has to decide to disbelieve.
  const refundableCents = Math.max(0, (depositPaidCents ?? 0) - alreadyRefundedCents);
  const overDeposit = refundCents !== null && depositPaidCents !== null && refundCents > refundableCents;

  /**
   * ⛔ 'Cancel THE run for good', NOT 'Cancel THIS run for good', and the one
   * word is load-bearing. The <summary> above is 'Cancel this run', and
   * Playwright matches accessible names by case-insensitive SUBSTRING - so the
   * obvious label would have CONTAINED the summary's name and made the net's
   * query for either of them ambiguous on the unpaid booking, where both render
   * at once. Caught by the collision gate in
   * test/client/heading-label-collision.spec.tsx, which exists for exactly this.
   */
  const restLabel = depositPaidCents === null
    ? 'Cancel the run for good'
    : refundCents === null
      ? 'Cancel and refund the amount above'
      : refundCents === 0
        ? 'Cancel and keep the full deposit'
        : `Cancel and refund ${euro(refundCents)}`;
  // DERIVED, never re-typed: the confirm must restate the SAME consequence.
  const confirmLabel = `Yes, ${restLabel.charAt(0).toLowerCase()}${restLabel.slice(1)}`;
  const blocked = busy || overDeposit || (depositPaidCents !== null && refundCents === null);

  const run = async (plainPath = false) => {
    // The plain path carries no amount, so only `busy` can block it.
    if (inFlight.current || (plainPath ? busy : blocked)) return;
    inFlight.current = true;
    setBusy(true);
    setPlainPress(plainPath);
    try {
      // ⭐ Board 2814 M4: the acknowledgement rides ONLY a confirm that said the trip
      // ran (`travelled`, which renders the sentence below), never the plain path.
      const res = plainPath
        ? await cancelBookingRun(bookingId, undefined, 'not-going-ahead')
        : await cancelBookingRun(
          bookingId,
          depositPaidCents === null ? undefined : refundCents ?? undefined,
          undefined,
          travelled ? { travelledAcknowledged: true } : {},
        );
      setConfirming(false);
      setOutcome(res);
      // ⭐ Board 2814 M4: a `refused-travelled` re-reads too, so the spine shows the
      // run as travelled and the next confirm says so.
      if (res.kind === 'cancelled' || res.kind === 'already-cancelled' || res.kind === 'refused-not-going-ahead'
        || res.kind === 'refused-travelled') {
        onCancelled();
      }
    } finally {
      setBusy(false);
      inFlight.current = false;
    }
  };

  // An already-cancelled booking opened fresh offers nothing and says nothing:
  // the status chip at the top of the record already says it.
  if (!cancellable && outcome === null) return null;

  const banners = (
    <>
      {/**
        * ⛔ THE TONE MAP IS ABOUT WHAT ACTUALLY HAPPENED, not about whether we
        * got what we asked for. `Banner`'s kind drives ARIA: 'error' announces
        * as role="alert" and interrupts, everything else is role="status". A
        * refused or unknown refund is NOT an error banner, because the booking
        * IS cancelled — an interrupting alert would misdescribe the request as
        * having failed. Only the 409, where nothing moved at all, is an error.
        */}
      {outcome?.kind === 'cancelled' && outcome.refund === null && !plainPress && (
        <Banner kind="ok" elementRef={bannerRef} tabIndex={-1}>
          The run is cancelled. Any payment link on it has been switched off.
        </Banner>
      )}
      {/* board 2343 rework 1 — the plain path's outcome says what ITS confirm
          promised, in its words (job, Cancelled, deposit link, nothing sent). */}
      {outcome?.kind === 'cancelled' && outcome.refund === null && plainPress && (
        <Banner kind="ok" elementRef={bannerRef} tabIndex={-1}>{NOT_GOING_AHEAD_DONE_COPY}</Banner>
      )}
      {outcome?.kind === 'cancelled' && outcome.refund?.outcome === 'refunded' && (
        <Banner kind="ok" elementRef={bannerRef} tabIndex={-1}>
          The run is cancelled and {euro(outcome.refund.amountCents)} has been sent back to the customer.
        </Banner>
      )}
      {outcome?.kind === 'cancelled' && outcome.refund?.outcome === 'retained' && (
        <Banner kind="ok" elementRef={bannerRef} tabIndex={-1}>{CANCEL_RETAINED_COPY}</Banner>
      )}
      {outcome?.kind === 'cancelled' && outcome.refund?.outcome === 'refused' && (
        <Banner kind="warn" elementRef={bannerRef} tabIndex={-1}>{CANCEL_REFUND_REFUSED_COPY}</Banner>
      )}
      {/* ⛔ THE SHARED CONSTANT, NEVER A RESTATEMENT. The server's 502 body and
          this sentence are the same string by construction: they drifted once
          already (the server said "check before trying again" while the client
          said "do not try again") on the ONE route where a retry is a second
          real refund. The run is cancelled either way, so this is a warn. */}
      {outcome?.kind === 'cancelled' && outcome.refund?.outcome === 'unknown' && (
        <Banner kind="warn" elementRef={bannerRef} tabIndex={-1}>
          The run is cancelled. {REFUND_OUTCOME_UNKNOWN_COPY}
        </Banner>
      )}
      {/* ⭐⭐ T2088 REWORK 1 — THE HOME WAS NOT TOLD. The run IS cancelled (so this
          is a warn, never an error), and the nurse's My requests still reads
          "The taxi is booked." — which is exactly what Captain's live drive found
          and what this banner exists to stop happening silently. ⛔ The two quiet
          outcomes (`cancelled`, `already-ended`) render NOTHING: an ordinary cancel
          must not grow a sentence, or the screen teaches Helen to skim past it. */}
      {outcome?.kind === 'cancelled' && outcome.portalRequest === 'failed' && (
        <Banner kind="warn" elementRef={bannerRef} tabIndex={-1}>{CANCEL_PORTAL_RETRY_COPY}</Banner>
      )}
      {outcome?.kind === 'cancelled' && outcome.portalRequest === 'unreachable' && (
        <Banner kind="warn" elementRef={bannerRef} tabIndex={-1}>{CANCEL_PORTAL_NOT_TOLD_COPY}</Banner>
      )}
      {outcome?.kind === 'cancelled' && taxiPassed && <Banner kind="warn">{TAXI_TELL_FIRM_COPY}</Banner>}
      {outcome?.kind === 'already-cancelled' && (
        <Banner kind="warn" elementRef={bannerRef} tabIndex={-1}>{CANCEL_ALREADY_COPY}</Banner>
      )}
      {outcome?.kind === 'refused' && (
        <Banner kind="error" elementRef={bannerRef} tabIndex={-1}>{CANCEL_REFUSED_COPY}</Banner>
      )}
      {/* ⛔ T1011 M6 — ITS OWN ARM, ABOVE `refused` IN MEANING THOUGH NOT IN
          ORDER (the kinds are disjoint, so these two can never both render).
          The generic refusal says "it may already be finished", which is FALSE
          about an invoiced run and points nowhere; this one names the document
          and the remedy. 'error' tone, like the other refusal, because nothing
          moved at all. */}
      {outcome?.kind === 'refused-invoiced' && (
        <Banner kind="error" elementRef={bannerRef} tabIndex={-1}>
          {cancelInvoicedCopy(outcome.invoiceNumber)}
        </Banner>
      )}
      {/* board 2343 — "Not going ahead" refused. 'error' tone: nothing moved. The
          copy points at the disclosure, which renders below it after the reload. */}
      {outcome?.kind === 'refused-not-going-ahead' && (
        <Banner kind="error" elementRef={bannerRef} tabIndex={-1}>{NOT_GOING_AHEAD_REFUSED_COPY}</Banner>
      )}
      {/* ⭐ Board 2814 M4 (ruling (c)): the screen did not know the run had travelled
          (it was marked meanwhile), so nothing moved; after the re-read the confirm
          below says so and carries the acknowledgement. */}
      {outcome?.kind === 'refused-travelled' && (
        <Banner kind="error" elementRef={bannerRef} tabIndex={-1}>{runTravelledText('booking')}</Banner>
      )}
      {outcome?.kind === 'error' && (
        <Banner kind="error" elementRef={bannerRef} tabIndex={-1}>{CANCEL_FAILED_COPY}</Banner>
      )}
    </>
  );

  /**
   * ⭐⭐ board 2343 — "NOT GOING AHEAD", THE PLAIN PATH. When the server says this
   * job may still be dropped (a web enquiry's job, new or quoted, nothing paid:
   * `notGoingAheadRefusal`), the action is on the screen rather than behind the
   * disclosure, because Helen's workaround for not finding it was a EUR 1 quote.
   * It is still §5.1's shape: a rule between it and the primary's row, the
   * Yes/Keep swap, and the primary hidden while the confirm is up.
   * ⛔ NEVER BOTH: when the plain path shows, the disclosure does not.
   * ⛔ ITS BANNER RENDERS OUTSIDE ANY <details>. Every press re-reads the booking,
   * which flips `notGoingAhead` false (cancelled, or refused), and the disclosure
   * that then mounts is CLOSED: a banner inside it would be hidden and unfocusable
   * exactly when Helen needs it. So a plain press records itself (`plainPress`)
   * and its outcome renders above the controls whatever the flag reads next; a
   * later press on the revealed disclosure clears it, so a banner never renders
   * twice or claims `bannerRef` twice.
   */
  const plain = notGoingAhead && cancellable;
  const bannersOutside = plainPress && outcome !== null;

  return (
    <>
      {bannersOutside && banners}
      {plain && (
        <>
          {/* The rule separates this from the primary's row AT REST. Mid-confirm
              the primary is hidden, so the rule would sit directly under the
              one above that row and draw a double line (seen on the seat). */}
          {!confirming && <hr class="section-rule" />}
          {confirming && (
            <p class="field-hint" id="not-going-ahead-hint" tabIndex={-1} ref={plainHintRef}>
              The job moves to Cancelled. Any deposit link stops working. Nothing is sent to the customer.
            </p>
          )}
          <div class="action-row">
            {confirming ? (
              <>
                <button
                  type="button"
                  class="btn btn--quiet btn--destructive"
                  aria-describedby="not-going-ahead-hint"
                  disabled={busy}
                  onClick={() => void run(true)}
                >
                  Yes, not going ahead
                </button>
                <button
                  type="button"
                  class="btn btn--quiet"
                  onClick={() => {
                    plainFocus.current = 'rest';
                    setConfirming(false);
                  }}
                >
                  Keep the job
                </button>
              </>
            ) : (
              <button
                type="button"
                class="btn btn--quiet btn--destructive"
                ref={plainRestRef}
                disabled={busy}
                onClick={() => {
                  plainFocus.current = 'hint';
                  setConfirming(true);
                }}
              >
                Not going ahead
              </button>
            )}
          </div>
        </>
      )}
      {!plain && (cancellable || !bannersOutside) && (
        <details
          onToggle={(e) => {
            // Collapsing mid-confirm must not leave the screen's primary hidden
            // behind a closed disclosure.
            if (!(e.currentTarget as HTMLDetailsElement).open) setConfirming(false);
          }}
        >
          <summary class="capture-hint">Cancel this run</summary>
          {cancellable && (
            <>
              {depositPaidCents !== null && (
                <>
                  {/* The shape of this app's ONE existing money input (Quote.tsx's
                      "Enter agreed price (€)"), not a second one invented beside it.

                      ⭐ T1002 M7 (ux-review R6) — THE CEILING IS STATED BEFORE
                      TYPING, not only after a refusal. `max` puts it in the
                      accessibility tree (aria-valuemax) and the always-rendered hint
                      puts it on the screen, so nobody has to discover the bound by
                      tripping it and watching the button go dark. */}
                  <label class="field-label" for="cancel-refund-amount">Refund amount (€)</label>
                  <input
                    id="cancel-refund-amount"
                    class="login-input price-input"
                    type="number"
                    inputMode="decimal"
                    max={(depositPaidCents / 100).toFixed(2)}
                    /* ⛔ THE IDREFS ARE EMITTED ON EXACTLY THE CONDITIONS THEIR
                       NODES ARE - SendQuote's channelOffId discipline, and the same
                       reason: a dangling idref announces nothing and is worse than
                       no description. The ceiling line always renders here, the
                       refusal line only when it applies, so the list has one member
                       or two and never names an absent node. */
                    aria-describedby={overDeposit ? 'cancel-refund-max cancel-refund-over' : 'cancel-refund-max'}
                    value={refundEuros}
                    onInput={(e) => setRefundEuros((e.target as HTMLInputElement).value)}
                  />
                  {/* ⛔ THE HINT QUOTES THE FIGURE THE CHECK ACTUALLY USES. Leaving
                      these two sentences on the GROSS deposit while `overDeposit`
                      bounds on the net one would put a ceiling on screen that the
                      control itself refuses — the operator reads "Up to €42", types
                      €42, and is told €42 is too much. Both sentences take the
                      refundable figure, and when something has already gone back they
                      say so rather than quietly naming a smaller number. */}
                  <span id="cancel-refund-max" class="field-hint">
                    {alreadyRefundedCents > 0
                      ? `Up to ${euro(refundableCents)} - the deposit was ${euro(depositPaidCents)} and ${euro(alreadyRefundedCents)} is already refunded or on its way back.`
                      : `Up to ${euro(depositPaidCents)}, the deposit paid on this booking.`}
                  </span>
                  {overDeposit && (
                    <span id="cancel-refund-over" class="field-hint">
                      {alreadyRefundedCents > 0
                        ? `That is more than is left to refund on this booking, which is ${euro(refundableCents)}.`
                        : `That is more than the deposit on this booking, which is ${euro(depositPaidCents)}.`}
                    </span>
                  )}
                </>
              )}
              {/* ⭐ Board 2814 M4 (Captain ruling (c)): on a travelled run the confirm
                  says the trip ran and what cancelling does to the bill, BEFORE the
                  press; only this confirm sends the acknowledgement. */}
              {confirming && travelled && (
                <p class="field-hint">{TRAVELLED_CANCEL_HINT}</p>
              )}
              <div class="action-row">
                {confirming ? (
                  <>
                    {/* ⚠️ `blocked`, NOT `busy` (code-review R7). The amount field
                        stays editable mid-confirm, so an over-deposit figure typed
                        AFTER the confirm opened left a live-looking button whose
                        only effect was `run()`'s own re-check returning early. A
                        control that looks armed and does nothing is the worse of the
                        two failures on a money screen; the same predicate now drives
                        both halves of the pair. */}
                    <button type="button" class="btn btn--quiet btn--destructive" disabled={blocked} onClick={() => void run()}>
                      {confirmLabel}
                    </button>
                    {/* 'Keep the run', not Inbox's bare 'Keep'. Two reasons, both
                        measured: beside a label that itself contains "keep the full
                        deposit" a bare "Keep" reads as "keep the deposit", and
                        Playwright matches accessible names by case-insensitive
                        SUBSTRING, so a bare 'Keep' would also match the Yes button
                        and make the pair mutually ambiguous to the net. */}
                    <button type="button" class="btn btn--quiet" onClick={() => setConfirming(false)}>
                      Keep the run
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    class="btn btn--quiet btn--destructive"
                    disabled={blocked}
                    onClick={() => setConfirming(true)}
                  >
                    {restLabel}
                  </button>
                )}
              </div>
            </>
          )}
          {!bannersOutside && banners}
        </details>
      )}
    </>
  );
}

/**
 * ⭐ "Mark this run as travelled" — T1033 M7 part 2. THE CALLER that makes
 * `markBookingTravelled` more than a tested layer.
 *
 * ⛔ WHY THIS COMPONENT IS THE DELIVERABLE AND THE ROUTE IS NOT. A route with
 * full API-layer coverage and no client caller reads exactly like a working
 * feature — this repo shipped precisely that in T1011 (`POST
 * /api/account-invoices`, the centrepiece of a build named "assemble, then
 * issue", caller-less under 5257 passing tests). So the spec that discharges
 * this milestone clicks THIS button; a test that calls `markRunTravelled`
 * directly proves nothing about whether the stage can ever light up.
 *
 * ⛔ NO CONFIRM STEP, DELIBERATELY, and the line is worth drawing because the
 * write IS permanent (`booking_events` is append-only and `roles.sql` withholds
 * DELETE from the app role). This screen confirms for `Cancel this run` because
 * that destroys a run and moves money; "the bus came back" is neither. The
 * operators abandoned a previous tool for having too many buttons, and a
 * confirm on every irreversible-but-harmless action is how that happens. ⚠️ If
 * a mis-click ever proves costly, the remedy is a confirm here — NOT an erasure
 * path, which the audit table must never grow.
 */
function MarkTravelled({
  bookingId,
  travelled,
  onMarked,
}: {
  bookingId: number;
  /** Already recorded. The control goes; the spine above already says `done`. */
  travelled: boolean;
  /** Re-read the booking so the spine advances. ⛔ Never patched in locally —
   *  that would claim a write the server may not have made. */
  onMarked: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<MarkTravelledOutcome | null>(null);
  /** A REF, not state, for CancelRun's reason: `setState` does not re-render
   *  before a second tap in the same task, so only a ref is read back
   *  synchronously. The server's own idempotency is the real guarantee that two
   *  taps mean one event; this saves the wasted round trip. */
  const inFlight = useRef(false);
  const bannerRef = useRef<HTMLDivElement | null>(null);

  // Banner.tsx: role="status" tells a screen-reader user what happened and does
  // nothing for a keyboard user's caret, and the control that caused this is
  // gone by then. Move the caret onto the announcement.
  useEffect(() => {
    if (outcome !== null) bannerRef.current?.focus();
  }, [outcome]);

  const run = async () => {
    if (inFlight.current || busy) return;
    inFlight.current = true;
    setBusy(true);
    try {
      const res = await markRunTravelled(bookingId);
      setOutcome(res);
      // ⛔ 'already' RE-READS TOO. It means the server holds an event this
      // screen's spine does not show, i.e. the spine is STALE — so refusing to
      // re-read would leave Helen looking at the wrong answer after being told
      // the right one.
      //
      // ⛔ 'cancelled' DELIBERATELY DOES NOT, and the reason is structural
      // rather than an oversight (code-review raised the staleness; the
      // suggested re-read was MEASURED to be worse). This component renders
      // INSIDE the screen's `lifecycle !== null` guard, and a cancelled run
      // serves `lifecycle: null` — so re-reading here would unmount this very
      // component and TAKE THE REFUSAL BANNER WITH IT, leaving Helen with a
      // vanished panel and no explanation of why nothing happened.
      // ⚠️ THE ACCEPTED COST, stated rather than hidden: on the rare
      // concurrent-cancel path the spine beside the banner is momentarily
      // stale. Nothing is written and the server stays the sole writer, so this
      // is a display lag, not an integrity problem; the next open re-reads.
      // ⇒ Fixing it properly means lifting the outcome banner OUT of the
      // lifecycle guard, which is a render-structure change, not a one-line one.
      // ⭐ board 2377 — 'not-confirmed' does not re-read either, for the SAME
      // structural reason: an unconfirmed run serves `lifecycle: null` too.
      if (res === 'marked' || res === 'already') onMarked();
    } finally {
      setBusy(false);
      inFlight.current = false;
    }
  };

  // Already travelled and nothing to report: the spine above says `done`, so a
  // disabled button would only add a control that can never do anything.
  if (travelled && outcome === null) return null;

  return (
    <>
      {!travelled && (
        <div class="action-row">
          <button type="button" class="btn btn--quiet" disabled={busy} onClick={() => void run()}>
            Mark this run as travelled
          </button>
        </div>
      )}
      {outcome === 'marked' && (
        <Banner kind="ok" elementRef={bannerRef} tabIndex={-1}>
          Recorded: this run travelled.
        </Banner>
      )}
      {/* ⚠️ 'ok', NOT a warning. The run IS marked travelled — which is what was
          asked — so an alarming tone would invite Helen to go looking for a
          problem that does not exist. */}
      {outcome === 'already' && (
        <Banner kind="ok" elementRef={bannerRef} tabIndex={-1}>
          This run was already recorded as travelled.
        </Banner>
      )}
      {/* 'error' tone: nothing was written, and the reason is a real refusal
          rather than a transient fault, so it must not read as "try again". */}
      {outcome === 'cancelled' && (
        <Banner kind="error" elementRef={bannerRef} tabIndex={-1}>
          This run is cancelled, so it cannot be marked as travelled.
        </Banner>
      )}
      {outcome === 'not-confirmed' && (
        <Banner kind="error" elementRef={bannerRef} tabIndex={-1}>
          {MARK_TRAVELLED_NOT_CONFIRMED_COPY}
        </Banner>
      )}
      {/* ⭐ board 2814 M7 (DD-7): the run was passed to a taxi in the meantime (the
          control is hidden on a passed run, so only a stale screen meets this). */}
      {outcome === 'taxi-passed' && (
        <Banner kind="error" elementRef={bannerRef} tabIndex={-1}>
          This run was passed to a taxi, so it cannot be marked as travelled.
        </Banner>
      )}
      {outcome === 'error' && (
        <Banner kind="error" elementRef={bannerRef} tabIndex={-1}>
          Recording that failed. Nothing was changed. Try again in a moment.
        </Banner>
      )}
    </>
  );
}

/**
 * The two rails a balance may be asked on, IN THE ORDER THE PICKER OFFERS THEM.
 *
 * ⛔ THE ORDER IS LOAD-BEARING TWICE OVER, which is why it is a const and not an
 * inline literal: it is what Helen reads top to bottom, and it is also the
 * fallback the default rule lands on when nothing else decides
 * (`reachable[0]`). Text first, because a deposit-paid consumer booking almost
 * always has a mobile and the text rail is the one that reaches a customer in
 * the minute rather than at the next inbox check.
 *
 * ⚠️ NOT `BALANCE_ASK_CHANNELS`, which spells the same two the other way round
 * (`['email', 'text']`). That constant is the route's VALIDATION vocabulary and
 * nothing about it is a presentation order; reusing it here would make an
 * operator-facing default move the day somebody re-sorted a validator.
 * The VALUES are still the schema's, through the `BalanceAskChannel` type, so a
 * third spelling cannot appear on this screen without `tsc` refusing it.
 */
const BALANCE_CHANNEL_OPTIONS: readonly { value: BalanceAskChannel; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'email', label: 'Email' },
];

/** ⭐ board 2811 M7: `Thu 15 Oct at 10:30` split into its day and its time; null on junk. */
function dublinDayAndTime(iso: string): { day: string; time: string } | null {
  const s = dublinDayAtTime(iso);
  const at = s.lastIndexOf(' at ');
  return at < 0 ? null : { day: s.slice(0, at), time: s.slice(at + 4) };
}

/**
 * ⭐ board 2811 R3 (item 1): the sum an `asked` balance row asked for, as the
 * tail of Helen's "Balance emailed on ..." / "Balance texted on ..." line:
 * ", asking for €178". Empty when the server sent no figure (`askedCents`).
 */
function askedForWords(askedCents: number | undefined): string {
  return askedCents === undefined ? '' : `, asking for ${euro(askedCents)}`;
}

/**
 * ⭐ board 2811 M7 (plan M7 step 2, D-17): a SENT balance text. On time: "Balance
 * texted on Thu 15 Oct at 10:31." Late (`dueIso` set): "Balance texted late on Thu
 * 15 Oct at 14:20 (it was due at 10:00).", naming the due DAY too when it was
 * another day, so a text held over a weekend never reads as due that afternoon.
 * ⭐ board 2811 R3: each ends with the sum asked for (`askedForWords`).
 */
function balanceTextSentCopy(sentIso: string, dueIso: string | null, askedCents?: number): string {
  const askedFor = askedForWords(askedCents);
  const sent = dublinDayAndTime(sentIso);
  if (sent === null) return `Balance texted${askedFor}.`;
  const due = dueIso === null ? null : dublinDayAndTime(dueIso);
  if (due === null) return `Balance texted on ${sent.day} at ${sent.time}${askedFor}.`;
  const dueWhen = due.day === sent.day ? `at ${due.time}` : `on ${due.day} at ${due.time}`;
  return `Balance texted late on ${sent.day} at ${sent.time} (it was due ${dueWhen})${askedFor}.`;
}

/**
 * ⭐ board 2811 M7 (A3 item 4 with the Helen-test ending): a LATE balance text.
 * It names the due day and time (a late text may be days late with the
 * killswitch off) and Helen's one remedy past the scheduler's next tick.
 * ⭐ board 2811 rework part 2c (follow-up 2): when the server KNOWS nothing here
 * will send it (`automaticTextsOff`), "in a few minutes" would imply a send that
 * cannot happen, so the note gives the `due` note's reason instead.
 */
function balanceTextLateCopy(dueIso: string, automaticTextsOff: boolean): string {
  const due = dublinDayAtTime(dueIso);
  const lead = due === ''
    ? 'The balance text is late and has not gone yet.'
    : `The balance text is late: it was due on ${due} and has not gone yet.`;
  return automaticTextsOff
    ? `${lead} ${AUTOMATIC_TEXTS_OFF_COPY}`
    : `${lead} If it is still late in a few minutes, ring the customer and tell Alex.`;
}

/** ⭐ board 2811 rework part 2c: the one reason sentence the `due` and `late`
 *  notes give when `automaticTextsOff` is set. */
const AUTOMATIC_TEXTS_OFF_COPY = 'Automatic texts are not running on this system, so tell Alex.';

/**
 * ⭐ board 2811 rework part 2c (Captain item 13): THE ASK BUTTON'S LABEL, ONE
 * SOURCE for the button and for the failed-text note that names it, so the two
 * cannot drift. `single` is the one reachable rail, or null when both are.
 */
function balanceAskLabel(single: BalanceAskChannel | null): string {
  return single === null
    ? 'Ask for the balance'
    : single === 'email'
      ? 'Ask for the balance by email'
      : 'Ask for the balance by text';
}

/**
 * ⭐ board 2811 rework part 2c (Captain item 13): HOW to text again, by the
 * control's OWN label. "Text it again below." pointed at the gold "Send quote to
 * customer", the next control under the note; the ask is further down. null when
 * there is no mobile on file (no text rail to point at). Both failed-text
 * sentences that name the control build it here, so they cannot drift.
 */
function textAgainWords(phone: string | null, email: string | null): string | null {
  if (phone === null) return null;
  return email === null
    ? `press “${balanceAskLabel('text')}” further down`
    : `choose Text and press “${balanceAskLabel(null)}” further down`;
}

/** ⭐ board 2811 R3 (item 3): this screen has no phone control, so a refused
 *  number ends with who can correct it. */
const WRONG_NUMBER_TELL_ALEX = 'If the number is wrong, tell Alex.';

/**
 * ⭐ board 2811 M7 (plan M7 step 2, A3 item 1): a FAILED balance text, one
 * sentence per `BalanceTextFailure`:
 *   - `not_sent` (the row is void, no live link): "The balance text failed on Thu
 *     15 Oct." and where to text it again (`textAgainWords`), only when the text
 *     rail is reachable, so it never points at a control that is not there.
 *   - `not_delivered` (the carrier failed it, the link is live): "The balance text
 *     did not arrive (Thu 15 Oct). The link is still live: ring the customer."
 *   - `unfinished` (a link is attached and the send never finished): the
 *     existing stuck instructions, unchanged.
 * ⭐ board 2811 rework part 2c (Captain item 13): when the provider refused the
 * NUMBER (`numberRejected`), the first two say so and ask Helen to check it,
 * rather than to text or ring the same number again; `not_sent` still names the
 * control to text again with once it is checked (follow-up 1). The number cannot
 * be corrected on this screen (no phone control here), so both end "If the
 * number is wrong, tell Alex." (board 2811 R3, item 3).
 */
function balanceTextFailedCopy(
  b: { textFailure?: string | null; failedAt?: string | null; askedAt: string | null; numberRejected?: true },
  textAgain: string | null,
): string {
  const day = dublinDayAndTime(b.failedAt ?? '')?.day ?? null;
  const wrongNumber = b.numberRejected === true;
  if (b.textFailure === 'not_delivered') {
    const lead = day === null ? 'The balance text did not arrive' : `The balance text did not arrive (${day})`;
    return wrongNumber
      ? `${lead}: the mobile number may be wrong. The link is still live: check the number with the customer. ${WRONG_NUMBER_TELL_ALEX}`
      : `${lead}. The link is still live: ring the customer.`;
  }
  if (b.textFailure === 'not_sent') {
    const lead = day === null ? 'The balance text failed' : `The balance text failed on ${day}`;
    if (wrongNumber) {
      const check = `${lead}: the mobile number may be wrong. Check it with the customer`;
      return `${textAgain === null ? `${check}.` : `${check}, then ${textAgain}.`} ${WRONG_NUMBER_TELL_ALEX}`;
    }
    return textAgain === null ? `${lead}.` : `${lead}. To text it again, ${textAgain}.`;
  }
  // ⛔ board 2811 rework (item 6): the FAILURE's instant first. A scheduled row's
  // `askedAt` is the deposit day it was opened, weeks before it fired.
  return `The balance ask on ${dublinDayAtTime(b.failedAt ?? b.askedAt ?? '')} did not finish. Ask Alex to check it.`;
}

/**
 * ⭐ board 2811 rework (item 5): why there is NO AUTOMATIC BALANCE ASK, by the
 * refund state the server's rule read (`automaticAskRefunded`).
 *   - `refunded`: the Q271 words, unchanged (Captain's ruling keeps them).
 *   - `part_refunded`, `failed`, `unconfirmed` (Captain's ruling on the item 5
 *     deviation, A2 item 9): what happened, that no automatic ask goes out, and,
 *     only when the control is on screen (`controlOffered`), to ask herself with
 *     it, so the words never point at a control that is not there. The refund
 *     words are the list chips' ("Part refunded", "Refund failed", "Refund not
 *     confirmed", Bookings.tsx).
 */
function noAutomaticAskWords(
  standing: 'refunded' | 'part_refunded' | 'failed' | 'unconfirmed',
  controlOffered: boolean,
): string {
  if (standing === 'refunded') return 'No automatic balance ask: the deposit was refunded.';
  const what = standing === 'part_refunded'
    ? 'Part of the deposit was refunded'
    : standing === 'failed'
      ? 'A refund on this booking failed'
      : 'A refund on this booking is not confirmed yet';
  /* ⭐ board 2812 fix round (SC-7): no "below" any more. The words are the Balance fact
     row's now, and the control sits above that row when the ask is the next step and
     below it otherwise, so a direction would be false in one of the two. */
  return `${what}, so no automatic balance ask goes out.${controlOffered ? ' Ask for the balance yourself.' : ''}`;
}

/**
 * WHEN THE ASK IS OFFERED (the control's own rule, read by the control and by the
 * Balance fact row's "ask yourself" words, so the two cannot disagree). `closed` joins
 * `none` because a voided ask is exactly the case the server's reader exists to tell
 * apart from "never asked": the balance is still owed and nobody is chasing it.
 * ⚠️ NOT `stuck`: a stuck ask may already have minted a payable link, so offering a
 * second one is how a customer gets two demands for one balance. M3's stuck-text
 * reader and Alex own that case.
 * ⭐ board 2811 M7 (success criterion 2, A3 item 1's cycle-5 note): offered again only
 * after a COMPENSATED failure, whose row is void and whose link was switched off. A
 * failed text that still holds a live link is not.
 */
function balanceAskOffered(balance: NonNullable<ApiBookingDetail['balance']>): boolean {
  return balance.state === 'none'
    || balance.state === 'closed'
    || (balance.state === 'failed' && balance.textFailure === 'not_sent');
}

/**
 * ⭐⭐ board 2812 fix round (Captain item 1, SC-7) - THE BALANCE FACT ROW'S WORDS: what
 * is still to pay, then where the ask stands, in board 2811's own sentences (moved here
 * from `AskForBalance`, which now holds the control only, so each is stated once).
 *
 * · The figure is the server's `askCents` (the agreed total less what was actually
 *   paid, net of succeeded refunds), and only where the state sentence carries none:
 *   an `asked` row says the sum it asked for, a `paid` one has nothing left. Absent on
 *   the wire, no figure is stated (never a client subtraction).
 * · A failed, late or stuck ask also has its `warn` note in the top zone, which says
 *   what to do; the row states only the fact.
 * · Any refund withdraws the automatic ask and its words replace the "We will ..." and
 *   "late" lines (board 2811 rework item 5), exactly as they did inside the control.
 * · `unpaidAtDeparture` is the server's flag (the list's "unpaid" word).
 */
function balanceFactWords(
  balance: NonNullable<ApiBookingDetail['balance']>,
  unpaidAtDeparture: boolean,
  askYourselfOffered: boolean,
): string {
  const { state } = balance;
  const refunded = balance.automaticAskRefunded;
  // ⛔ A TWO-WAY BRANCH, AND THE `else` IS TEXT BY SCHEMA: `balance_invoices_channel_check`
  // is `CHECK (channel IN ('email', 'text'))` (migrations-v42.sql).
  const byEmail = balance.channel === 'email';
  const figure = state === 'asked' || state === 'paid' || balance.askCents === undefined
    ? null
    : `${euro(balance.askCents)} still to pay.`;
  const stateWords =
    state === 'asked'
      ? balance.wholeJob === true
        ? `The balance is on the whole-job invoice made on ${dublinDayAtTime(balance.askedAt ?? '')}.`
        : byEmail
          ? // ⭐ board 2811 M4: the day it was EMAILED (`sendsAt`), not the day the row
            // was opened, which for an automatic ask is weeks earlier.
            `Balance emailed on ${dublinDayAtTime(balance.sendsAt ?? balance.askedAt ?? '')}${askedForWords(balance.askedCents)}.`
          : // ⭐ board 2811 M7 (D-17): the moment it was TEXTED (`sentAt`), and a late
            // one says so with when it was due.
            balanceTextSentCopy(balance.sentAt ?? balance.askedAt ?? '', balance.sentLate === true ? balance.sendsAt ?? null : null, balance.askedCents)
      : state === 'paid' ? 'Balance paid.'
        : state === 'stuck' ? 'The balance ask did not finish.'
          : state === 'failed'
            ? balance.textFailure === 'not_sent' ? 'The balance text failed.'
              : balance.textFailure === 'not_delivered' ? 'The balance text did not arrive.'
                : 'The balance ask did not finish.'
            : refunded !== undefined ? null
              : state === 'scheduled'
                ? `We will ${byEmail ? 'email' : 'text'} the balance link on ${dublinDayAtTime(balance.sendsAt ?? '')}.`
                : state === 'due'
                  ? balance.automaticTextsOff === true
                    ? `The balance text is due and has not gone yet. ${AUTOMATIC_TEXTS_OFF_COPY}`
                    : 'The balance text is due and has not gone yet.'
                  : state === 'late' ? 'The balance text is late.'
                    : state === 'closed' ? 'The last balance ask was cancelled or did not go through.'
                      : 'Not asked for yet.';
  return [
    figure,
    refunded === undefined ? null : noAutomaticAskWords(refunded, askYourselfOffered),
    stateWords,
    unpaidAtDeparture ? 'Not paid before the journey started.' : null,
  ].filter((w): w is string => w !== null).join(' ');
}

/**
 * ⭐⭐ board 2106 M5 — "Ask for the balance". THE CALLER that makes M4's
 * `POST /api/bookings/:id/balance-ask` more than a tested route.
 *
 * ⛔ WHY THIS COMPONENT IS THE DELIVERABLE AND THE ROUTE IS NOT — `MarkTravelled`
 * above states the case and it is the same one: a route with full API-layer
 * coverage and no client caller reads exactly like a working feature, and this
 * repo shipped precisely that in T1011. So the spec that discharges this
 * milestone clicks THIS button.
 *
 * ⛔⛔ ABSENT IS NOT A STATE. `balance === undefined` means the server decided
 * this booking cannot be asked at all (not confirmed, no paid deposit, or
 * account-billed) and renders NOTHING — no control and no sentence. `state:
 * 'none'` means "askable, nobody has asked", which renders the button. Reading
 * the absence as a default would offer Helen a control the server refuses, on
 * the account rail in particular, where a balance ask is a defect by rule.
 *
 * ⛔ THE BUTTON IS `secondary` UNLESS IT IS THE NEXT STEP. DESIGN.md §5.2 allows
 * exactly one gold per screen; a gold ask on every paid booking would tell Helen
 * that asking for money is the next move when it is not. ⭐ board 2812 M3 fix round
 * 3 (F3-3, SC-11): the detail's gold is its NEXT STEP's action, so the ask is gold
 * exactly when the host says it is next (`gold`, from `balanceIsNext`: askable and
 * nobody has asked), and then nothing else on the screen is.
 *
 * ⛔ AND IT IS THE LIBRARY'S `Button`, not a hand-written `<button class="btn">`,
 * even though every other control on this screen is the latter. `Button` carries
 * the library's own stamp (named at the import above, and spelled nowhere in this
 * file for the reason given there), which is what keeps the element OUT of the
 * adoption census's class-level debt; a raw one would add to `CLASS_LEVEL_DEBT`'s
 * `btn` count for this screen and make a new control a small regression in the
 * one figure T1749 M10 exists to drive down.
 *
 * ⚠️ THE `FormField` PICKER DOES **NOT** GET THAT FOR FREE, and the measurement
 * says so: this screen's declared class-level debt goes `5 / 2 / 2` to
 * `5 / 3 / 3`, because a `FormField`'s own LABEL and CONTROL carry no stamp of
 * their own. A library component ADDS class-level debt on that census — board
 * 2089 M3 recorded the identical +1/+1 on the two Quote rows — so "use the
 * library and the count cannot move" is true of `Button` and false of
 * `FormField`. Declared in `test/client/lib/adoption-debt.ts`, not absorbed.
 */
function AskForBalance({
  bookingId,
  balance,
  refundedCents,
  phone,
  email,
  contactPreference,
  onAsked,
  gold = false,
}: {
  bookingId: number;
  /** The server's projection, or `undefined` for "not askable" (see above). */
  balance: ApiBookingDetail['balance'];
  /**
   * ⛔ board 2106 M6 delta 3 — `depositRefundedCents`, normalised to `null`.
   *
   * It is a TOP-LEVEL key on the detail, not part of `balance`, because the cancel
   * control reads the same figure on bookings where `balance` is absent — so it
   * arrives as its own prop rather than through the projection.
   *
   * ⛔ IT IS NOT SUBTRACTED FROM `balance.depositPaidCents` ANYWHERE ON THIS
   * SCREEN. See the sentence that renders it for why netting the displayed figure
   * would re-open the screen-vs-demand divergence M5 rework 3 closed.
   */
  refundedCents: number | null;
  /**
   * `customers.phone_e164`, TRIMMED, with blank read as absent — the TEXT rail's
   * reachability and nothing else. The trim is not tidiness: `validateBalanceAsk`
   * does `(await getCustomerPhoneE164(…))?.trim() ?? ''` and refuses `''`, so a
   * whitespace-only column is ABSENT to the route and would otherwise be present
   * here. See the `email` prop for why both oracles are the route's.
   */
  phone: string | null;
  /**
   * ⛔⛔ THE **ASK** ROUTE'S OWN EMAIL ORACLE — `customers.email`, trimmed, blank
   * read as absent — AND DELIBERATELY **NOT** THE SEND SURFACE'S RESOLVER.
   *
   * `resolveRecipientEmail(customer.email, booking.inquiry_email)` is right for
   * `handleSendQuote`, which composes to whichever of the two it returns. It is
   * WRONG here, because `validateBalanceAsk` reads `identity.email` —
   * `getCustomerBillingIdentity`'s four-column SELECT on `customers` — and never
   * touches `inquiries` at all. A control that asked the SEND route's question
   * would offer a rail the ASK route refuses.
   *
   * ⛔ AND THE DIVERGENCE IS ORDINARY, NOT THEORETICAL: the public inquiry path
   * writes an address onto the INQUIRY and never onto `customers`, so a customer
   * with no phone and a null `customers.email` whose booking carried an inquiry
   * address rendered the single-rail button 'Ask for the balance by email', 409'd
   * `no_email` on every press, and was then told "Ask by text instead" — a remedy
   * that is also impossible. It now renders no control, which is this component's
   * existing and correct answer for "neither contact on file".
   *
   * ⚠️ SO THE TWO BLOCKS ON THIS SCREEN **MAY** DISAGREE ABOUT AN ADDRESS, and
   * that is the honest rendering rather than a defect: they are asking different
   * questions of different routes. What must never diverge is this prop and
   * `validateBalanceAsk`.
   */
  email: string | null;
  /** `booking.contact_preference` — what the CUSTOMER asked for, never what this
   *  kiosk can do. Null on most rows and on every walk-in. */
  contactPreference: string | null;
  /** Re-read the booking so the state below is the server's. ⛔ Never patched in
   *  locally — that would claim a write the server may not have made, which is
   *  `MarkTravelled`'s rule and matters more here: the thing claimed is a demand
   *  for money sent to a customer. */
  onAsked: () => void;
  /** ⭐ board 2812 M3 fix round 3 (F3-3, SC-11): the ask is the screen's ONE gold when it
   *  is the detail's next step (`balanceIsNext`), and secondary otherwise. */
  gold?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<BalanceAskOutcome | null>(null);
  /** Helen's own pick, or null while the default below stands. PER-ASK state,
   *  deliberately never written back to the booking: `contact_preference` is what
   *  the customer asked for and must stay the durable record of that. */
  const [chosen, setChosen] = useState<BalanceAskChannel | null>(null);
  /** A REF, not state, for `MarkTravelled`'s reason: `setState` does not
   *  re-render before a second tap in the same task, so only a ref is read back
   *  synchronously. ⛔ IT MATTERS MORE HERE THAN THERE. Marking a run travelled
   *  twice is idempotent server-side; asking twice would post a second ask, and
   *  the route's own defence is a 409 `already_asked` AFTER the first row exists
   *  — a race between two in-flight posts is not covered by it. */
  const inFlight = useRef(false);
  const bannerRef = useRef<HTMLDivElement | null>(null);

  // Banner.tsx: role="status" tells a screen-reader user what happened and does
  // nothing for a keyboard user's caret, and on success the control that caused
  // it is gone by then. Move the caret onto the announcement.
  useEffect(() => {
    if (outcome !== null) bannerRef.current?.focus();
  }, [outcome]);

  /**
   * Whose contact detail this rail would use — the ONLY reachability test this
   * component makes.
   *
   * ⚠️ AN SMS CONFIGURATION ORACLE **DOES** EXIST CLIENT-SIDE and is not consulted
   * here, which is a choice rather than an absence — an earlier draft of this
   * comment claimed the kiosk could not know, and that was false for one of the
   * three codes. `isChannelConfigured(registry, 'sms')` reads /api/options'
   * `configuredChannels.sms`, which the server derives from the SAME `smsClient`
   * the ask route's `sms_unconfigured` 503 tests; the host screen imports it and
   * passes it to `SendQuote`.
   *
   * ⛔ IT IS NOT THREADED IN, FOR TWO REASONS THAT BOTH SURVIVE THE CORRECTION.
   * It would narrow exactly ONE of the three deployment refusals this control can
   * collect: `stripe_unconfigured` (every ask needs Stripe) and
   * `balance_invoicing_disabled` (the killswitch is an env read on the server)
   * have no client oracle at all, so the server stays the authority whatever this
   * component does. And the accessor is deliberately FAIL-OPEN — `!== false`, so a
   * dropped or renamed key reads as configured — which makes it sound only as a
   * negative, while what a picker needs is a positive. The server answers all
   * three with `stripe_unconfigured` / `sms_unconfigured` /
   * `balance_invoicing_disabled`, which `BALANCE_ASK_COPY` has operator copy for.
   */
  const targetFor = (c: BalanceAskChannel): string | null => (c === 'email' ? email : phone);
  const reachable = BALANCE_CHANNEL_OPTIONS.map((o) => o.value).filter((c) => targetFor(c) !== null);
  /**
   * THE DEFAULT SELECTION, adapted from `SendQuote.tsx`'s rule rather than copied
   * from its code — and the difference is stated because the plan said "as
   * SendQuote does it", which is not directly possible. That picker is a
   * hand-built ARIA radiogroup whose default also consults `channelConfigured`;
   * this is a `<select>` with no configuration oracle. What survives is the RULE:
   *   1. the customer's stated preference, if that rail is reachable;
   *   2. otherwise the first reachable rail.
   *
   * ⚠️ A 'call', 'whatsapp' OR ABSENT PREFERENCE FALLS TO TEXT AND CLAIMS
   * NOTHING. `contact_preference`'s vocabulary is sms / call / email / whatsapp
   * (shared/send-schema.ts), so `!== 'email'` sweeps three values into `text`
   * exactly as `SendQuote` does. It carries no statement about what the customer
   * asked for, because it lands on the same answer the reachable-first rule
   * gives: the option order above puts text first. ⛔ Which is also why this
   * screen renders no "they asked for X" hint the way SendQuote does — there is
   * no branch here where the default outranks a stated wish silently.
   */
  const preferred: BalanceAskChannel = contactPreference === 'email' ? 'email' : 'text';
  const channel: BalanceAskChannel =
    chosen ?? (targetFor(preferred) !== null ? preferred : reachable[0] ?? preferred);

  const run = async () => {
    if (inFlight.current || busy) return;
    inFlight.current = true;
    setBusy(true);
    try {
      const res = await askForBalanceRequest(bookingId, channel);
      setOutcome(res);
      // ⛔ A RE-READ, NEVER A PATCH: on success, and after a failure that
      // CHANGED the ask on the server (`reread`: the `text_not_sent` family,
      // which voids the row or leaves it a draft with a live link). Board 2811
      // M8 (M7 security review, carried): without it a link left live kept the
      // stale ask button and hid the "did not finish, ask Alex" note until a
      // manual reload. The 409 refusals still change nothing, so they skip it.
      if (res.ok || res.reread === true) onAsked();
    } finally {
      setBusy(false);
      inFlight.current = false;
    }
  };

  if (balance === undefined) return null;
  /**
   * ⛔⛔ THE TWO MONEY FIGURES ARE THE **SERVER'S**, OFF THIS SAME OBJECT, AND THE
   * SCREEN DERIVES NEITHER OF THEM (M5 rework 3 — output-gate BLOCK).
   *
   * They used to arrive as props read off `detail.payment?.amount_cents` and
   * `detail.latest_quote_cents`, and both oracles were wrong in the same
   * direction: `detail.payment` is the NEWEST payment row of any status, so on a
   * booking whose paid deposit is superseded by an unpaid link the sentence
   * stated an amount nobody had paid; and `latest_quote_cents` moves on a
   * re-price, so after one the two halves came from two different quotes. The
   * route now sends the ONE `deposit_paid` row's amount and THAT deposit's own
   * quote total, derived by the same reader `validateBalanceAsk` uses.
   *
   * ⛔ `?? null` RATHER THAN A DIRECT READ: both are optional on the wire type
   * (every hand-built fixture predates them), and `undefined` must reach the
   * silence rule below as "no figure", never as a rendered `€undefined`.
   */
  const paidCents = balance.depositPaidCents ?? null;
  const totalCents = balance.agreedTotalCents ?? null;
  /** WHEN THE BUTTON IS OFFERED: `balanceAskOffered`, the one rule the Balance fact
   *  row's "ask yourself" words read too. */
  const offerAsk = balanceAskOffered(balance);
  /** After a SUCCESSFUL ask the banner below says what happened; the ask's own
   *  sum and picker go. A REFUSAL keeps everything on screen: nothing was asked,
   *  and she may want the other rail. */
  const asked = outcome?.ok === true;
  const single = reachable.length === 1 ? reachable[0]! : null;

  return (
    <>
      {/* ⭐⭐ board 2812 fix round (Captain item 1, SC-7): WHERE THE ASK STANDS (2811's
          scheduled, due, asked, paid, closed and refund lines) IS THE BALANCE FACT ROW
          NOW, in the Money column's facts (`balanceFactWords`), so it is stated once.
          This block is the control: what is on record, the picker, the button, the sum
          it asks for, where it goes, and the outcome. */}
      {!asked && offerAsk && reachable.length > 0 && (
        <>
          {/* ⭐⭐ ux-review R2 — WHAT IS ON RECORD, BECAUSE THE SCREEN USED TO SEND
              A DEMAND FOR MONEY WITH NO SUM ANYWHERE ON IT. The word "balance"
              appeared exactly once, inside the button's own label.

              ⛔⛔ AND IT IS NOT THE BALANCE. `total − deposit` is NOT derived
              here, deliberately: `validateBalanceAsk` refuses
              `deposit_total_mismatch` precisely when the deposit on record is not
              that quote's deposit (a re-price after the deposit landed is
              ordinary supported use), so in exactly the case that guard exists
              for, a client-side subtraction would print a WRONG figure on a money
              demand — worse than printing none.

              ⚠️ NEITHER FIGURE IS THE ONE THE **DEPOSIT** OR THE **QUOTE** FACT
              ROW RENDERS, AND AN EARLIER DRAFT OF THIS COMMENT SAID
              THEY WERE. Those two read `payment` (the newest row of any status)
              and `latest_quote_cents` (the newest quote); these come off
              `balance`, which carries the PAID deposit and THAT deposit's own
              quote — see where they are read, above. On an ordinary booking the
              pairs agree, which is exactly why the disagreement was invisible.

              ⚠️ SILENT WHEN EITHER IS MISSING. A booking with no quote total is a
              booking the ask itself refuses (`no_quote`); half a sentence about
              money is worse than none. */}
          {paidCents !== null && totalCents !== null && (
            <p class="send-status">
              {`Paid so far ${euro(paidCents)} of ${euro(totalCents)} agreed.`}
            </p>
          )}
          {/* ⭐⭐ board 2106 M6 delta 3 — THE REFUND, SAID AS ITS OWN SENTENCE.

              ⭐ board 2811 R3 (Captain's money ruling): the button now demands the
              agreed total less what was ACTUALLY paid, net of SUCCEEDED refunds
              (`balanceCents`), and the sum is stated beside the button ("Asks for
              ..."), from the server's own derivation (`askCents`). The sentence
              above stays GROSS (what was paid), and this one says what went back
              (`depositRefundedCents`, which also counts a refund still on its way,
              one that does not lower the sum asked until it succeeds). Nothing
              here re-derives the demand: the figure is the server's.

              ⚠️ ZERO IS NOT RENDERED. Every booking has a `depositRefundedCents`
              of 0, and "€0.00 has been refunded" on every unrefunded booking is
              noise that trains the eye to skip the line that matters. */}
          {refundedCents !== null && refundedCents > 0 && (
            <p class="send-status">
              {`${euro(refundedCents)} of that has been refunded to the customer, or is on its way back.`}
            </p>
          )}
          {/* Two rails on file: she picks. Exactly one: no picker at all, and the
              button names the rail instead — a select with one option is a
              control that asks a question with one answer, which is the kind of
              button the operators abandoned a previous tool over.

              ⛔⛔ ux-review R1 — THE FIELD IS **OUTSIDE** THE `.action-row` NOW, and
              the move is the fix rather than a tidy-up. `.action-row` is
              `display: flex` with no `align-items`, so the default `stretch` grew
              the button to the height of the label+select stack beside it:
              measured live at 222x102px against `Re-open in calculator`'s 238x48
              with the IDENTICAL `btn btn--secondary` class. This component's own
              docblock says it must not read as the next move on every paid
              booking, and at 2.1x the height of its siblings it was the loudest
              thing in the column.
              ⛔ FIXED HERE, NEVER IN `app.css`. `.action-row` is shared by every
              screen in this tool, so an `align-items` there would move the
              control-budget and conformance measurements everywhere for one
              screen's defect. `CustomerEmailCorrection` on this same screen
              already separates its field from its action exactly this way. */}
          {single === null && (
            <FormField
              id="balance-ask-channel"
              label="Send by"
              kind="select"
              options={BALANCE_CHANNEL_OPTIONS}
              value={channel}
              onInput={(v) => { setChosen(v === 'email' ? 'email' : 'text'); }}
            />
          )}
          <div class="action-row">
            <Button variant={gold ? 'primary' : 'secondary'} disabled={busy} onClick={() => void run()}>
              {balanceAskLabel(single)}
            </Button>
          </div>
          {/* ⭐ board 2811 R3 (item 1): THE SUM THIS BUTTON ASKS FOR, the server's
              `askCents` (the validator's derivation). Absent when the ask would
              refuse on the money, and then no figure is stated. Helen's screen
              only: no customer surface shows a price. */}
          {balance.askCents !== undefined && (
            <p class="send-status">{`Asks for ${euro(balance.askCents)}.`}</p>
          )}
          {/* ⭐ ux-review R3 — WHERE IT GOES. Bound to the SELECTED channel, so
              changing the picker changes the address under it; the value is what
              `targetFor` gates reachability on, which is why this can never name
              a rail the button would not use.

              ⛔⛔ IT MIRRORS THE SEND SURFACE'S SHAPE BUT **NOT** ITS SENTENCE, and
              the difference is load-bearing rather than stylistic. SendQuote's
              line is `Goes to the customer at X. Not the team.`, and its own
              docblock records that a second paragraph matching it "breaks the e2e
              net's unqualified getByText on this sentence" —
              `e2e/tests/green/send-quote-ui.spec.ts:28` is
              `getByText(/Goes to the customer at/)` with no `.first()`, and
              `send-ui.spec.tsx` asserts `getAllByText(...)` has length 1. Both
              blocks render on a confirmed, deposit-paid booking, so reusing the
              words would make that locator ambiguous on the live screen.

              ⚠️ THE RAW STORED VALUE, not `formatPhoneWithCountry`: this states
              what the ask will actually address, and the send surface one block
              up states its own the same way. */}
          {targetFor(channel) !== null && (
            <p class="recipient-copy">{`Goes to ${targetFor(channel)!}.`}</p>
          )}
        </>
      )}
      {outcome?.ok === true && (
        <Banner kind="ok" elementRef={bannerRef} tabIndex={-1}>
          {outcome.channel === 'email'
            ? 'Balance asked by email. Stripe sends it within the hour.'
            : 'Balance asked by text.'}
        </Banner>
      )}
      {/* SendQuote.tsx's mapping, not a second one: 'not-configured' is a
          deliberate switch-off and reads as information; everything else is
          something wrong and interrupts. The SENTENCE is the server's code
          translated in actions.ts, never its prose (SEC-5). */}
      {outcome?.ok === false && (
        <Banner
          kind={outcome.kind === 'not-configured' ? 'info' : 'error'}
          elementRef={bannerRef}
          tabIndex={-1}
        >
          {outcome.message}
        </Banner>
      )}
    </>
  );
}

/** board 2249 M5 — what the document will be, in one sentence per action. */
const JOB_INVOICE_ACTION_WORDS: Readonly<Record<ApiJobInvoicePreview['action'], string>> = {
  new: 'This makes a new invoice for this job.',
  // board 2281 (UX7): never "already sent". A draft, a no-email document and a text
  // link are replaced too; the warning below names which one and when it was made.
  replace: 'This replaces the invoice for this job.',
  receipt: 'This is a paid receipt; nothing will be charged.',
};

/**
 * board 2281 (UX7) — what a replace cancels, named as History names it (our row id,
 * `jobInvoiceHistoryWords`) and dated by the Dublin civil day it was made. "Made on"
 * is true of a draft, a no-email document and a text link alike; "sent" is not.
 * `replaces` absent (an older server) or a `madeOn` that does not parse drops the
 * part it would have said, never the warning.
 */
function jobInvoiceReplaceWarning(replaces: ApiJobInvoicePreview['replaces']): string {
  if (replaces == null) return 'It cancels the current invoice for this job, so that one can no longer be paid.';
  const id = String(replaces.documentId);
  const doc =
    replaces.kind === 'job'
      ? `invoice #${id}`
      : replaces.channel === 'text'
        ? `the balance link #${id}`
        : `the balance invoice #${id}`;
  const day = typeof replaces.madeOn === 'string' ? civilDateLabel(replaces.madeOn, null) : '';
  return `It cancels ${doc}${day === '' ? '' : `, made on ${day}`}, so that one can no longer be paid.`;
}

/**
 * board 2281 (security Low 1) — the replaced document went to an email Helen TYPED
 * (a funder's, say), and this one is going to the booking email: the form looks
 * filled in for the funder, so without this line the funder's replacement would
 * reach the traveller. Said only as a fact; the address itself is never echoed (H12).
 */
const JOB_INVOICE_TYPED_RECIPIENT_COPY =
  'That one went to an email typed in by hand: type it again above if this one should go there too.';
/** The same fact on a RECEIPT (security round 2, Low 3): nothing is cancelled, but a
 *  job gets one paid receipt, so one sent to the booking email by mistake is Alex's
 *  to re-make. */
const JOB_INVOICE_TYPED_RECIPIENT_RECEIPT_COPY =
  'The invoice for this job went to an email typed in by hand: type it again above if this receipt should go there too.';

/**
 * board 2249 M5 — who it goes to, from the preview's SUMMARY (the route never
 * sends the address itself). ⛔ NOT SendQuote's "Goes to the customer at" sentence:
 * both blocks render on one paid booking, and `send-quote-ui.spec.ts` reads that
 * sentence with an unqualified `getByText`.
 * board 2281 (UX8): these are now the FALLBACKS, for an address this screen does
 * not hold; `jobInvoiceRecipientSentence` names the address when it does.
 */
const JOB_INVOICE_RECIPIENT_WORDS: Readonly<Record<ApiJobInvoicePreview['recipient']['summary'], string>> = {
  'booking email': 'Stripe emails it to the email address on this booking.',
  'the address you entered': 'Stripe emails it to the address you typed above.',
  'nobody, PDF only': 'Nobody is emailed. You download the PDF and pass it on.',
};

/**
 * board 2281 (UX8) — the recipient sentence WITH the address, filled in client-side
 * from what the screen already holds: the booking's own email (the column
 * `resolveRecipient` defaults from, trimmed the same way, never the inquiry
 * fallback) or the email in the body this preview was made from. The summary still
 * decides WHICH; an empty address falls back to the sentence without one.
 */
function jobInvoiceRecipientSentence(
  summary: ApiJobInvoicePreview['recipient']['summary'],
  bookingEmail: string,
  typedEmail: string,
): string {
  if (summary === 'booking email' && bookingEmail !== '') return `Stripe emails it to ${bookingEmail}, the email on this booking.`;
  if (summary === 'the address you entered' && typedEmail !== '') return `Stripe emails it to ${typedEmail}, the email you typed above.`;
  return JOB_INVOICE_RECIPIENT_WORDS[summary];
}

/** Plan A3, in the client's own words, keyed off the preview's boolean (the
 *  route's `note` is server prose and is not rendered). */
const JOB_INVOICE_NOTHING_PAID_COPY =
  'Nothing is recorded as paid on this job yet. If the customer paid cash or by bank transfer, do not send it.';

/** The PDF link's words, one spelling for the outcome banner and the read-back. */
const JOB_INVOICE_PDF_LINK = 'Download the invoice (PDF)';

/** A document line's figure: the deposit line is negative on the wire. */
function jobInvoiceLineFigure(cents: number): string {
  return cents < 0 ? `minus ${euro(-cents)}` : euro(cents);
}

/** What the preview says, before anything is made. */
function JobInvoicePreviewBlock({
  preview,
  anchorRef,
  bookingEmail,
  typedEmail,
  lastRecipientWasTyped,
}: {
  preview: ApiJobInvoicePreview;
  /** The first sentence takes the caret when the preview lands (M5 gate, ux WARN),
   *  so a screen reader starts at "what this does" and reads on through the figures. */
  anchorRef: Ref<HTMLParagraphElement>;
  /** board 2281 (UX8): the booking's email, trimmed, or ''. */
  bookingEmail: string;
  /** board 2281 (UX8): the email in the body this preview was made from, or ''. */
  typedEmail: string;
  /** board 2281 (security Low 1): the live document went to an email typed by hand. */
  lastRecipientWasTyped: boolean;
}) {
  const a = preview.billTo.address;
  const address =
    a === null
      ? ''
      : [a.line1, a.line2, a.town, a.county, a.eircode].filter((p): p is string => p !== null && p !== '').join(', ');
  const facts = [
    { term: 'Billed to', value: address === '' ? preview.billTo.name : `${preview.billTo.name}, ${address}` },
    ...preview.lines.map((l) => ({ term: l.description, value: jobInvoiceLineFigure(l.amountCents) })),
    { term: preview.action === 'receipt' ? 'Paid' : 'To pay', value: euro(preview.totalCents) },
  ];
  return (
    <>
      <p class="send-status" ref={anchorRef} tabIndex={-1}>
        {JOB_INVOICE_ACTION_WORDS[preview.action]}
      </p>
      {preview.action === 'replace' && (
        <Banner kind="warn">
          {jobInvoiceReplaceWarning(preview.replaces)}
          {lastRecipientWasTyped && preview.recipient.summary !== 'the address you entered'
            ? ` ${JOB_INVOICE_TYPED_RECIPIENT_COPY}`
            : ''}
        </Banner>
      )}
      {preview.action === 'receipt' && lastRecipientWasTyped && preview.recipient.summary !== 'the address you entered' && (
        <Banner kind="warn">{JOB_INVOICE_TYPED_RECIPIENT_RECEIPT_COPY}</Banner>
      )}
      <FactsTable facts={facts} />
      <p class="send-status">{jobInvoiceRecipientSentence(preview.recipient.summary, bookingEmail, typedEmail)}</p>
      {preview.nothingRecordedAsPaid && <Banner kind="warn">{JOB_INVOICE_NOTHING_PAID_COPY}</Banner>}
    </>
  );
}

/** The outcome banner's sentence and tone. `sent: false` with a recipient means
 *  the document was made but Stripe did not email it (the core's send-failure
 *  branch), which is a warning, not a success. */
function jobInvoiceIssuedWords(i: ApiIssuedJobInvoice): { kind: 'ok' | 'info' | 'warn'; text: string } {
  const doc = i.kind === 'receipt' ? 'The paid receipt' : i.action === 'replace' ? 'The new invoice' : 'The invoice';
  const replaced = i.action === 'replace' ? ' The old one is cancelled.' : '';
  if (i.recipient === 'nobody, PDF only') {
    return { kind: 'info', text: `${doc} was made.${replaced} Nothing was sent. Download the PDF and pass it on.` };
  }
  if (!i.sent) {
    return { kind: 'warn', text: `${doc} was made, but the email did not go out.${replaced} Download the PDF and pass it on.` };
  }
  const to = i.recipient === 'the address you entered' ? 'to the address you typed' : 'to the email on this booking';
  return { kind: 'ok', text: `${doc} was made and emailed ${to}.${replaced}` };
}

/**
 * ⭐⭐ board 2249 M5 — "INVOICE THIS JOB": one action, three fields, a preview
 * before anything is made, and the outcome inline with the PDF link.
 *
 * ⛔ THE SERVER DECIDES EVERYTHING. `offered` gates the button; the preview route
 * derives every figure, the recipient and the action; the issue route derives them
 * again and never trusts the preview. This component posts what Helen typed and
 * renders what came back, in `actions.ts`'s words.
 *
 * ⭐ PRESSING "Invoice this job" RUNS THE PREVIEW STRAIGHT AWAY with the default
 * name (plan M5 step 2, carried from M3): a job that already has a paid receipt,
 * or any other booking-level refusal, is said BEFORE Helen types anything, and the
 * form does not open on a job that cannot be invoiced. On an ordinary job the
 * preview is already showing, so the common case is two presses.
 * ⛔ EDITING ANY FIELD CLEARS THE PREVIEW, so "Yes, send it" is never offered
 * under figures for values that are no longer in the boxes; "Check it" comes back.
 *
 * ⛔ THE ADDRESS IS NEVER PREFILLED FROM THE JOURNEY (M1 security carry): not from
 * the pickup, not from the drop-off. The notice's "never where anyone was collected
 * or taken" claim is only true if no value can arrive here that way. ⭐ board 2281:
 * the ONE source is `jobInvoice.defaults`, which carries the live job document's
 * own bill-to (name and address, both typed by Helen) when a replace would cancel
 * it, else the traveller's name and no address. Never an email: the box for one
 * always opens empty.
 *
 * ⛔ THE INLINE TWO-STEP REVEAL, not a modal (`AccountInvoices.tsx`'s issue card),
 * and every control is the library's: `Button`, `FormField`, `FactsTable`, `Notice`.
 * The button is `secondary`, never `primary`: this screen's one gold is its next
 * step's action (board 2812 M3 fix round 3, F3-3; see `AskForBalance`).
 */
/** The three boxes' control ids, one place: the FormFields read them, and so does
 *  the focus move onto a box that has an error. */
const JOB_INVOICE_FIELD_IDS: Readonly<Record<JobInvoiceFormField, string>> = {
  billTo: 'job-invoice-bill-to',
  address: 'job-invoice-address',
  email: 'job-invoice-email',
};

function InvoiceThisJob({
  bookingId,
  jobInvoice,
  bookingEmail,
  onIssued,
}: {
  bookingId: number;
  /** The server's projection; absent (a hand-built fixture) renders nothing. */
  jobInvoice: ApiBookingDetail['jobInvoice'];
  /** board 2281 (UX8): the booking's own email (`customer.email`, trimmed), or ''.
   *  Named in the recipient sentence only; never posted, never prefilled. */
  bookingEmail: string;
  /** Re-read the booking after a document is made (never patched locally). */
  onIssued: () => void;
}) {
  const [open, setOpen] = useState(false);
  /** board 2281 (UX6): Cancel re-opens the replace disclosure it came from, so the
   *  button inside can take the caret back. Reset by the next press. */
  const [reopenDisclosure, setReopenDisclosure] = useState(false);
  const [billTo, setBillTo] = useState('');
  const [address, setAddress] = useState('');
  const [email, setEmail] = useState('');
  /**
   * ⛔ THE PREVIEW AND THE EXACT BODY IT WAS MADE FROM, TOGETHER (M5 gate, security
   * Advisory 1). "Yes, send it" posts `body`, never the boxes as they are now, so the
   * document issued is the one Helen was shown.
   */
  const [previewed, setPreviewed] = useState<{ preview: ApiJobInvoicePreview; body: JobInvoiceBody } | null>(null);
  /** ONE error at a time (the server names one field per refusal), cleared only by an
   *  edit to THAT box, so fixing the name does not hide an address still wrong. */
  const [fieldError, setFieldError] = useState<{ field: JobInvoiceFormField; message: string } | null>(null);
  const [refusal, setRefusal] = useState<JobInvoiceRefusal | null>(null);
  const [issued, setIssued] = useState<ApiIssuedJobInvoice | null>(null);
  const [busy, setBusy] = useState(false);
  /** A REF, for `AskForBalance`'s reason: a second tap in the same task must see
   *  the first, and `setState` has not re-rendered by then. It matters most on the
   *  issue, where two posts would be two Stripe documents. */
  const inFlight = useRef(false);
  /**
   * ⛔ BUMPED BY EVERY EDIT (M5 gate, security Advisory 1). The boxes stay editable
   * while a preview is in flight, so a preview that comes back after an edit
   * describes values no longer in the boxes; it is DROPPED, not shown.
   */
  const editCount = useRef(0);
  const bannerRef = useRef<HTMLDivElement | null>(null);
  const previewRef = useRef<HTMLParagraphElement | null>(null);
  /** board 2281 (UX5): the preview AND its action row, scrolled into view together. */
  const previewAreaRef = useRef<HTMLDivElement | null>(null);
  /** board 2281 (UX6): the "Invoice this job" button, and whether Cancel owes it the caret. */
  const openerRef = useRef<HTMLButtonElement | null>(null);
  const returnFocus = useRef(false);

  // WHERE THE CARET GOES (M5 gate, ux WARN): the banner on an outcome or a refusal
  // (on success the form it came from is gone); the preview's first sentence when a
  // preview lands, so a screen reader reads on from what will happen; the box that
  // is wrong when one is.
  useEffect(() => {
    if (issued !== null || refusal !== null) bannerRef.current?.focus();
  }, [issued, refusal]);
  useEffect(() => {
    if (previewed === null) return;
    // ⭐ board 2281 (UX5) — FOCUS WITHOUT ITS SCROLL, THEN THE WHOLE AREA INTO VIEW.
    // ⚠️ A DEPARTURE FROM `PriceList.tsx`'s "focus, not `scrollIntoView`" ruling, and
    // only half of one: focus still moves, which is the screen-reader half that
    // ruling protects. Focus's own scroll stops at the focused line, and measured
    // live it brought the first sentence to the viewport's bottom edge with the
    // figures, the warning and "Yes, send it" below the fold. `nearest` scrolls only
    // as far as the preview and its buttons need.
    // FEATURE-DETECTED as `PortalRequests.tsx` does: jsdom has no `scrollIntoView`,
    // and the screen must not depend on a scroll for its correctness.
    previewRef.current?.focus({ preventScroll: true });
    const area = previewAreaRef.current;
    if (area !== null && typeof area.scrollIntoView === 'function') area.scrollIntoView({ block: 'nearest' });
  }, [previewed]);
  useEffect(() => {
    // board 2281 (UX6): Cancel removes the form and the button that had the caret;
    // the caret goes back to the opener rather than to the page's top.
    if (!open && returnFocus.current) {
      returnFocus.current = false;
      openerRef.current?.focus();
    }
  }, [open]);
  useEffect(() => {
    // simplified: focus by the control's id, because `FormField` hands `elementRef`
    // to an input but not to its textarea; upgrade trigger: the library passes it on.
    if (fieldError !== null) document.getElementById(JOB_INVOICE_FIELD_IDS[fieldError.field])?.focus();
  }, [fieldError]);

  const guarded = async (work: () => Promise<void>): Promise<void> => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    try {
      await work();
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  /** Preview what the boxes hold. `onOpen`: the first check, with the defaults. */
  const check = (name: string, addr: string, mail: string, onOpen: boolean) =>
    guarded(async () => {
      setRefusal(null);
      setFieldError(null);
      setIssued(null);
      const built = jobInvoiceBody(name, addr, mail);
      if (!built.ok) {
        setFieldError({ field: built.field, message: built.message });
        return;
      }
      const asked = editCount.current;
      const res = await previewJobInvoiceRequest(bookingId, built.body);
      // ⛔ An edit landed while this was in flight: the answer is about old values.
      if (editCount.current !== asked) return;
      if (res.ok) {
        setPreviewed({ preview: res.preview, body: built.body });
        setOpen(true);
        return;
      }
      if (res.field !== null) {
        // A box is wrong. On the first check that is the DEFAULT name, which Helen
        // did not type, so the form opens without scolding her for it.
        if (!onOpen) setFieldError({ field: res.field, message: res.message });
        setOpen(true);
        return;
      }
      // ⛔ A refusal about the BOOKING on the first check: the form stays shut.
      if (onOpen && res.code !== null) {
        setRefusal(res);
        return;
      }
      setOpen(true);
      setRefusal(res);
    });

  const start = () => {
    // board 2281: name AND address from `defaults` (the live job document's own
    // bill-to on a replace, else the traveller's name and no address), never an
    // email. The first preview is made from EXACTLY what the boxes show, so the body
    // "Yes, send it" posts carries the prefilled address too.
    const name = jobInvoice?.defaults.billToName ?? '';
    const addr = jobInvoice?.defaults.billToAddress ?? '';
    editCount.current += 1;
    setReopenDisclosure(false);
    setBillTo(name);
    setAddress(addr);
    setEmail('');
    setPreviewed(null);
    void check(name, addr, '', true);
  };

  /** An edit withdraws the preview and any refusal (both are about the values that
   *  were in the boxes), and this box's own error; another box's error stays. */
  const edit = (field: JobInvoiceFormField, set: (v: string) => void) => (v: string) => {
    editCount.current += 1;
    set(v);
    setPreviewed(null);
    setRefusal(null);
    setFieldError((e) => (e !== null && e.field === field ? null : e));
  };

  const close = () => {
    setOpen(false);
    setPreviewed(null);
    setFieldError(null);
  };

  const send = () =>
    guarded(async () => {
      if (previewed === null) return;
      setRefusal(null);
      const res = await issueJobInvoiceRequest(bookingId, previewed.body);
      if (res.ok) {
        setIssued(res.issued);
        close();
        onIssued();
        return;
      }
      // ⛔ The preview is withdrawn on every refusal but one: whatever changed may
      // have changed the figures. `deposit_link_live` changed nothing on the
      // document (an unpaid link appeared, which the next press switches off), and
      // its sentence asks her to press "Yes, send it" again.
      // ⛔ Rework 1 B4: unless this press already cancelled the old invoice. Then the
      // preview's "It cancels invoice #N" warning is no longer true, and the
      // booking is re-read so the rest of the screen shows the old one cancelled.
      if (res.code !== 'deposit_link_live' || res.oldInvoiceCancelled) setPreviewed(null);
      if (res.oldInvoiceCancelled) onIssued();
      if (res.field !== null) setFieldError({ field: res.field, message: res.message });
      else setRefusal(res);
    });

  // ⛔ Rework 1 B1: switched off, the action is dark, the read-back included (its
  // PDF link would only be refused `not_switched_on`).
  if (jobInvoice === undefined || jobInvoice.switchedOn !== true) return null;
  const { offered, lastDocument } = jobInvoice;
  /** The newest document, read back with its PDF, so a PDF made earlier is not
   *  lost when Helen leaves the screen. Hidden while this mount's own outcome shows
   *  (one link, not two) and while the form is open. A draft or a void document
   *  has no PDF to offer (the route refuses a void one). */
  const showLast =
    issued === null && !open && lastDocument !== null && (lastDocument.status === 'open' || lastDocument.status === 'paid');
  /** board 2281: why the action is missing, in one line, for the codes whose remedy
   *  is Alex (`JOB_INVOICE_STANDING_COPY`); null for every other code, and absent. */
  const standingLine = offered ? null : jobInvoiceStandingCopy(jobInvoice.standing);
  // Nothing to show: no separator either (an orphan rule would divide nothing).
  if (!showLast && !offered && standingLine === null && !open && issued === null && refusal === null) return null;
  const errorFor = (f: JobInvoiceFormField): string | undefined =>
    fieldError?.field === f ? fieldError.message : undefined;
  const words = issued === null ? null : jobInvoiceIssuedWords(issued);

  return (
    <>
      {/* ⭐ M5 gate (ux WARN) — THE HOUSE SEPARATOR, as before Progress and History:
          measured live, the balance ask's "Goes to …" line sat directly on top of
          this block and the two read as one flow. */}
      <hr class="section-rule" />
      {/* ⭐ board 2281 — ONE COLUMN WITH A HEADING, as Progress and History have. ⛔ The
          heading never says "Booking": e2e's getByRole('heading', { name: 'Booking' })
          matches by substring. */}
      <div class="job-invoice">
        <h2 class="field-heading">Invoice for the whole job</h2>
        <p class="send-status">The full fare with any deposit taken off, made out to whoever is paying.</p>
        {standingLine !== null && <p class="send-status">{standingLine}</p>}
        {showLast && (
          <p class="send-status">
            {/* ⛔ DUBLIN, NOT THE BROWSER'S ZONE (CLAUDE.md §2): `shortDateTime` pins
                Europe/Dublin; `shortDate` would read the host's zone. */}
            {`${lastDocument.kind === 'receipt' ? 'A paid receipt' : 'An invoice'} for this job was made on ${shortDateTime(lastDocument.issuedAt)}. `}
            <a href={jobInvoicePdfHref(bookingId, Number(lastDocument.rowId))} download>
              {JOB_INVOICE_PDF_LINK}
            </a>
          </p>
        )}
        {/* ⛔ Rework 2 (delta 6): ONCE A LIVE INVOICE EXISTS, REPLACING IT IS NOT THE
            OBVIOUS NEXT MOVE. The press would cancel it, so the action sits behind a
            closed disclosure (the house pattern for a deliberate, rarer action, as
            "Cancel this run"), and the preview then says what is cancelled. */}
        {offered && !open && jobInvoice.replaces === true && (
          <details open={reopenDisclosure}>
            <summary class="capture-hint">Replace the invoice for this job</summary>
            <div class="action-row">
              <Button variant="secondary" disabled={busy} onClick={start} elementRef={openerRef}>
                Invoice this job
              </Button>
            </div>
          </details>
        )}
        {offered && !open && jobInvoice.replaces !== true && (
          <div class="action-row">
            <Button variant="secondary" disabled={busy} onClick={start} elementRef={openerRef}>
              Invoice this job
            </Button>
          </div>
        )}
        {open && (
          <>
            <FormField
              id={JOB_INVOICE_FIELD_IDS.billTo}
              label="Bill to"
              autoComplete="off"
              value={billTo}
              onInput={edit('billTo', setBillTo)}
              error={errorFor('billTo')}
            />
            <FormField
              id={JOB_INVOICE_FIELD_IDS.address}
              label="Address (optional)"
              kind="textarea"
              rows={3}
              value={address}
              onInput={edit('address', setAddress)}
              hint="One part per line: street, area, town, county, Eircode."
              error={errorFor('address')}
            />
            <FormField
              id={JOB_INVOICE_FIELD_IDS.email}
              label="Send to a different email (optional)"
              type="email"
              autoComplete="off"
              value={email}
              onInput={edit('email', setEmail)}
              hint={
                // ⛔ Rework 2 (delta 7): with no booking email there is nothing to "use";
                // an empty box means nothing is sent at all.
                // board 2281 (Q181): a funder pays by their own email, typed here each time.
                jobInvoice.defaults.bookingHasEmail === false
                  ? 'This booking has no email. Leave it empty and nothing is sent: you download the PDF.'
                  : 'Leave it empty to use the email on this booking. If someone else is paying, type their email.'
              }
              error={errorFor('email')}
            />
            <div ref={previewAreaRef}>
              {previewed !== null && (
                <JobInvoicePreviewBlock
                  preview={previewed.preview}
                  anchorRef={previewRef}
                  bookingEmail={bookingEmail}
                  typedEmail={previewed.body.recipientEmail ?? ''}
                  lastRecipientWasTyped={jobInvoice.defaults.lastRecipientWasTyped === true}
                />
              )}
              <div class="action-row">
                {previewed === null ? (
                  <Button variant="secondary" disabled={busy} onClick={() => void check(billTo, address, email, false)}>
                    Check it
                  </Button>
                ) : (
                  <Button variant="secondary" disabled={busy} onClick={() => void send()}>
                    {/* ⛔ Rework 2 (delta 7): the preview's own recipient decides the verb;
                        when it is nobody, nothing is sent, so the button does not say so. */}
                    {previewed.preview.recipient.summary === 'nobody, PDF only' ? 'Yes, make it' : 'Yes, send it'}
                  </Button>
                )}
                <Button
                  variant="quiet"
                  size="inline"
                  disabled={busy}
                  onClick={() => {
                    returnFocus.current = true;
                    setReopenDisclosure(true);
                    close();
                    setRefusal(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </>
        )}
        {issued !== null && words !== null && (
          <Banner kind={words.kind} elementRef={bannerRef} tabIndex={-1}>
            {`${words.text} `}
            {/* ⛔ THIS SERVER'S OWN ROUTE, with the row id from the issue response:
                the PDF is streamed through the kiosk, never a Stripe URL (H12). */}
            <a href={jobInvoicePdfHref(bookingId, issued.documentId)} download>
              {JOB_INVOICE_PDF_LINK}
            </a>
          </Banner>
        )}
        {refusal !== null && (
          <Banner kind={refusal.kind === 'not-configured' ? 'info' : 'error'} elementRef={bannerRef} tabIndex={-1}>
            {refusal.message}
          </Banner>
        )}
      </div>
    </>
  );
}

/**
 * ⭐ T2181 rework 1 — CAN THIS CIVIL DATE GO THROUGH THE HOUSE FORMATTER WITHOUT
 * SAYING SOMETHING THE CUSTOMER DID NOT?
 *
 * `whenText` feeds `YYYY-MM-DDT00:00:00Z` to `new Date`, which does not reject an
 * impossible day — it ROLLS OVER. `2026-02-31` renders as "Tue 3 Mar", a date
 * nobody typed, on the one row whose whole job is to repeat what the customer
 * said. So the value is formatted only when the instant round-trips BACK to the
 * same civil date.
 *
 * ⛔ A ROUND TRIP, NOT A FOURTH `isCalendarDate`. `server/app.ts:679` has one and
 * `server/inquiry.ts` carries a deliberate byte-identical copy of it whose own
 * docblock flags the duplication; both are server-side and unreachable from here.
 * A third hand-written copy is the drift that note is managing. The round trip
 * needs no calendar rules, and catches `NaN` and rollover with one comparison.
 *
 * ⚠️ UTC on both sides, for the reason `whenText` states: a civil date has no
 * zone, so reading it back in the zone it was written in is what keeps the day
 * the customer picked on every host.
 */
function rendersAsCivilDate(date: string): boolean {
  const civil = date.slice(0, 10);
  const at = new Date(`${civil}T00:00:00Z`);
  return !Number.isNaN(at.getTime()) && at.toISOString().slice(0, 10) === civil;
}

export function BookingDetail({ registry, bookingId, onReopen, onRequestsChanged, onInboxChanged }: BookingDetailProps) {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  // T408: ONE background payment check per booking-OPEN — a mount/booking-
  // scoped one-shot, deliberately NOT keyed on payment status (a booking that
  // stays deposit_sent would re-fire a status-keyed effect into a Stripe-
  // calling loop). Reset only when the operator opens a different booking.
  const autoChecked = useRef(false);
  // T608 M4 — its OWN request and its OWN state, deliberately not folded into
  // `state`. A timeline failure must never blank the booking record: the journey,
  // the quote and the send affordance are what Helen is on this screen for, and
  // `getBookingTimeline` cannot reject (see actions.ts), so a history outage
  // cannot reach the detail's error arm however this component is edited.
  const [timeline, setTimeline] = useState<TimelineState>({ kind: 'loading' });
  // T1002 M7 — lifted out of CancelRun because DESIGN.md §5.1's rule is about
  // the SCREEN's primary, which lives in the action row below, not inside the
  // disclosure. Same shape as Inbox.tsx's `confirmingId`.
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  /**
   * ⭐ T1749 M8 (C15, the client half) — has the email correction come back 409
   * on THIS mount? Held by the host and not by the control, because the control
   * is the thing that may unmount: on the account 409 the re-read removes the
   * customer record the control edits, and a flag inside it would vanish with it
   * before it could be read. See `CustomerEmailCorrection`'s `onConflict`.
   */
  const [emailConflict, setEmailConflict] = useState(false);
  /** T1749 M8 round 2 (security L3): the 409 re-read is IN FLIGHT. Until it lands
   *  the client cannot tell the account 409 from the email one, so the control
   *  states neither — a flash of "already on file for a different customer" on a
   *  booking that turns out to be account-billed is a sentence about a third party
   *  that was never true. ⛔ NOT `emailConflict`, which stays true afterwards. */
  const [conflictReloading, setConflictReloading] = useState(false);
  /**
   * ⭐⭐ Board 2814 M2 (DD-11 item 4) - THE HOME'S REQUEST, READ ONCE PER BOOKING for
   * the ask block and the date-change disclosure together (`useLinkedRequest`),
   * keyed on the server's portal-link flag and re-read on `requestReload`. Called
   * above the loading and error returns, as a hook must be; it reads nothing until
   * the detail says the booking is portal-linked.
   */
  const [requestReload, setRequestReload] = useState(0);
  const linkedRequest = useLinkedRequest(
    bookingId,
    state.kind === 'ready' && state.detail.reschedule?.linked === true,
    requestReload,
  );
  const reloadRequest = () => setRequestReload((k) => k + 1);

  /**
   * ⚠️ T1749 M8 round 2 (security L3): it RETURNS the DETAIL read's promise now,
   * so a caller that has to know when the re-read has LANDED can wait for it. The
   * one such caller is the email correction's 409 path — see `conflictReloading`.
   * Every other call site ignores the value exactly as before.
   * ⛔ THE RETURN IS AT THE END, AND THAT IS NOT A STYLE CHOICE. A `return` on the
   * first statement made the TIMELINE read below unreachable — measured: the
   * Progress spine and the History panel stopped rendering altogether, in 36
   * specs. Both reads still fire on every call; only one of them is awaitable.
   */
  const load = () => {
    const detailRead = getBookingDetailFull(bookingId)
      .then((detail) => {
        setState({ kind: 'ready', detail });
      })
      .catch(() => setState({ kind: 'error' }));
    // `null` is the read's could-not-load signal, never an empty history.
    // T1033 M7 part 2: the SAME request now carries the lifecycle spine, so the
    // screen makes no extra round trip for it and the two cannot disagree about
    // which booking they describe.
    void getBookingTimeline(bookingId).then((read) => setTimeline(
      read === null ? { kind: 'error' } : { kind: 'ready', entries: read.entries, lifecycle: read.lifecycle },
    ));
    return detailRead;
  };

  useEffect(() => {
    autoChecked.current = false;
    // Back to 'loading' first: without this, a switch to another booking would
    // show the PREVIOUS booking's history until the new fetch lands.
    setTimeline({ kind: 'loading' });
    // ...and a confirm carried across a booking switch would hide the new
    // booking's primary behind a question about the old one.
    setConfirmingCancel(false);
    // ⭐ T1749 M8 round 2 (security L4) — AND NEITHER FLAG MAY CROSS A BOOKING.
    // `emailConflict` is the answer to a 409 on THIS booking (C15); carried across
    // a switch, it would explain a refusal the operator never saw on a booking that
    // is account-billed. Reset with the reads, in the effect that owns the identity
    // change. (⛔ board 2812 M3 fix round 3, F3-3: the `sentQuote` witness retired
    // with `primaryIsSend`; the send's own gold is `SendQuote`'s.)
    setEmailConflict(false);
    setConflictReloading(false);
    load();
  }, [bookingId]);

  const refreshPayment = async () => {
    // The server's all-rows refresh detects a paid deposit; re-read after.
    await getOr(`/api/bookings/${bookingId}/payment`, null);
    load();
  };

  // T408 self-heal: opening a booking that is still awaiting its deposit runs
  // the same server-side refresh the button does (one Stripe pull, bounded) —
  // a missed webhook surfaces by simply opening the booking; Helen never has
  // to open Stripe, or even press anything, to know.
  useEffect(() => {
    if (state.kind !== 'ready' || autoChecked.current) return;
    if (state.detail.payment?.status === 'deposit_sent') {
      autoChecked.current = true;
      void refreshPayment();
    }
  }, [state]);

  if (state.kind === 'loading') {
    return (
      <div class="screen-pane">
        <h1 class="screen-title" tabIndex={-1}>Booking</h1>
        {/* T1749 M8 round 2 (impeccable P2-3, §7.2): the library's `loading` state.
            This screen already renders its own error branch beside it, so the two
            facts stay two facts. */}
        <EmptyState state="loading" headline="Getting the booking…" />
      </div>
    );
  }
  if (state.kind === 'error') {
    return (
      <div class="screen-pane">
        <h1 class="screen-title" tabIndex={-1}>Booking</h1>
        <Banner kind="error">
          Loading this booking failed. It is safe on the server. Try again in a moment.{' '}
          <button type="button" class="btn btn--quiet btn--inline" onClick={load}>Try again</button>
        </Banner>
      </div>
    );
  }

  const { detail } = state;
  const b = detail.booking;
  const prefers = contactPreferenceLabel(b.contact_preference);
  const requirements = addOnLabelPairs(registry).filter(([key]) => {
    const map: Record<string, boolean | number> = {
      meetAndGreet: b.meet_greet,
      extraPickups: b.extra_pickups,
      returnTrip: b.return_trip,
      lateNight: b.late_night,
      trailer: b.trailer,
      accessibleVehicle: b.accessible_vehicle,
    };
    const v = map[key];
    return typeof v === 'number' ? v > 0 : Boolean(v);
  });
  const when = whenText(b.journey_date, b.journey_time);
  /**
   * ⭐⭐ board 2637 (plan D2) — THE HOME'S APPOINTMENT, SMALL, UNDER WHEN. A
   * crossed appointment request now lands its COLLECTION in `journey_time` (or
   * no time at all), so the appointment would otherwise vanish from the booking.
   * It comes off the SAME `portalRequest` read as the return marker; null on a
   * web enquiry, a collection run and every booking crossed before this.
   * ⭐ Board 2647 (K4): IN THE WHEN LINE'S OWN SHAPE ("Thu 1 Oct, 13:00"), through
   * the house formatter `whenText` uses, in Europe/Dublin with its 24-hour clock
   * (a local-midnight appointment reads 00:30). `formatDublinInstant` stays the
   * validity check only, and is itself unchanged: its "Thu, 1 Oct, 13:00" is the
   * driver message's and the Requests list's shape.
   */
  const appointmentIso = detail.portalRequest?.appointmentAt ?? null;
  const appointmentParts =
    formatDublinInstant(appointmentIso) === null
      ? null
      : formatWhenParts({ journeyDate: appointmentIso, timeZone: 'Europe/Dublin' });
  const appointmentAt =
    appointmentParts === null ? null : [appointmentParts.date, appointmentParts.clock].filter(Boolean).join(', ');
  const appointmentTime = detail.portalRequest?.appointmentTime ?? null;
  const appointment =
    appointmentAt !== null ? `Appointment ${appointmentAt}`
      : appointmentTime !== null ? `Appointment ${appointmentTime}` : null;
  // ⭐ T1871 M6 - the exact headcount, from the route's own one-column read at
  // the TOP LEVEL of the payload (not under `booking`, which is getBooking's
  // 18-column SELECT and may not be widened). null = not captured, and the
  // Passengers row below then shows the band alone.
  const passengersExactLabel = formatPassengerCount(detail.passengersExact);
  /**
   * ⭐⭐ T1717 M6 (DD5 + DD6, P0-7 + P0-11, surface 11 of the 19) - THE TWO
   * RETURN FACTS THIS SCREEN NOW STATES, and they are separate on purpose.
   *
   *   `backWhen`   - WHEN the bus is wanted back (DD5). Gated on the booking's
   *                  OWN `return_trip`, so a job the operator has set to one way
   *                  stops printing a time without the stored instant being
   *                  destroyed. Null on every non-portal booking, on a recurring
   *                  request (DD4 residual) and on every row written before M6.
   *                  ⭐⭐ T2088 M7 (D5): it also answers `time to confirm`, off
   *                  the HOME's marker, when the home said the return time is
   *                  not known yet. The flag is still the outer gate and the
   *                  instant still wins whenever there is one; the marker fills
   *                  the slot the instant would have taken, on a booking whose
   *                  price is already doubled for that leg.
   *                  ⛔ THE MARKER IS READ FROM `portalRequest`, WHICH IS THE
   *                  DURABLE `inquiries` ROW, and not from anything on
   *                  `bookings`: the kiosk took no column and no migration for
   *                  it (it rides `inquiries.extra`), so this screen is reading
   *                  the same durable answer `homeSaid` below reads, through the
   *                  same one read the route already performs.
   *                  ⛔ AND A NON-PORTAL BOOKING CANNOT REACH IT: `portalRequest`
   *                  is null there, so the marker is `undefined` and the
   *                  expression falls through to the instant exactly as before.
   *   `homeSaid`   - what the NURSING HOME answered, shown ONLY when it differs
   *                  from the flag on the row (DD6). The chips above already say
   *                  what the booking IS; this says what was asked for, which is
   *                  only worth a line when the two disagree.
   *
   * ⛔ `portalRequest === null` IS "NOBODY FROM A HOME ANSWERED" AND MUST NOT
   * RENDER. It is a web enquiry or a walk-in, and a line reading "The home said:
   * one way" over a customer's own tick would attribute her answer to a third
   * party who never saw the booking.
   */
  /**
   * ⭐⭐ T2181 (board 2181) — THE SITE LANE'S FREE TEXT, AS THE FALLBACK IT ALWAYS
   * SHOULD HAVE BEEN. Same two-lane shape `CustomerInfoBlock` already ships and
   * the P2 follow-up it recorded: the portal's validated instant is preferred,
   * and a web enquiry — which has no instant, because nothing normalises its
   * three boxes into `return_at` — falls back to what the customer typed.
   *
   * ⛔ ONE ROW, NOT THREE. Helen bounced off a tool with too many buttons, and
   * the same reasoning that made this one row on the Quote card applies here.
   *
   * 🔴 `|| null`, NOT `?? `. `.join(', ')` returns `''` for an all-absent set, and
   * `''` is NOT nullish — so `??` would keep it, and the push below guards on
   * `!== null`, which `''` passes. That renders an EMPTY `Back` row in precisely
   * the "the customer left it blank" case that must render nothing, and it reds
   * the `unset` control arm in booking-detail.spec.tsx. Blank return boxes mean
   * "same as outbound", never "there is no return" — so the row stays silent
   * rather than asserting an absence.
   * ⭐ board 2871 (Captain's ruling) SUPERSEDES THAT LAST SENTENCE: the `|| null`
   * still keeps an EMPTY value out, and `backWhen` below now says `time not set`.
   */
  /* ⭐ T2181 rework 1 (Captain's live drive) — THE RETURN READS IN THE HOUSE STYLE.
     The raw text sat directly under a `When` row reading "Tue 15 Sept, 08:30" and
     said "2026-10-03, 05:30": one screen, two grammars for the same kind of fact.
     `returnDate` + `returnTime` are exactly the shape `whenText` takes, so the
     Back row now goes through the SAME formatter the `When` row uses.

     ⛔ FORMAT ONLY WHEN BOTH HALVES ARE THERE AND THE DATE SURVIVES THE ROUND TRIP;
     otherwise print WHAT THE CUSTOMER TYPED, verbatim. Never drop a half to make
     the other half pretty — a date with no time, a time with no date, and an
     impossible date are all things she said, and this row exists to repeat them.
     `whenText` itself drops neither half, so the raw arm only carries the cases
     the formatter would misrepresent. */
  const returnDate = detail.returnDate ?? null;
  const returnTime = detail.returnTime ?? null;
  const returnPassengers = detail.returnPassengers ?? null;
  const returnWhen =
    returnDate !== null && returnTime !== null && rendersAsCivilDate(returnDate)
      ? whenText(returnDate, returnTime)
      : [returnDate, returnTime].filter(Boolean).join(', ');
  /* ⛔ THE BAND CARRIES ITS UNIT HERE AND NOWHERE ELSE. On the `Passengers` row a
     bare "8-12" is unambiguous because the term says what it is; on this row it
     sits beside a date and a time, where a bare range reads as anything. The unit
     is composed from the VARIABLE — a band literal in source is what `check:copy`
     bans — and it is always plural because a band is a RANGE with no singular
     form, so this is not a second copy of `formatPassengerCount`'s plural rule. */
  const returnLeg =
    [returnWhen, returnPassengers === null ? null : `${returnPassengers} passengers`]
      .filter(Boolean)
      .join(', ') || null;
  /* ⭐ board 2871 (Captain's ruling): a returning job ALWAYS has a Back row. The
     stored return (or the home's to-confirm marker, which the portal never sets
     beside an instant), then the customer's own text, then `time not set`: an
     absent row read as "no return" on a job priced for one. It supersedes the
     "stays silent" note above. The row is in the When row's shape (C5). */
  const backWhen = b.return_trip
    ? (formatBackWhen(detail.returnAt, detail.portalRequest?.returnAtState, b.journey_date) ?? returnLeg ?? RETURN_TIME_NOT_SET)
    : null;
  const homeAnswer = detail.portalRequest?.returnTrip ?? null;
  const homeSaid =
    homeAnswer !== null && homeAnswer !== b.return_trip
      ? homeAnswer
        ? 'The home said: comes back'
        : 'The home said: one way'
      : null;
  // T549 M8 - the server's OWN recipient precedence (customers.email first,
  // this booking's inquiry address second), from the helper the send path uses.
  // Resolved ONCE so the row below and the send surface further down cannot
  // disagree about either the address or where it came from.
  // ⛔ v33 — `detail.customer` is NULL for an ACCOUNT booking (billed to an
  // organisation, no customer row). Bound once so every read below states what it
  // does about the absence, rather than each one re-deriving it.
  const customer = detail.customer;
  // ⭐ T1746 M4 (B5) - absent on every hand-built fixture that predates the key,
  // so it is read through a default rather than asserted; both halves already
  // mean "say nothing" when null.
  const identity = detail.identity ?? { accountName: null, residentDisplayName: null };
  // ⭐ board 2811 M4: when the stale balance email goes, '' when it cannot be read
  // (the note then says so undated rather than "on .").
  const staleEmailWhen = dublinDayAtTime(detail.balance_email_stale_sends_at ?? '');
  const recipient = resolveRecipientEmail(customer?.email ?? null, detail.inquiry_email);
  // T660 M4 (P0-7) - WHERE that send went, off the ledger row. `?? null` absorbs
  // an absent field (older server, or one of the hand-built detail fixtures) as
  // "not recorded", never as `undefined` reaching the sentence.
  // T660 M7: hoisted out of the SendQuote props below so the send surface and
  // `primaryIsSend` read ONE expression rather than two that can drift.
  const latestSend = detail.latest_send
    ? {
        channel: detail.latest_send.channel,
        deliveryStatus: detail.latest_send.delivery_status,
        target: detail.latest_send.target ?? null,
      }
    : null;
  /**
   * T660 M7 (P0-22, plan erratum 26) — does the SEND hold the gold on this
   * render?
   *
   * M6 left `Re-open in calculator` unconditionally `btn--primary` with the
   * reason written into the action row below: SendQuote does not mount on an
   * unquoted booking, so demoting the re-open outright would leave that screen
   * with no primary at all. Correct, and it left the other half open — a
   * QUOTED-but-unsent booking showed two gold controls at once, with nothing to
   * say which of "send this" and "go back and re-price it" was the next move.
   * ux-review confirmed both visible together at 1024x768.
   *
   * This is Done.tsx's shape, not a new rule: secondary exactly when a primary
   * send control is on screen, primary otherwise. It ASKS `sendQuoteHasPrimary`
   * (the same exported helper Done asks) rather than restating the condition,
   * because a restatement is how the two hosts drift — the send affordance also
   * needs a REACHABLE channel, so a quoted booking with no phone and no email
   * renders the block with no send button in it and must keep its primary here.
   * `sendBlock` is the mount condition of the <SendQuote> below, lifted to a
   * const so the two cannot disagree. Label unchanged either way.
   * ⛔ SUPERSEDED by board 2812 M3 fix round 3 (F3-3, SC-11): Re-open is never gold
   * now; the detail's one gold is its next step's action (see `sendBlock` below).
   */
  /**
   * ⭐ T1002 M7 — IS THE DEPOSIT ALREADY PAID? Read ONCE and used three times
   * (two control removals plus the cancel affordance's refund arm), because
   * three restatements of the same predicate is how one of them ends up
   * disagreeing with the other two on the screen that decides money.
   */
  const depositPaid = detail.payment?.status === 'deposit_paid';
  /** ⭐ board 2812 M3 (step 7): the server's `moneyKind`; absent (a hand-built fixture
   *  that predates it) reads as web, today's screen. */
  const takesWebMoney = detail.moneyKind === undefined || detail.moneyKind === 'web';
  /** ⭐ Board 2814 M2 - the home's ask is open on this booking (read, not guessed). */
  const askOpen = linkedRequest?.kind === 'ready' && linkedRequest.request.askState === 'open';
  /**
   * ⭐ Board 2814 M4 (Captain ruling (c), 2026-10-06) - THE RUN HAS TRAVELLED, read
   * off the server's own spine (the same read `MarkTravelled` uses). A travelled run
   * stays on the month's bill from every Requests-screen control, so the ask block
   * says so before the press, and this screen's own cancel confirm says the trip
   * ran and sends `travelledAcknowledged`: the one correction path.
   */
  /** ⭐ board 2812 M3 fix round 2 (UX-4): the run is confirmed by the server's own spine
   *  (`null` lifecycle = cancelled or not yet confirmed), the run controls' own gate. */
  const runConfirmedForControls = timeline.kind === 'ready' && timeline.lifecycle !== null;
  const runTravelled = timeline.kind === 'ready' && timeline.lifecycle !== null
    && timeline.lifecycle.some((s) => s.id === 'travelled' && s.state === 'done');
  /* ⭐⭐ board 2812 M3 fix round (Captain's item 6): a home's run (an `account` or a `cash`
     run) takes no money online and has no quote to send its customer: the home is invoiced
     at month end, or the driver is paid on the day. So the whole send block (Send quote to
     customer, Copy message, the deposit-link offer, Payment link only) mounts on WEB money
     only.
     ⭐⭐ board 2814 M8R1 (R7, Q288 (i)), kept at board 2812 M5's Keep current - AND ONLY
     WHERE THE SERVER'S `calculatorAllowed` SAYS SO (`core/calculator-access.ts`: false for
     an account booking, a nursing latest quote or a portal-linked booking), `=== true` so
     an absent flag shows no send either. Never a client guess; both must hold. */
  const sendBlock = takesWebMoney && detail.calculatorAllowed === true
    && detail.latest_quote_cents != null && detail.latest_quote_cents > 0;
  /* ⛔ board 2812 M3 fix round 3 (F3-3): `primaryIsSend` is RETIRED with the rule it
     served. Re-open in calculator is never gold now, so it no longer asks whether the
     send holds the gold; the send's own gold is `SendQuote`'s (`sendQuoteHasPrimary`
     inside it, with `sentOk`), yielded unless the send is the detail's next step. */
  // A golf stop address is the stored route label (em dash + arrow); render one
  // arrow. Display only — the persisted stop address is untouched.
  //
  // T608 M6a display fix: a golf journey joins its ends with the ARROW. Before
  // M6a a golf booking was ONE stop carrying the composite label, so this screen
  // showed 'Arklow Bay Hotel → Druids Glen Resort' via formatRouteLabel; the two-stop
  // split newly routed golf into the word-join and it read '... to ...' here
  // while every other surface (Location tiles, Quote, Done) kept the arrow.
  // GOLF ONLY: an airport transfer has always read 'X to Y' on this screen and
  // has its own pins.
  //
  // T660 M6 (R2) — the WIDER collapse is decided PER STOP, never per booking.
  // A stop address is free text on every service INCLUDING golf
  // (`booking_stops.address` carries whatever the customer or Helen typed), and
  // `displayRouteLabel` rewrites EVERY ' — ', so a booking_type-only gate turned
  // a typed 'The K Club — Straffan' into 'The K Club → Straffan' on a persisted
  // record. R1 read that as an off-golf hazard and it is not: `journeyStops`
  // (client/lib/actions.ts) pushes `customerStops`/`operatorStops` as
  // `role: 'middle'` rows on EVERY service, golf included, before it appends the
  // course row — so a golf outing with a customer-stated extra pickup is an
  // ordinary record, and R1 corrupted its middle stop. The golf ENDS are free
  // text too whenever no route was picked (`pickupEndPlace` falls through to
  // `pickupSearchText`), which is why `role` is not the discriminator either.
  //
  // No BYTE test can separate the two shapes (route-label.ts's caller contract:
  // a typed 'X — Y' and a stored label both split as a clean pair), so the
  // discriminator is PROVENANCE, and this screen holds exactly one proof of it:
  // the rate key the persisted quote was priced against. It is the SAME
  // predicate `reopenBooking` uses to prove a stop pair is the rate's own pair —
  // both key writers compose `${slugify(hubName)}__${slugify(routeName)}`
  // (shared/pricing-flatten, core/data/audit-repo's entryKeys) and `slugify`
  // strips the course arrow, so a label that re-derives the persisted key
  // demonstrably IS that rate's label, in both leg shapes. slugify is a
  // PREDICATE here and nothing more: it is lossy, so the answer is only ever
  // "does this address derive the key we already hold?" and no key is minted.
  //
  // `booking_type === 'golf'` is kept over it on the `service === 'golf'`
  // precedent in store/selectors.ts: the hub boundary is a golf-rates construct,
  // so no other booking_type can hold a label worth collapsing.
  //
  // T660 M6 (R3) — the key match alone is NOT enough, because `slugify` is
  // many-to-one (lowercase, then every non-alphanumeric run becomes '-'). A
  // free-text MIDDLE that merely restates this booking's already-quoted hub and
  // course in different case or punctuation re-derives the IDENTICAL key
  // ('arklow bay hotel — druids glen resort' -> 'arklow-bay-hotel__druids-glen-resort')
  // and had its own em dash silently rewritten. So `role` joins the key match as
  // a further EXCLUSIONARY conjunct — never as a replacement for it. The two
  // exclude different halves of the free text and both are needed: the key match
  // excludes free-text ENDS (a golf job with no route picked persists typed text
  // under role 'pickup', so `role` can never positively prove a stop IS the
  // stored composite), and `role` excludes free-text MIDDLES (per `journeyStops`
  // in client/lib/actions.ts, a 'middle' row is BY CONTRACT customer- or
  // operator-stated text — `customerStops`/`operatorStops` are its only writers —
  // so it can never legitimately be the persisted route's own end, whatever key
  // its bytes happen to derive).
  //
  // An ABSENT role (`null`/`undefined`) is NOT read as "safe". An earlier
  // revision did read it that way, on the premise that a pre-v20 golf booking is
  // always ONE row carrying the whole composite. The output gate falsified that
  // from the history: free-text middle stops shipped in `ff4380b` (T400 M2,
  // 2026-07-17) while the `role` column only landed in migration v20 (`b30236a`,
  // 2026-07-30) — a 13-day window whose golf bookings hold middle stops with a
  // NULL role. `replaceBookingStops` is DELETE + wholesale reinsert, never a
  // partial update, so any booking not re-saved since still carries them.
  //
  // So an absent role falls back to the one question it can still answer
  // honestly: a SOLE stop cannot be a middle, because a middle by construction
  // sits between two ends. That keeps the legacy single-row composite collapsing
  // (the population this exists for) while a legacy MULTI-stop booking — the
  // only shape that can hide a colliding middle — degrades to the bare boundary
  // rather than rewriting a customer's own em dash. Cosmetic miss over
  // corruption, in the direction that cannot lose data.
  // simplified: an UNQUOTED golf booking (no quote version, so no key) keeps the
  // bare boundary on a route label it may really hold - the honest degradation
  // reopenBooking already takes, since nothing on this projection can tell that
  // address from typed text. Upgrade trigger = a rate id on `bookings` itself.
  const quotedRouteId = detail.latest_quote_location_id;
  const isQuotedRouteLabel = (address: string): boolean => {
    const ends = splitRouteLabel(address);
    return ends !== null
      && quotedRouteId != null && quotedRouteId !== ''
      && `${slugify(ends.origin)}__${slugify(ends.destination)}` === quotedRouteId;
  };
  const notAMiddleStop = (s: (typeof detail.stops)[number]): boolean =>
    s.role != null ? s.role !== 'middle' : detail.stops.length === 1;
  const displayStop = (s: (typeof detail.stops)[number]): string =>
    b.booking_type === 'golf' && notAMiddleStop(s) && isQuotedRouteLabel(s.address)
      ? displayRouteLabel(s.address)
      : formatRouteLabel(s.address);
  const route = detail.stops
    .map((s) => displayStop(s))
    .join(b.booking_type === 'golf' ? DISPLAY_SEPARATOR : ' to ');
  // T400 item 4 — the captured driver details per stop (v16 slots), rendered
  // as quiet copy-friendly lines Helen reads out or pastes to a driver. A stop
  // with nothing beyond its place label adds no line.
  const stopDetail = (s: (typeof detail.stops)[number]) =>
    [s.address_line, s.town && s.town !== s.address ? s.town : null, s.eircode]
      .filter((v): v is string => Boolean(v && v.trim()))
      .join(', ');
  const detailedStops = detail.stops.filter((s) => stopDetail(s) !== '');

  /**
   * ⭐⭐ T1749 M8 (DS-22 c) — THE RECORD'S FACTS, AS DATA, for the library's
   * `FactsTable`. Every row is the one it was in the hand-drawn `<dl>`, in the
   * same order and with the same gate; what changed is that the SHAPE is the
   * ruled one and lives in one place for both tools.
   *
   * ⛔ ONE ADDITION, AND IT IS A PROMOTION RATHER THAN A NEW FACT: `Back` — the
   * return leg's time — was a `<p class="field-hint">` UNDER the block, a quiet
   * line in hint grey below the facts it belongs to, so the second half of a
   * return journey read as a footnote to the first. It is a fact about the
   * journey and it sits with the journey.
   * ⛔ THE `homeSaid` LINE STAYS A SEPARATE CONDITION AND STAYS OUTSIDE, because
   * the two were never one: a booking can have a return time with no
   * disagreement (the ordinary case) or a disagreement with no time (the home
   * said one way, Helen ticked return). Folding them would hide one behind the
   * other, which is what the line's own note has said since T1717 M6.
   */
  const facts: { term: string; value: ComponentChildren }[] = [];
  // ⛔ v33 — the whole customer block is conditional: an ACCOUNT booking has no
  // customer record, and an empty value would read as a customer whose name we
  // failed to store.
  /* ⭐ board 2812 M5 (fix round 1, U1): the title says WHO the booking is for, as the
     round-2 mockup draws it: the home on an account run (`identity.accountName`), the
     customer on a web job, and "Booking" only when neither is known. Never the resident
     (S8). The hidden "Booking: " keeps the heading's accessible name saying what the
     screen is, which is also the name the e2e net finds this screen by. The fact the
     title already says (Name, or Home) is not repeated below it, as the mockup draws. */
  const titleAccount = identity.accountName?.trim() || null;
  const titleCustomer = titleAccount === null ? customer?.name?.trim() || null : null;
  const titleWho = titleAccount ?? titleCustomer;
  if (customer && titleCustomer === null) facts.push({ term: 'Name', value: customer.name });
  /* ⭐⭐ T1746 M4 (B5, DD6) - WHO THE JOB IS FOR WHEN THERE IS NO CUSTOMER
     RECORD. Two facts from the route's own `identity` bolt-on: the home is
     `accounts.name` through this booking's account snapshot, the resident is the
     portal request's display name resolved at render and stored in NO kiosk
     table (S8).
     ⛔ EACH IS GATED ON ITS OWN VALUE, never on the other: a home whose resident
     could not be read still says which home, which is the degraded answer B5
     asks for. */
  if (identity.accountName != null && titleAccount === null) facts.push({ term: 'Home', value: identity.accountName });
  // ⭐ board 2814 M4 (DD-5): the cash fact, in the words the Requests step uses.
  if (detail.cashToDriver === true) facts.push({ term: 'Paying', value: CASH_TO_DRIVER_WORDS });
  // ⭐ board 2814 M7 (DD-7, S16): the pass, with its time and Helen's note. This
  // screen is the only place the note is ever shown.
  if (detail.taxi != null) {
    facts.push({ term: 'Passed to a taxi', value: shortDateTime(detail.taxi.at) });
    if (detail.taxi.note !== null) facts.push({ term: 'Taxi note', value: detail.taxi.note });
  }
  if (identity.residentDisplayName != null) {
    facts.push({ term: 'Resident', value: identity.residentDisplayName });
  }
  if (customer?.phone_e164) {
    /* T660 M2: the two copy affordances used to hang off this value cell. They
       render inside the SHARED delivery block below (P0-14), beside the same
       manual route on Done. Copy MESSAGE moved on equivalent terms (its gate was
       already the send block's own expression); Copy NUMBER moved on strictly
       NARROWER ones (it was gated on this cell alone, so it showed on any booking
       with a number on file, and now inherits the block's mount condition too).
       The narrowing costs nothing real: the `tel:` link here is unchanged. */
    /* ⭐ BOARD 2089 M3 (R-7) — THE TEXT IS THE READ-BACK FORM, THE HREF IS THE
       BARE E.164. The two are deliberately different: `formatPhoneWithCountry`
       spaces the number and names its country, which is what Helen reads out and
       what tells her a `+44` booking is a visitor rather than a typo; a `tel:`
       target with spaces and a bracketed country name in it is a URI nothing
       should have to parse. The href is byte-identical to what it was. */
    facts.push({
      term: 'Phone',
      value: <a href={`tel:${customer.phone_e164}`}>{formatPhoneWithCountry(customer.phone_e164)}</a>,
    });
  } else if (customer) {
    /* ⛔⛔ BOARD 2089 M3 (R-6) — THE ABSENCE IS STATED, NOT LEFT BLANK. A customer
       row with a null `phone_e164` is the shape a REFUSED number leaves behind
       (M1 stores the reason and never the digits), and until now this panel simply
       omitted the Phone row — so the one screen Helen opens to chase a booking
       said nothing about why no text can go to it.
       ⚠️ IT NAMES NO COUNTRY, and that is honest rather than an omission: this
       route carries `{ name, phone_e164, email }` and neither the typed string
       nor the country it was read under reaches this screen, so there is nothing
       to name. `CustomerInfoBlock`, which DOES hold both, says which country.
       ⚠️ NOR DOES IT CLAIM A REFUSAL: a booking whose customer never gave a number
       is the same null. Both states have the same consequence and the same
       remedy, which is what the line states. */
    facts.push({
      term: 'Phone',
      value: <p class="field-hint" role="status">{phoneRefusalCopy(null)}</p>,
    });
  }
  if (customer?.email) facts.push({ term: 'Email', value: customer.email });
  /* T549 M8 - the address supplied on THIS booking's own inquiry. Shown only
     when the customer record has none, because that is the only case where it
     changes anything: it is then what a quote email would go to. The label is the
     point - it says the address came off the web form rather than out of the
     customer record, a distinction Helen can act on (ring and confirm) and which
     no downstream code is allowed to lose. */
  if (recipient.fromInquiry) {
    facts.push({ term: 'Email from this inquiry', value: recipient.email });
  }
  // T484 — same row, same wording as the Quote screen's CustomerInfoBlock, so
  // "Prefers" means one thing wherever Helen sees it.
  if (prefers) facts.push({ term: 'Prefers', value: prefers });
  facts.push({
    term: 'Journey',
    // A booking with NO stops is undispatchable: a venue name and an Eircode, no
    // address to send a bus to. Weddings land here by design - they never visit
    // the Location screen and the site form never asks - so the gap has to
    // surface HERE, days before the job, rather than on the morning. Quiet and
    // non-blocking on purpose: it is a prompt to ring the customer, not a
    // validation error.
    // ⭐ board 2814 M6 (DD-8): `route` is a JOINED STRING, so the `??` this used was
    // never reached and the line never showed; an empty join is the no-stops case.
    // An account run never opens the calculator, so its line says to ring the home.
    value: route !== ''
      ? route
      : detail.accountBooking === true
        ? 'No pickup address yet. Ring the home to confirm it before the day.'
        : 'No pickup address yet. Add it in the calculator before the day.',
  });
  if (detailedStops.length > 0) {
    facts.push({
      term: 'Driver details',
      value: detailedStops.map((st) => <div key={st.seq}>{`${st.address}: ${stopDetail(st)}`}</div>),
    });
  }
  if (detail.flight) facts.push({ term: 'Flight', value: detail.flight });
  /* ⭐⭐ T2181 — THE TERMINAL THE CUSTOMER PICKED, directly under the flight it
     belongs to. 🔴 GUARDED ON THE RAW TOKEN, never on the designation:
     `terminalDesignation('not-sure')` is null by design, so guarding on the
     wording would silently drop the customer who answered "not sure" — the one
     answer this row most needs to carry, because it tells Helen to ask. The
     server has already gated the token on the inquiry being an airport job, so
     an airport lead that never answered arrives null here and renders nothing
     rather than asserting "Not sure" at her. */
  if (detail.terminal) {
    facts.push({ term: 'Terminal', value: terminalDesignation(detail.terminal) ?? 'Not sure' });
  }
  if (b.venue) facts.push({ term: 'Venue', value: b.venue });
  if (when || appointment !== null) {
    facts.push({
      term: 'When',
      value: appointment === null ? when : (
        <>
          {when}
          <span class="portal-request-appointment">{appointment}</span>
        </>
      ),
    });
  }
  // ⭐ T1749 M8 (DS-22 c) — the return leg, promoted out of the hint line below.
  if (backWhen !== null) facts.push({ term: 'Back', value: backWhen });
  /* ⭐⭐ T1871 M6 - THE BAND AND THE COUNT, in the row that already says
     "Passengers". Two facts, `·`-separated like the list strip: the band is the
     PRICE KEY (NOT NULL, and what the quote was built on), the count is how many
     people are travelling.
     ⛔ THE GUARD IS AN OR, NOT AN AND. A row with a count and no band is as
     renderable as one with a band and no count; `.filter(Boolean)` drops whichever
     is missing. `formatPassengerCount` answers null for "not captured" - the
     majority answer - so a legacy row renders the bare band exactly as it did
     before that milestone, never a `0` and never a placeholder. */
  if (b.passengers_band || passengersExactLabel) {
    facts.push({
      term: 'Passengers',
      value: [b.passengers_band, passengersExactLabel].filter(Boolean).join(' · '),
    });
  }
  /* T549 M4 — provenance on the PERSISTED quote, which is the whole point of
     persisting it: this panel is what a booking opened in a fresh session (or by
     the other operator) shows, and until then it rendered a distance-derived
     guess and a real grid price as the same bare figure. Reads the server's own
     column, never client state — there is no live engine result on this screen.
     ⛔⛔ IT IS THE PERSISTED QUOTE AND NOTHING IS RE-RESOLVED HERE. The account
     price already reached this booking through the ordinary quote path, so this
     screen renders the figure that was AGREED. Asking the server to re-resolve it
     at render time would let a LATER edit to the price book silently rewrite a job
     that is already confirmed and may already be invoiced. Pinned by the
     no-second-read arm in test/client/booking-detail.spec.tsx.
     ⚠️ SERVER TRUTH FOR THE SERVICE (`latest_quote_service`), never
     `booking_type` — a nursing job folds to 'local-other' and reads 'Custom'.
     ⭐⭐ board 2812 fix round (Captain item 1, SC-7): THE FIGURE MOVED TO THE MONEY
     COLUMN, as the round-2 mockup draws it: `moneyFacts`, not `facts`, so it is stated
     once, under "Money". Its term is the mockup's: "Quote" on a web job, "Price" on a
     home's run (whose figure comes from the home's agreed prices, never a quote). */
  const moneyFacts: { term: string; value: ComponentChildren }[] = [];
  if (detail.latest_quote_cents != null) {
    moneyFacts.push({
      term: takesWebMoney ? 'Quote' : 'Price',
      value: (
        <span class="money">
          {euro(detail.latest_quote_cents)}
          {detail.latest_quote_pricing_status === 'estimated' &&
            <span class="chip chip--estimate">Estimate</span>}
          {/* ⚠️ T1749 M8 (ux round 2, WARN 1) — `display: block`, so the advisory
              starts a line of its own. As an inline sibling of the figure it
              rendered "€130Agreed for this run when it was priced…": two facts
              with no space between them, which is what the `<dd>` it used to
              live in was hiding. A LINE rather than a literal space, because it
              is a sentence about the figure and not a clause of it. */}
          {/* ⚠️ T1749 M8 round 3 (impeccable C6) — `.provenance-line`, NOT a hint.
              §3.1 sends "a caution to weigh" to the gold provenance treatment, and
              that is exactly what this sentence is: it says WHERE the figure came
              from (agreed when the run was priced) and what it is NOT (re-checked
              against today's book). The own-line display stays — it follows a
              figure on the same line. */}
          {/* ⭐ board 2814 M6 fix round 1 (ux F5): SAY WHERE THE FIGURE CAME FROM. A
              figure Helen typed ("Change the price", or a price typed on the phone-in
              form) is not the agreed price, so it does not say so. Server truth:
              the latest version's `pricing_context.basis` (`quoteSetByHand`). */}
          {detail.latest_quote_service === 'nursing-home' && detail.quoteSetByHand === true && (
            <span class="provenance-line field-hint--own-line">
              Set by hand for this run. A later change to the home’s agreed prices
              does not move it.
            </span>
          )}
          {detail.latest_quote_service === 'nursing-home' && detail.quoteSetByHand !== true && (
            <span class="provenance-line field-hint--own-line">
              Agreed for this run when it was priced. It is not re-checked against
              the account price book here, so a later change to the book does not
              move this figure.
            </span>
          )}
        </span>
      ),
    });
  }
  // ⭐ (SC-7) the quote's own note travels with the figure it annotates.
  if (detail.latest_quote_note) moneyFacts.push({ term: 'Quote note', value: detail.latest_quote_note });
  if (b.note) facts.push({ term: 'Notes', value: b.note });
  /* ⭐⭐ board 2812 fix round (Captain item 1, SC-7): A WEB JOB'S DEPOSIT AND BALANCE AS
     FACT ROWS, the mockup's "Deposit" and "Balance". Every figure is one the detail
     already serves; nothing is computed on this screen.
     · Deposit is `detail.payment` (the newest payment row), the same oracle the
       "Payment:" line it replaces read, so it says no more and no less than that line.
     · Balance is the server's `balance` projection, absent when the server will not let
       anyone ask (no paid deposit, or not a web job), and then there is no row at all.
       Its words are `balanceFactWords`: 2811's balance lines, moved here from
       `AskForBalance`, which now holds the control only.
     ⛔ A home's run has no deposit stage and no balance: its Money column says how it is
     billed (`RUN_MONEY_WORDS`) and never offers either. */
  if (takesWebMoney) {
    const p = detail.payment;
    moneyFacts.push({
      term: 'Deposit',
      value: p === null
        ? (b.status === 'cancelled' ? 'Not asked' : 'Not asked yet')
        : p.status === 'deposit_paid'
          ? (p.paid_at ? `${euro(p.amount_cents)} paid on ${dublinDayAtTime(p.paid_at)}` : `${euro(p.amount_cents)} paid`)
          : p.status === 'deposit_sent'
            /* ⛔ (code-review L2) NO "yet" ON A CANCELLED BOOKING. The cancel sweeps
               switch a live deposit link off after the cancel commits (best effort:
               `deactivateOneLink`, server/app.ts), and nothing writes the payment row
               back, so it still reads `deposit_sent`. "Not paid yet" would read as a
               link still waiting to be paid; "not paid" is true whether or not the
               switch-off landed, so it claims neither. */
            ? `${euro(p.amount_cents)} link sent, not paid${b.status === 'cancelled' ? '' : ' yet'}`
            : `${euro(p.amount_cents)}, ${paymentStatusLabel(registry, p.status).toLowerCase()}`,
    });
  }
  if (detail.balance !== undefined && detail.balance !== null) {
    moneyFacts.push({
      term: 'Balance',
      value: balanceFactWords(
        detail.balance,
        detail.balance_unpaid_at_departure === true,
        // The "ask yourself" words ride only while the control is on screen.
        !confirmingCancel && balanceAskOffered(detail.balance)
          && (customer?.phone_e164?.trim() || customer?.email?.trim() || '') !== '',
      ),
    });
  }

  /* ⭐⭐ board 2812 M3 fix round 2 (UX-3, UX-4) - THE ONE NEXT STEP. The `Detail`'s
     next-step slot holds the most urgent action and nothing else: a home's ask (its
     Accept the gold, `LinkedAsk`, always first); else the balance still to pay ("Ask
     for the balance"); else, on a job not yet confirmed, sending the quote; else, on a
     home's run, marking it travelled or passing it to a taxi. Each node renders in ONE
     place: the next-step slot when it is the next step, its own section otherwise. */
  const balanceIsNext = !askOpen && detail.balance !== undefined && detail.balance !== null
    && detail.balance.state === 'none';
  const confirmedOrLater = b.status === 'confirmed' || b.status === 'completed' || b.status === 'cancelled';
  const sendIsNext = !askOpen && !balanceIsNext && !confirmedOrLater;
  const runIsNext = !takesWebMoney && !askOpen && runConfirmedForControls && !runTravelled
    && detail.taxi == null && b.status !== 'cancelled';
  const sendBlockNode = (
    <>
      {/* ⭐ board 2812 M3 fix round: on web money only, with the block (a home's run has
          no quote to send, so "No quote sent yet." would say something false about it). */}
      {takesWebMoney && detail.latest_quote_cents == null && !confirmingCancel && (
        <p class="send-status">No quote sent yet.</p>
      )}
      {sendBlock && !confirmingCancel && (
        <SendQuote
          // The channel override is component-local state for ONE send. Keying
          // it on the booking makes a booking change REMOUNT rather than
          // inherit: unreachable today (the routing unmounts between bookings),
          // cheap insurance against a future "next booking" quick-nav.
          key={bookingId}
          serverPhone={customer?.phone_e164 ?? null}
          serverPhoneKnown={true}
          // T549 M8 - the resolved recipient (see `recipient` above). Passing
          // customer.email alone would hide the send affordance from exactly the
          // email-only website customer M8 made reachable, and the recipient
          // line would read "no email address on file" for a send that succeeds.
          // The provenance travels WITH it so the send surface can say where the
          // address came from, the same distinction the row above draws.
          serverEmail={recipient.email}
          serverEmailFromInquiry={recipient.fromInquiry}
          contactPreference={b.contact_preference}
          latestSend={latestSend}
          sendStatusLabel={(s) => sendStatusLabel(registry, s)}
          channelLabel={(c) => channelLabel(registry, c)}
          // T524 (C5). This screen already holds the registry, so it derives the
          // callback here rather than taking a new BookingDetailProps member.
          channelConfigured={(c) => isChannelConfigured(registry, c)}
          // ⭐ T1002 M7, REMOVAL 2 OF 2. On a paid deposit the toggle does not
          // merely add a redundant link - it drives the send into
          // handleSendQuote's DepositAlreadyPaidError leg, so switching it on
          // 409s THE WHOLE SEND. See the prop's docblock in SendQuote.tsx.
          // ⭐ board 2814 M4 (DD-5): and never on a cash run, which takes nothing
          // online (the server refuses it as `cash_to_driver` either way).
          // ⭐ board 2812 M3 (step 7, SC-5): and only on WEB money (`moneyKind`), so an
          // account run never offers the deposit the server refuses (`account_booking`).
          offerDepositLink={takesWebMoney && !depositPaid && detail.cashToDriver !== true}
          onSend={async (includeDeposit, channel) => {
            const res = await sendQuoteRequest(bookingId, includeDeposit, channel);
            if (res.ok) {
              load();
              // ⭐ board 2812 M3 (§7.5): a send moves the Web badge now (Q246).
              onInboxChanged?.();
            }
            return res;
          }}
          // T660 M2 - the manual copy route, inside the shared block. Called
          // DIRECTLY, with no re-price guard: this screen renders the persisted
          // quote row (there is no live engine result here to disagree with the
          // composed total), which is exactly why Done's guard lives in App.tsx
          // and not in the shared component.
          onCopyMessage={async () => {
            const copied = await copyQuoteMessage(bookingId);
            // ⭐ board 2812 M3 (§7.5): a copy of the quote message is a quote send.
            onInboxChanged?.();
            return copied;
          }}
          // T660 M3 - the payment-link copy row, wired IDENTICALLY to Done's
          // (which is App.tsx's, minus a store read for the same booking id).
          // The block must render the same on both hosts or
          // delivery-block-parity.spec.tsx reds, which is the point of it.
          //
          // Deliberately no `load()` afterwards, unlike onSend above. A send
          // changes the delivery status this panel renders; a copy changes
          // nothing visible here - M1's ledger row carries a NULL quote_id
          // precisely so it stays out of `latest_send` - and re-reading would
          // only be a chance to drop the "Payment link copied" confirmation
          // Helen is still looking at.
          // ⭐ T1002 M7, REMOVAL 1 OF 2. `handlePostPaymentLink` 409s
          // UNCONDITIONALLY on a `deposit_paid` row, so on a paid booking this
          // button cannot succeed - every tap is a refusal. The prop is already
          // `(() => Promise<CopyLinkOutcome>) | null`, and null is already "no
          // button rather than a no-op one", so the removal needs no change to
          // the shared component.
          onCopyPaymentLink={!takesWebMoney || depositPaid || detail.cashToDriver === true ? null : () => copyPaymentLink(bookingId)}
          // ⭐ Board 2814 M2 (DD-11 item 4): while the home's ask is open its Accept
          // is this screen's one gold, so the send steps down as Re-open does.
          // ⭐ board 2812 M3 fix round 3 (F3-3, SC-11): the send is gold only when it
          // is the detail's NEXT STEP (a job not yet confirmed); below The trip and
          // Money on a confirmed job it is quiet.
          yieldGold={askOpen || !sendIsNext}
        />
      )}
    </>
  );
  const balanceNode = (
    <>
      {!confirmingCancel && (
        <AskForBalance
          bookingId={bookingId}
          balance={detail.balance}
          refundedCents={detail.depositRefundedCents ?? null}
          /* ⛔ BOTH CONTACTS ARE READ THE WAY THE **ASK** ROUTE READS THEM —
             `customers` columns, trimmed, blank as absent — and `recipient` above
             is deliberately NOT reused. That resolver falls back to this
             booking's inquiry address, which `validateBalanceAsk` never sees; the
             props' own docblocks carry the measured consequence. */
          phone={customer?.phone_e164?.trim() || null}
          email={customer?.email?.trim() || null}
          contactPreference={b.contact_preference}
          /* ⛔ THE TWO MONEY FIGURES ARE NO LONGER PASSED FROM HERE, and the
             removal is the fix (M5 rework 3). They were `detail.payment
             ?.amount_cents` and `detail.latest_quote_cents` — the newest payment
             row of ANY status, and the newest quote — so the money sentence could
             state an amount nobody had paid, against a total from a different
             quote. They ride `detail.balance` now, derived server-side off the
             one `deposit_paid` row and that deposit's own quote. */
          /* ⛔ THE SCREEN'S OWN RE-READ, exactly as `MarkTravelled` is wired. The
             ask writes a `balance_invoices` row and an audit event; what this
             screen then shows must be what the server holds, not what the client
             hoped. */
          onAsked={load}
          gold={balanceIsNext}
        />
      )}
    </>
  );
  const runControlsNode = (
    <>
          {/* ⭐ board 2814 M7 (DD-7): not on a run passed to a taxi, which the
              server refuses (`run_taxi_passed`). */}
          {detail.taxi == null && (
          <MarkTravelled
            bookingId={bookingId}
            /* ⛔ THE CONTROL'S CONDITION IS READ OFF THE SERVER'S OWN SPINE, not
               re-derived from the booking. A second derivation on this screen
               could offer the button on a run the server would refuse, or hide
               it on one it would accept — and the spine is the thing the button
               exists to advance, so asking it is also the more direct question. */
            travelled={(timeline.kind === 'ready' && timeline.lifecycle !== null ? timeline.lifecycle : []).some((s) => s.id === 'travelled' && s.state === 'done')}
            onMarked={load}
          />
          )}
          {/* ⭐⭐ board 2814 M7 (DD-7) - "Pass to a taxi", beside the travelled mark it
              excludes. The spine is the server's `isRunConfirmed` (null for a
              cancelled or unconfirmed run), so the block shows on a confirmed run
              only; and not while the cancel confirm is up (one question at a time).
              `key` drops an open field on a booking switch. */}
          {/* ⭐ board 2814 M8R1 (R6): and not on a run that has travelled, which the
              server refuses (`taxi_travelled`); a passed run keeps its Undo. */}
          {!confirmingCancel && b.status !== 'cancelled' && (detail.taxi != null || !runTravelled) && (
            <PassToTaxi key={bookingId} bookingId={bookingId} passed={detail.taxi != null} onChanged={load} />
          )}
    </>
  );

  return (
    // ⭐ (U1) the wide pane: `.screen-pane`'s 900px cap halved The trip and Money into two
    // 402px columns at 1920 with ~1000px empty beside them; the Detail part's grid is
    // the measure here, and the next-step blocks keep their own `--measure` caps.
    <div class="screen-pane screen-pane--wide">
      <h1 class="screen-title" tabIndex={-1}>
        {titleWho === null ? 'Booking' : <><span class="visually-hidden">Booking:</span> {titleWho}</>}
      </h1>

      {/* T1683 M3 (A1). The one string Helen and the nurse both hold, and until
          now NO identifier of any kind was rendered on this screen.
          It is what a phone call opens with, so it sits under the heading rather
          than buried in the grid below.
          OMITTED ENTIRELY when null: every booking written before M3 has no
          reference and never will (there is no backfill), and a label over an
          empty value would read as one that failed to load. */}
      {b.job_ref != null && b.job_ref !== '' && (
        <p class="booking-job-ref">
          {/* ⛔ THE VALUE IS WRAPPED, THE LABEL IS NOT (M3 review, F2). At 375px
              the browser breaks at the reference's own hyphens and splits the one
              string a phone call is built around; the LABEL beside it should still
              wrap freely, which is why the span is around the token alone.
              ⛔ AND SINCE T1719 IT IS THE SPACE AS WELL AS THE HYPHENS. The
              account form is `KLNH 01` and DD3 STORES that space rather than
              formatting it in, so this value is a two-token string a narrow
              column will split into `KLNH` / `01`. `nowrap` suppresses every
              soft-wrap opportunity including a space, so the same one class
              still covers both forms.
              ⚠️ OTHER SURFACES CARRY IT TOO; derive the set rather than
              trusting a list: `/usr/bin/grep -rl 'job-ref-value'
              quote-helper/src/client/screens`. The two invoice renders that
              joined at T1719 M6 (Invoice.tsx and the Invoices tab's runs table)
              left at board 2764 M6 (R-12): the reference now prints on Stripe's
              line. */}
          Job reference: <span class="job-ref-value">{b.job_ref}</span>
        </p>
      )}

      {/* ⭐⭐ board 2812 M3 (plan step 7, R-3) — THE ONE DETAIL SCREEN, ON THE SHARED
          `Detail`: the next step first (the paid banner, a home's ask with Accept the
          one gold, the send block), then The trip and Money, side by side from 1280,
          then what ends the screen in an `ActionRow`. The DOM order is unchanged, so
          every fold and one-question gate below still holds. */}
      <Detail
        next={<>
      {/* ⭐⭐ board 2811 M7 (carried M4-ux-1) - THE WARN NOTES ABOUT THE BALANCE, IN
          THE TOP BANNER ZONE beside the confirmed banner. The M4 ux-review found the
          stale-email note just below the fold at 1024x700 while the green banner
          was at the top, so Helen could miss the one note that
          says a wrong bill is about to go. Moved, not restyled: the same catalog
          `warn` notice, no control (D-12), and no control moved, so the control
          budget is unchanged.
          1. The stale balance email (board 2811 M4; Captain, 2026-10-06).
          2. A FAILED or LATE balance text (M7, A3 items 1 and 4), by the same rule:
             each is a word the list flags, and each asks Helen to act (ring the
             customer, ask again with the control below, or tell Alex), so it is a
             `warn`. Inside `!confirmingCancel` with the block it points at: while
             a destructive confirm is up the screen asks ONE question (§5.1).
          ⭐ board 2811 R3 (item 5): FIRST in the zone, ABOVE the green confirmed
          banner, and on the goldline chip's tint (`.needs-person .banner`, app.css):
          a white `warn` and a white `ok` differed by their edge alone, so a note
          asking Helen to act read like the good news under it. The catalog's
          `warn` kind is untouched at every other call site (derive them:
          `git grep -n 'kind="warn"' -- quote-helper/src portal/src`); only this
          wrapper tints. */}
      <div class="needs-person">
        {detail.balance_email_stale === true && (
          <Banner kind="warn">
            {staleEmailWhen === ''
              ? 'This booking has changed, but its balance email is still set to go and should not. Tell Alex now.'
              : `This booking has changed, but its balance email is still set to go on ${staleEmailWhen} and should not. Tell Alex before then.`}
          </Banner>
        )}
        {/* ⭐ board 2811 rework part 2c (Captain item 14): UNPAID AT DEPARTURE is a
            `warn` note here too, the "needs a person" look the failed text wears,
            and the list's precedence (stale, unpaid, failed, late). It was the grey
            `info` notice beside the Payment line, louder for nothing to do than for
            this. Ungated like the stale note: a fact the list flags. */}
        {detail.balance_unpaid_at_departure === true && (
          <Banner kind="warn">The journey has started and the balance is not paid. Ring the customer about the balance.</Banner>
        )}
        {!confirmingCancel && detail.balance?.state === 'failed' && (
          <Banner kind="warn">
            {balanceTextFailedCopy(
              detail.balance,
              textAgainWords(customer?.phone_e164?.trim() || null, customer?.email?.trim() || null),
            )}
          </Banner>
        )}
        {/* ⭐ board 2811 R1 review (ux-review): a STUCK ask (an email row that never
            finished) is the same "did not finish" fact as a failed text's
            `unfinished`, so it wears the same top-zone `warn` note, not a grey line
            under Payment. ⛔ 'Ask Alex', NOT 'try again': a stuck ask is a row M3's
            reader surfaces and an operator cannot resolve from this screen;
            inviting a retry would invite a second payment demand. */}
        {!confirmingCancel && detail.balance?.state === 'stuck' && (
          <Banner kind="warn">
            {`The balance ask on ${dublinDayAtTime(detail.balance.askedAt ?? '')} did not finish. Ask Alex to check it.`}
          </Banner>
        )}
        {!confirmingCancel && detail.balance?.state === 'late' && detail.balance.automaticAskRefunded === undefined && (
          <Banner kind="warn">{balanceTextLateCopy(detail.balance.sendsAt ?? '', detail.balance.automaticTextsOff === true)}</Banner>
        )}
      </div>

      {detail.payment?.status === 'deposit_paid' && (
        // T408: the unmistakable paid state: Owen's "flashing". Banner over
        // chip: role=status announces it, the ok border carries it visually.
        <Banner kind="ok" id="deposit-paid-banner">
          Deposit paid: {euro(detail.payment.amount_cents)} received
          {detail.payment.paid_at ? ` ${shortDateTime(detail.payment.paid_at)}` : ''}. This is now a
          confirmed booking.
        </Banner>
      )}

      {/* ⭐⭐ Board 2814 M2 (DD-11 item 4) - THE HOME'S ASK, FIRST UNDER THE HEADING:
          above the send block and the record, and so above the "Change date and
          time" disclosure whose sentence points at it. R11 is the reason it is
          this high: under the send block its Accept (the screen's one gold while
          the ask is open) measured about 760px at 1024x700 on a portal-linked
          account booking (the M2 ux review). Outside `load()` and the action row;
          inside `!confirmingCancel`, the screen's one-question rule. */}
      {!confirmingCancel && (
        <LinkedAsk
          key={bookingId}
          portal={linkedRequest}
          bookingStatus={b.status}
          travelled={runTravelled}
          taxiPassed={detail.taxi != null}
          onAnswered={() => {
            void load();
            reloadRequest();
            onRequestsChanged?.();
          }}
        />
      )}

      {/* ⭐⭐ T1749 M8 (DS-22 e, THE FOLD — measured on the seat, not reasoned) —
          THE SEND BLOCK SITS ABOVE THE RECORD, AND THAT IS THE WHOLE FIX.
          ⛔ THE MEASUREMENT FIRST. At 1024x700 this screen's one gold primary
          (`Send quote to customer`) bottomed at **982** — 282px past the plane
          the milestone grades, and 214px past it at 1024x768, which is the
          below-the-fold finding Captain's M3 live drive recorded (item 10) and
          M8 owns. The record above it is **403px** of facts on this fixture and
          grows with the booking: a customer, a phone, a journey, a when, a
          passenger count, a quote, a note. NOTHING there is spacing — condensing
          the block's own rhythm (the shared tightening in `app.css`) buys 56px,
          and the other 226 would have to come out of FACTS.
          ⛔ SO THE ORDER MOVES, NOT THE FACTS. On a quoted booking the job Helen
          opened it to do IS the send; the record is what she reads while doing it,
          and it is one scroll away rather than hidden. Nothing is removed and no
          fact is stated twice.
          ⛔ AND IT IS NOT THE FIXED `Bar`, which was the other ruled rung. The
          send button is inside the SHARED `SendQuote` block, whose channel choice
          and in-flight state are its own; lifting it into a bar means hoisting
          that state into BOTH hosts, on the money path, and
          `delivery-block-parity.spec.tsx` exists precisely because those two hosts
          must render one identical block. That is a milestone, not a fold fix.
          ⚠️ MEASURED AFTER: 982 -> the probe's own figure at both planes. Re-derive:
          `npx playwright test tests/green/tablet-ia.spec.ts --project=rate-limited -g "fold"`.
          ⚠️ THE CANCEL GATE TRAVELS WITH IT, unchanged: while a destructive confirm
          is up this screen asks ONE question, so the block (which holds a primary)
          stays hidden — §5.1, and `conformance.spec.tsx` grades it. */}
      {/* ⭐⭐ T1749 M8 (DS-22 c) — THE SENT STATE IS ALWAYS STATED, INCLUDING WHEN
          THERE IS NOTHING TO STATE.
          The block below says it in Done's own sentence shape (it IS the same
          component, so the three ledger sentences and their chip cannot drift
          between the two screens) — but it only mounts on a PRICED booking, so an
          unquoted one said nothing at all about sending and left Helen to infer
          it from an absence. One line closes that.
          ⛔ GATED ON `latest_quote_cents == null`, which is "no quote exists",
          rather than on `sendBlock` (which is also false for a zero-valued
          quote): a quote of zero is a quote, and this sentence would be false on
          it. ⛔ AND IT NEVER SITS BESIDE THE BLOCK, so the sent state is stated
          exactly once whichever branch renders. */}
      {/* ⭐⭐ board 2812 M3 fix round 2 (UX-3): the send block is the next step only on a
          job NOT YET CONFIRMED, and only when no home's ask and no balance to ask for
          outranks it; on a confirmed job it sits below The trip and Money. */}
      {sendIsNext && sendBlockNode}
      {/* ⭐⭐ (UX-3) the balance still to pay, when it is the next step. */}
      {balanceIsNext && balanceNode}
      {/* ⭐⭐ (UX-4) a home's run whose next act is to mark it travelled or pass it to a
          taxi: those two, here, rather than below Progress. */}
      {runIsNext && runControlsNode}

        </>}
        trip={<>
      {/* ⭐⭐ board 2812 fix round (Captain item 1, SC-7): THE SAME TWO HEADINGS ON EVERY
          BOOKING TYPE, "The trip" here and "Money" over the money, in the one heading
          style the Money heading already wore (`h2.field-heading`), so the two columns
          start level at 1280 and up. */}
      <h2 class="field-heading">The trip</h2>
      <div class="customer-info">
        <p class="customer-info-title">
          {bookingTypeLabel(registry, b.booking_type)}{' '}
          <Chip neutral tone={statusTone(b.status)}>{bookingStatusLabel(registry, b.status)}</Chip>
          {/* FB7: a nursing-home job folds to booking_type 'local-other' (reads
              'Custom' above). Surface the true service from SERVER TRUTH
              (detail.latest_quote_service, top-level) so it is distinguishable
              from a real Custom job — never from booking_type or a client
              capture. */}
          {detail.latest_quote_service === 'nursing-home' && (
            <>{' '}<span class="chip chip--estimate">Nursing home</span></>
          )}
          {/* ⭐ board 2814 M8R1 (R8): a cash run says so in its header, in the Bookings
              list's own neutral "Cash" chip (the facts below say who collects it). */}
          {detail.cashToDriver === true && (
            <>{' '}<Chip neutral>Cash</Chip></>
          )}
        </p>
        {/* ⭐⭐ T1749 M8 (DS-22 c) — THE FACTS ARE THE LIBRARY'S `FactsTable` NOW.
            This screen drew its own `<dl class="customer-info-grid">`: a
            two-column flex per pair, its own label track, its own hairline-less
            rows. `FactsTable` is the shape the sitting ruled for exactly this
            block (DESIGN.md §11, `DECISIONS.md` §13 from D2 — label left, value
            right, a hairline between rows), and it is the same component the
            portal's open request card uses, so one record's facts read the same
            wherever an operator meets them.
            ⛔ A `<dl>` EITHER WAY — the markup carries the meaning (these are
            terms and their values, not two columns of comparable data), which is
            why `Table` and `FactsTable` are two components rather than one with a
            variant prop. Every `dt` / `dd` pin on this screen still resolves.
            ⚠️ `.customer-info-grid` IS NOT RETIRED: `CustomerInfoBlock` still
            wears it (the two nursing screens that did left at board 2814 M6).
            Nothing is minted either way — re-derive:
              python3 tools/css-census/families.py --map tools/css-census/mapping.json */}
        <FactsTable facts={facts} />
        {/* ⭐ board 2812 fix round (SC-7): "Change who pays" and "Change the price" moved
            to the Money column with the money facts they change. */}
        {requirements.length > 0 && (
          <p class="detail-chips">
            {requirements.map(([key, label]) => (
              <span key={key} class="chip chip--estimate">{label}</span>
            ))}
          </p>
        )}
        {/* ⭐⭐ T1717 M6 - BESIDE THE CHIPS, NOT INSIDE THEM. The chip row is
            registry-driven and every entry there is a bare add-on LABEL; a chip
            carrying a sentence would break that shape and would have to be
            special-cased inside the map, which is the same mistake the driver
            message's registry loop forbids one file over. These are two plain
            lines under the row instead.
            ⛔ AND THEY ARE TWO SEPARATE CONDITIONS. A booking can have a return
            time with no disagreement (the ordinary case) or a disagreement with
            no time (the home said one way, Helen ticked return). Folding them
            would hide one behind the other. */}
        {/* ⚠️ T1749 M8 (DS-22 c): the `Back: <time>` hint line WAS here and is now
            the facts table's `Back` row (see the `facts` array). The
            disagreement line below is NOT promoted with it and keeps its own
            condition — see the note on the array. */}
        {homeSaid !== null && <p class="field-hint">{homeSaid}</p>}
        {/* ⛔ T1749 M8 (C15) — GATED ON `customer`. An ACCOUNT booking has NO
            customer record (`detail.customer` is null; it is billed to the home),
            so "Correct the email address on this customer" is a control with no
            subject there — its 409 path could only ever confuse, and the address
            it would edit does not exist. `booking-detail.spec.tsx` asserts the
            control is absent for an account booking and present for a consumer. */}
        {customer ? (
          <CustomerEmailCorrection
            bookingId={bookingId}
            onCorrected={load}
            /* ⭐ T1749 M8 (C15): re-read, then let the render decide. The reload is
               what turns an ambiguous 409 into an observed fact. */
            onConflict={() => {
              setEmailConflict(true);
              setConflictReloading(true);
              void load().finally(() => setConflictReloading(false));
            }}
            /* ⭐ T1749 M8 round 2 (security L3) — WHILE THE RE-READ IS IN FLIGHT
               THE CONTROL SAYS NOTHING ABOUT WHICH 409 IT WAS. The two 409s are
               identical on the wire (SEC-5: the server's prose never reaches the
               screen), so the ordinary "already on file for a different customer"
               copy would FLASH for the duration of the reload on a booking that
               turns out to be account-billed — a sentence about a third party
               that was never true. The host has asked its own question by then;
               the control waits for the answer. */
            conflictPending={conflictReloading}
          />
        ) : emailConflict && (
          /* ⭐⭐ C15, the client half — THE ACCOUNT 409, IN THE CLIENT'S OWN WORDS.
             Reached only by the pair (a 409 came back, AND the re-read says this
             booking has no customer record), so it can never describe the
             ordinary address-belongs-to-someone-else 409, which keeps its own
             copy inside the control above. It names nobody and quotes nothing off
             the wire (SEC-5). `info`, not `error`: nothing failed — this booking
             is simply billed somewhere else. */
          <Banner kind="info">
            This booking is now billed to an account and has no customer record to correct.
          </Banner>
        )}
        {/* ⭐ board 2788 M1 - "Change date and time", a sibling of the email
            correction, after the facts table. Inside `!confirmingCancel` on this
            screen's standing rule (one question while a destructive confirm is up).
            Absent advisory (an older server or a hand-built fixture) renders nothing. */}
        {!confirmingCancel && detail.reschedule !== undefined && (
          <ChangeDateAndTime
            bookingId={bookingId}
            advisory={detail.reschedule}
            currentDay={b.journey_date}
            currentTime={b.journey_time}
            returnTrip={b.return_trip}
            returnAt={detail.returnAt ?? null}
            statedReturnDate={detail.returnDate ?? null}
            appointmentAt={detail.portalRequest?.appointmentAt ?? null}
            portal={linkedRequest}
            onReloadRequest={reloadRequest}
            onChanged={() => void load()}
            taxiPassed={detail.taxi != null}
          />
        )}
      </div>
        </>}

        money={<>
      {/* ⭐⭐ board 2812 fix round (Captain item 1, SC-7): "Money" heads the column on
          EVERY booking type, over the money facts (`moneyFacts`: the Quote or Price,
          and on a web job its Deposit and Balance), then the controls that act on
          them. The "Payment:" line this replaced said the Deposit row's fact. */}
      <h2 class="field-heading">Money</h2>
      {moneyFacts.length > 0 && <FactsTable facts={moneyFacts} />}
      {/* ⭐⭐ board 2812 M3 fix round 2 (UX-4): a home's run says how it is billed.
          ⭐ fix round 3 (F3-4): at the BODY size (no caption class), as a plain line. */}
      {!takesWebMoney && (
        <p>{
          b.status === 'cancelled' ? RUN_MONEY_WORDS.cancelled
            : detail.taxi != null ? RUN_MONEY_WORDS.taxi
              : detail.cashToDriver === true ? RUN_MONEY_WORDS.cash
                : RUN_MONEY_WORDS.account(identity.accountName)
        }</p>
      )}
      {/* T408 ux-review: a paid booking has nothing to refresh (the server refresh
          short-circuits on a paid row), so the no-op button is hidden there. */}
      {detail.payment && detail.payment.status !== 'deposit_paid' && (
        <p class="send-status">
          <button type="button" class="btn btn--quiet btn--inline" onClick={() => void refreshPayment()}>
            Refresh payment
          </button>
        </p>
      )}
      {/* ⭐ board 2814 M4 (DD-5, D7): a mistaken cash payer is corrected here, under
          the line that says the run is paid in cash, so it is billed to the home at
          month end. First, as it was in the card: the price control's
          `action-row` margin is what spaces the two. */}
      {detail.cashToDriver === true && !confirmingCancel && (
        <ChangeWhoPays bookingId={bookingId} onChanged={load} />
      )}
      {/* ⭐ board 2814 M6 (DD-8): "Change the price" on an account run, under the
          price it changes (or sets, on a run nobody has priced). Read off the
          server's own `accountBooking`; not on a cancelled run, which the route
          refuses. `key` drops an open field on a booking switch.
          ⭐ board 2814 M8R1 (R5): and not on a run passed to a taxi, which the route
          refuses too (`price_change_taxi_passed`). */}
      {detail.accountBooking === true && b.status !== 'cancelled' && detail.taxi == null && !confirmingCancel && (
        <ChangeThePrice
          key={bookingId}
          bookingId={bookingId}
          currentCents={detail.latest_quote_cents ?? null}
          onChanged={load}
        />
      )}

      {/* ⭐⭐ board 2811 M4 - THE TWO BALANCE FLAGS (they sat beside the old Payment
          line; the Deposit fact row replaced it at board 2812's fix round) and
          OUTSIDE `AskForBalance`, because they are top-level keys the server
          serves whether or not the `balance` block is (a re-quoted booking has
          none). No control is added (D-12): `Banner` is the catalog's `Notice`
          with no dismiss handler, so it renders no button. The stale note is
          first, for the list's reason: a wrong bill about to go out outranks an
          unpaid one. Captain, board 2811, 2026-10-06: a stale scheduled email
          must be flagged to Helen before it sends.
          ⭐ M4 review fix (UX Medium, UX Low): TWO WEIGHTS, BOTH THE CATALOG'S AND
          NEITHER RED. The stale note is a `warn` notice (NOTICE_KINDS: "something
          the reader should know before acting": a 2px gold-700 edge, ink-900 on
          white, 16.67:1); the unpaid note is the lighter `info` notice (a hairline,
          "a quiet fact"). Both used to be the grey `send-status` line.
          ⛔ M4 review fix (code-review Medium): THE COPY PROMISES NOTHING THE KIOSK
          MAY FAIL TO DO. Since board 2811 M6 the scheduler's sweep retries the
          withdrawal every tick, but only while the kiosk runs and Stripe answers
          (the note is shown exactly when it has not yet worked), so the note names
          the one remedy that holds either way: tell Alex, before it goes.
          ⭐ board 2811 M7 (carried M4-ux-1): the stale `warn` note now sits in the
          TOP banner zone, beside the confirmed banner, so it is above the fold at
          1024x700. ⭐ board 2811 rework part 2c (Captain item 14): the unpaid note
          is a `warn` now and moved there with it. */}

      {/* ⭐⭐ board 2106 M5 — THE BALANCE ASK, UNDER THE MONEY FACTS (the Deposit and
          Balance rows since board 2812's fix round; the old Payment line before): the
          deposit is what the Deposit row reports, and the balance is what is left
          after it. Placed by the fact rather than by the fold — a
          control about the same money, one line below the money.

          ⛔ INSIDE `!confirmingCancel`, on this screen's standing rule (DESIGN.md
          §5.1): while a destructive confirm is up the screen asks ONE question.
          The screen's two OTHER `!confirmingCancel` gates carry the same conjunct
          for the same reason — the send block, which renders ABOVE this mount, and
          `Re-open in calculator`, which renders below it — and a money control
          that did not would sit beside 'Yes, cancel and refund EUR X'.
          ⚠️ AN EARLIER DRAFT SAID BOTH WERE BELOW. They are not: `<SendQuote>`'s
          gate is the one at the top of this return, above the money facts.

          ⛔ THE KEY IS READ WITH NO DEFAULT. `detail.balance` is `undefined` on a
          booking the server will not let anyone ask (and on every hand-built
          fixture that predates the key), and the component renders nothing for
          it; a `?? { state: 'none' }` here would offer the control on an account
          booking, which is the one place a balance ask is a defect by rule. */}
      {!balanceIsNext && balanceNode}

      {/* ⭐⭐ board 2249 M5 — "INVOICE THIS JOB", beside the balance ask it is the
          whole-job sibling of: the same money, one block down. Inside
          `!confirmingCancel` for the balance ask's reason (one question while a
          destructive confirm is up). `key` remounts it on a booking switch, so an
          open form or an outcome never carries onto another booking. */}
      {!confirmingCancel && (
        <InvoiceThisJob
          key={bookingId}
          bookingId={bookingId}
          jobInvoice={detail.jobInvoice}
          bookingEmail={(detail.customer?.email ?? '').trim()}
          onIssued={load}
        />
      )}

      {/* ⭐⭐ T1002 M7 (ux-review R1) — THE WHOLE SEND BLOCK GOES WHILE THE
          CANCEL CONFIRM IS UP, not just the re-open button below it.

          ⛔ THIS IS THE HALF THE FIRST PASS MISSED, and the miss was hidden by
          the very control it gated. The re-open button is `btn--secondary`
          PRECISELY WHEN `primaryIsSend` holds - which is to say, whenever the
          gold on this screen is SendQuote's own 'Send quote to customer'. So
          hiding only the re-open covers the `primaryIsSend === false` branch and
          leaves the other one showing a gold primary beside
          'Yes, cancel and refund EUR X'.

          ⚠️ AND THAT BRANCH IS ORDINARY, NOT THEORETICAL: `deposit_paid` with
          `latest_send === null` is what a hand-copied payment link produces
          (M1's ledger row carries a NULL quote_id precisely so it stays out of
          `latest_send`), which is the fixture the confirm tests already use.

          While a destructive confirm is up the screen asks ONE question. Gated
          here rather than by a new prop on the shared component: the rule is
          about THIS screen's composition, and Done.tsx has no cancel confirm to
          teach SendQuote about. */}

      {/* T608 M4 (R6d) — ONE chronological history for this booking, unioned at
          read time from booking_events + send_ledger + payments. Reuses the
          `audit-trail` box AuditHistory already established for the price-audit
          union, so this adds no CSS.

          THREE states, said out loud (the live ux-review's finding 2). This used
          to render only when there was something to show, so a booking with no
          history and a booking whose history FAILED TO LOAD looked identical —
          blank — on the one panel that exists to be the record of what happened.
          A silent blank is the wrong failure mode: Helen cannot tell "nothing
          happened" from "we could not tell you". Only 'loading' renders nothing,
          because a request still in flight is the one state that makes no claim.
          One quiet sentence each, no retry button: the operators abandoned a
          previous tool for having too many buttons, and re-opening the booking
          already re-runs the read. */}
      {/* ⭐ T1033 M7 part 2 — ADR4-D11's spine, OPERATOR side. The other half of
          "one request object, one lifecycle": the nurse sees these same six
          stages on her own screen, with the last three honestly `not-known`
          because the portal holds no travel, invoice or payment.

          ⛔ THE STAGES AND THEIR STATES ARE SERVER-SUPPLIED AND ARE NOT TOUCHED
          HERE. No prop selects data, nothing is filtered, and no state is
          computed on this screen — `core/operator-lifecycle.ts` decides, before
          anything is serialised. That is the mechanism Rule 1 and OWASP
          API3:2023 require and the reason a role/density prop does not exist.

          Rendered beside History rather than at the top of the record: both come
          from the SAME request, so grouping them keeps every asynchronous block
          on this screen in one place and keeps the identity block above from
          shifting when they land. `null` = no spine (a cancelled run, or since
          board 2377 a run not yet confirmed), which renders nothing and lets
          the status chip carry the outcome.
          ⭐⭐ board 2812 fix round (Captain item 1, SC-7): IN THE MONEY COLUMN NOW, under
          the money it tracks (Quoted, Confirmed, Invoiced, Paid), so The trip and Money
          end level at 1920 rather than leaving the Money column half empty; History
          stays below the two columns. Below 1280 it still follows the money facts.
          No `<hr>` inside the column: its heading separates it, as "Money" itself is
          (a rule here stopped at 640px, short of the column's right edge). */}
      {timeline.kind === 'ready' && timeline.lifecycle !== null && (
        <>
          <h2 class="field-heading">Progress</h2>
          <Lifecycle stages={timeline.lifecycle} label="Progress of this booking" />
          {!runIsNext && runControlsNode}
        </>
      )}
        </>}
      >
      {/* ⭐⭐ board 2812 M3 fix round 2 (UX-3): on a confirmed job the send block is not
          the next step; it sits here, below The trip and Money.
          ⭐ board 2812 M5 (fix round 1, U2): and on a confirmed (or later) job it is a
          quiet disclosure, closed by default, native <details> as "Change date and time"
          is (no new CSS, no state): the same block inside, never gold (`yieldGold`), so a
          paid booking no longer reads as if sending were still to do. */}
      {!sendIsNext && (confirmedOrLater && sendBlock && !confirmingCancel ? (
        <details>
          <summary class="capture-hint">{SEND_AGAIN_WORDS}</summary>
          {sendBlockNode}
        </details>
      ) : sendBlockNode)}
      {timeline.kind !== 'loading' && (
        <>
          <hr class="section-rule" />
          <h2 class="field-heading">History</h2>
          <div class="audit-trail">
            {timeline.kind === 'error' && (
              <p>Loading the history failed. The booking itself is fine.</p>
            )}
            {timeline.kind === 'ready' && timeline.entries.length === 0 && (
              <p>No history recorded for this booking yet.</p>
            )}
            {timeline.kind === 'ready' && timeline.entries.length > 0 && (
              <ul>
                {timeline.entries.map((e) => (
                  <TimelineRow key={e.id} entry={e} registry={registry} />
                ))}
              </ul>
            )}
          </div>
        </>
      )}

      <hr class="section-rule" />
      <ActionRow>
        {/* T660 M7 (P0-22, erratum 26) - the conditional variant M6 named and
            deferred. `primaryIsSend` is derived above; see its comment for why
            it asks SendQuote's own helper instead of restating the rule.

            ⭐ T1002 M7: HIDDEN WHILE THE CANCEL CONFIRM IS UP, exactly as
            Inbox.tsx hides its card primary during the dismiss confirm - a
            destructive confirm must never sit beside a primary (DESIGN.md
            §5.1).

            ⚠️ THIS BUTTON IS ONLY HALF THE RULE, and the first pass read it as
            all of it. It is the screen's primary only when `primaryIsSend` is
            FALSE; when that flips, this goes `btn--secondary` and the gold moves
            to SendQuote's 'Send quote to customer' - so the send block carries
            its own copy of this gate (see it above). Hiding one without the
            other covers exactly one branch. */}
        {/* ⭐ Board 2814 M2 (DD-11 item 4) - THE ONE ACTION-ROW EDIT M2 MAKES: while
            the home's ask is open, its Accept is this screen's one gold, so Re-open
            steps down to a secondary.
            ⭐⭐ Board 2814 M6 (DD-8) - THE SECOND: Re-open renders ONLY when the server
            says `calculatorAllowed === true` (no account, no portal link, not a
            nursing quote). Absent hides it: fail closed. Without it the row holds
            no gold unless the send rules already make Send the gold, and the
            account run's "Change the price" above is quiet, never gold. */}
        {/* ⭐⭐ board 2812 M3 fix round 3 (F3-3, SC-11): NEVER GOLD. The detail's one
            gold is its NEXT STEP's action (a home's Accept, the balance ask, the send on
            a job not yet confirmed), which sits above 700; this button sits at the
            screen's foot, far below it on every web job. A state with no next-step
            action has no gold. */}
        {!confirmingCancel && detail.calculatorAllowed === true && (
        <button
          type="button"
          class="btn btn--secondary"
          onClick={() => onReopen(detail)}
        >
          Re-open in calculator
        </button>
        )}
      </ActionRow>

      {/* ⭐ T1002 M7 — LAST ON THE SCREEN, and behind a disclosure. A terminal,
          irreversible action is not something Helen should be able to reach by
          scrolling past the thing she came here to do. */}
      <CancelRun
        bookingId={bookingId}
        /* ⛔⛔ BOTH TERMS COME FROM THE **ROUTE'S** ORACLE — the ONE `deposit_paid`
           row `lockRefundablePayment` locks. This line read
           `depositPaid ? detail.payment?.amount_cents ?? null : null` until the M6
           security gate, and that was a money-loss path rather than an
           inconsistency (its MED-1):

           `detail.payment` is `getLatestPaymentByBooking` — the NEWEST payment row
           of ANY status. On a booking carrying TWO paid deposits (4200, then a
           newer 9000) the route serves BOTH figures as `null`, because
           `lockRefundablePayment` REFUSES `ambiguous_payment` rather than picking
           — but the old line found the newer paid row anyway and offered a
           ceiling of €90. Helen types €90 and cancels-and-refunds; ⛔ THE CANCEL
           COMMITS, and only then does the refund arm refuse. The run is gone for
           good and the €90 never went out. That is exactly the harm `overDeposit`
           exists to prevent, on the one shape the first cut of this delta did not
           cover.

           ⭐ NULL IS THE FAIL-SAFE ANSWER, and it is why re-pointing fixes rather
           than moves the problem: with both terms null the control offers NO
           amount field at all, so no refund is attempted on a booking the server
           would refuse. It also fixes the mirror case — a paid 4200 under a newer
           `deposit_sent 12000`, where the old line went null and the screen
           offered nothing while the server would happily have refunded €42.

           ⚠️ The pre-M7 property still holds: `null` puts no amount on the wire,
           so the server's deposit-paid guards do not relax. */
        depositPaidCents={detail.depositPaidCents ?? null}
        depositRefundedCents={detail.depositRefundedCents ?? null}
        // ⭐ T2088 M8 — the shared predicate, not a second copy: Helen's portal
        // Requests row now offers a cancel too and both must retire together.
        cancellable={isBookingCancellable(b.status)}
        // board 2343 — ADVISORY, from the server's one rule; absent reads false.
        notGoingAhead={detail.notGoingAhead === true}
        travelled={runTravelled}
        taxiPassed={detail.taxi != null}
        confirming={confirmingCancel}
        setConfirming={setConfirmingCancel}
        onCancelled={() => {
          // ⭐ board 2812 M3 (§7.5): a cancel or a not-going-ahead moves the Web badge.
          onInboxChanged?.();
          void load();
        }}
      />
      </Detail>
    </div>
  );
}
